/**
 * BarrierEffectivenessTab Component
 *
 * Displays a matrix of barrier nodes × reports showing barrier effectiveness.
 * Rows = fault tree barrier nodes (nodeClass === 'BARRIER')
 * Columns = reports (filtered, MAPPED only)
 * Cells = green (barrier success), red (barrier failure), gray (no mapping)
 *
 * Uses viewport-based canvas rendering: only the visible portion of the matrix
 * is drawn on a viewport-sized canvas, avoiding browser canvas size limits and
 * keeping performance constant regardless of dataset size.
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    LinearProgress,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GridViewIcon from '@mui/icons-material/GridView';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    ChartDataset,
    ChartOptions,
    Legend as ChartLegend,
    LinearScale,
    LineElement,
    LogarithmicScale,
    PointElement,
    Title,
    Tooltip as ChartTooltip,
} from 'chart.js';
import {Line, Scatter} from 'react-chartjs-2';
import {
    useBarrierAnalysisCache,
    useIRPWorkbookStore,
    usePrimaryFilter,
} from '../../../../../../../stores/irp-workbook.store';
import {FaultTreeNode} from '../../../../../../../@types/faultTreeNode';
import {IrpBarrierReportDTO} from '../../../../../../../@types/integrated-risk-picture';
import {useIRPContext} from '../../IRPWorkbookProvider';
import {
    CHART_COLORS,
    COLOR_FAILURE,
    COLOR_FAILURE_CVD,
    COLOR_NO_MAPPING,
    COLOR_SUCCESS,
    COLOR_SUCCESS_CVD,
    blendSuccessFailure,
    hexToRgb,
} from '../../utils/chartColors';
import {WeibullFit, fitWeibullMLE} from '../../utils/weibull';
import {
    buildBarrierHierarchy,
    getVisibleRows,
} from '../../utils/barrierHierarchy';
import {
    AnalysisResult,
    DEFAULT_PASS_THROUGH,
    EMPTY_BARRIER_MTBF,
    EMPTY_BARRIER_TOTALS,
    EMPTY_BARRIER_TRENDS,
    EMPTY_EXECUTIVE_SUMMARY,
    EMPTY_SOURCE_COMPARISON,
    EMPTY_STRING_ARRAY,
    EMPTY_TIME_BUCKETS,
    EMPTY_TIMELINE_LOOKUP,
    EMPTY_UNMAPPED_REPORTS,
    SerializedAnalysis,
    UNMAPPED_PAGE_SIZE,
    deserializeAnalysis,
} from '../../utils/barrierAnalysisTypes';
import {TrendWindow, TrendWindowSelect} from '../shared/TrendWindowSelect';
import {KpiCard} from '../shared/KpiCard';
import {useEscapeKey} from '../../hooks/useEscapeKey';
import {FULLSCREEN_OVERLAY_SX, FullscreenToggle} from '../shared/Fullscreen';
import {scatterDiagonalPlugin} from '../shared/scatterDiagonalPlugin';
import {TREND_BAND_FILL, drawTrendWindowBand, drawTrendWindowDivider, drawTrendWindowPill} from '../shared/trendWindowOverlay';
import {TrendWindowBoundary, computeTrendWindowBoundary} from '../../utils/trendWindowBoundary';
import {computeCountTrend, makeTemporalEntityComparator} from '../../utils/temporalDistribution';
import {CrowAmsaaFit, fitCrowAmsaa} from '../../utils/crowAmsaa';
import TemporalDistributionPanel, {TemporalEntity, TemporalSort} from './occurrence-patterns/TemporalDistributionPanel';
import {Distribution, buildDistribution, fiveNumberSummary} from '../../utils/distributionStats';
import {downloadCsvFile, rowsToCsv} from '../../../../../../../utils/export/csv';
import {GapDistData, TimeToFailureDialog} from '../shared/TimeToFailureDialog';
import {TopBarriersPanel} from './barrier-effectiveness/TopBarriersPanel';
import {CalendarView} from './barrier-effectiveness/CalendarView';
import {MetricDistributionPanel} from './barrier-effectiveness/MetricDistributionPanel';
import {
    FEXP_CRITICAL_THRESHOLD,
    LOW_CONFIDENCE_CHALLENGES,
    SILENT_KILLER_FEXP_THRESHOLD,
    TREND_STABLE_THRESHOLD,
    fexpPillStyle,
} from './barrier-effectiveness/barrierThresholds';

ChartJS.register(BarElement, CategoryScale, LinearScale, LogarithmicScale, PointElement, LineElement, Title, ChartTooltip, ChartLegend);

/** Canvas cell dimensions */
const CELL_WIDTH = 32;
const CELL_HEIGHT = 28;
const HEADER_HEIGHT = 120;
const TIMELINE_CELL_WIDTH = 56;

/** View mode and granularity types */
type ViewMode = 'effectiveness' | 'timeline' | 'sourceComparison' | 'calendar' | 'failurePattern';
type TimeGranularity = 'monthly' | 'quarterly' | 'yearly';

/** Trend-window selection for period-over-period barrier stats.
 *  `'midpoint'` splits the IRP date range in half; numeric values represent months;
 *  `'allTime'` switches the Top N (and downstream views) into all-time display mode —
 *  no period split, all values cumulative. */
type BarrierTrendWindow = TrendWindow;
const DEFAULT_BARRIER_TREND_WINDOW: BarrierTrendWindow = 'allTime';
/** Calendar view — number of years shown per pagination page. */
const CALENDAR_YEARS_PER_PAGE = 5;

/** Formats a timestamp for the trend-window divider pill, e.g. "Dec 1, 2025".
 *  Same shape as the Chart.js `tooltipFormat` used in Report Clustering so the
 *  divider reads identically across tabs. */
const fmtSplitDate = (ts: number): string =>
    new Date(ts).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});

/** Column keys for barrier attribute columns */
type BarrierColumn = 'id' | 'name' | 'success' | 'failure' | 'demand' | 'effectiveness' | 'failureExposure' | 'mtbf' | 'effectivenessTrend' | 'demandTrend';

/** Columns only shown in timeline view */
const TIMELINE_ONLY_COLUMNS: Set<BarrierColumn> = new Set(['mtbf', 'effectivenessTrend', 'demandTrend']);

const COLUMN_CONFIG: {key: BarrierColumn; label: string; width: number}[] = [
    {key: 'id', label: 'ID', width: 100},
    {key: 'name', label: 'Name', width: 140},
    {key: 'success', label: 'Success (S)', width: 32},
    {key: 'failure', label: 'Failure (F)', width: 32},
    {key: 'demand', label: 'Demand Coverage', width: 70},
    {key: 'effectiveness', label: 'Effectiveness Rate', width: 70},
    {key: 'failureExposure', label: 'Failure Exposure', width: 70},
    {key: 'mtbf', label: 'Mean Time Between Failures (days)', width: 70},
    {key: 'effectivenessTrend', label: 'Effect. Trend', width: 52},
    {key: 'demandTrend', label: 'Demand Trend', width: 52},
];

const BarrierEffectivenessTab: React.FC = () => {
    const {irpId, dataSource} = useIRPContext();
    const irpDetails = useIRPWorkbookStore((state) => state.irpDetails);
    const eventSequenceData = useIRPWorkbookStore((state) => state.eventSequenceData);
    const primaryFilter = usePrimaryFilter();
    const barrierAnalysisCache = useBarrierAnalysisCache();
    const setBarrierAnalysisCache = useIRPWorkbookStore((state) => state.setBarrierAnalysisCache);
    const navigateToReportAnalysis = useIRPWorkbookStore((state) => state.navigateToReportAnalysis);
    const navigateToOccurrencePatternsWithSearch = useIRPWorkbookStore((state) => state.navigateToOccurrencePatternsWithSearch);

    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [visibleColumns, setVisibleColumns] = useState<Set<BarrierColumn>>(
        new Set<BarrierColumn>(['id', 'name'])
    );
    const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('effectiveness');
    const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>('monthly');
    const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null);
    const [granularityMenuAnchor, setGranularityMenuAnchor] = useState<null | HTMLElement>(null);
    const [chartSelectedBarriers, setChartSelectedBarriers] = useState<Set<number>>(new Set());
    const [chartHeight, setChartHeight] = useState<number>(200);
    const [legendMenuAnchor, setLegendMenuAnchor] = useState<null | HTMLElement>(null);
    const [chartFilterMenuAnchor, setChartFilterMenuAnchor] = useState<null | HTMLElement>(null);
    const [chartShowSuccess, setChartShowSuccess] = useState(true);
    const [chartShowFailure, setChartShowFailure] = useState(true);
    const [selectedSource, setSelectedSource] = useState<string>('All');
    const [sourceMenuAnchor, setSourceMenuAnchor] = useState<null | HTMLElement>(null);
    // Sub-tab navigation: BE is structured as four mutually-exclusive panels selected
    // via an MUI tabs row instead of four show/hide booleans. Summary is the default
    // landing tab; analysts can switch to focused views for Top N attention, the
    // quadrant chart, or the deep detailed analysis.
    type BeSubTab = 'summary' | 'topN' | 'healthChart' | 'detail';
    const [activeSubTab, setActiveSubTab] = useState<BeSubTab>('summary');
    const [topLevelOnly, setTopLevelOnly] = useState(true);
    // Which chart panel is maximized — at most one at a time (avoids stacked fixed overlays).
    const [fullscreenChart, setFullscreenChart] = useState<'kpi' | 'topBarriers' | 'quadrant' | 'trend' | 'challengedFailed' | 'passThroughTrend' | null>(null);
    const [detailedFullscreen, setDetailedFullscreen] = useState(false);
    const [overviewMode, setOverviewMode] = useState(false);
    // Calendar view — index of the current page. Page 0 = the most recent
    // `calendarYearsPerPage` years within the IRP filter range.
    const [calendarPage, setCalendarPage] = useState(0);
    const [calendarYearsPerPage, setCalendarYearsPerPage] = useState<number>(CALENDAR_YEARS_PER_PAGE);
    // Layout mode — 'standard' is the original one-band-per-year layout; 'yoy' shows
    // mini-month grids in a 12-column (Jan…Dec) by N-row (years) grid for seasonality.
    const [calendarLayout, setCalendarLayout] = useState<'standard' | 'yoy'>('standard');
    // Trend-overlay toggle: when on (and a trend window is set), recent-window cells get
    // an amber border and prior-window cells get a soft blue border so the calendar shows
    // not just "where" (the existing intensity fill) but also "when relative to trend".
    const [calendarTrendOverlay, setCalendarTrendOverlay] = useState(true);
    // Tooltip data for the calendar's hovered cell (null when no cell hovered).
    const [calendarHover, setCalendarHover] = useState<{
        date: Date;
        demand: number;
        success: number;
        failure: number;
        x: number;
        y: number;
    } | null>(null);
    // Click-to-drill: when set, opens a Dialog listing the day's reports and which
    // visible barriers each one challenged.
    const [calendarDrillDate, setCalendarDrillDate] = useState<Date | null>(null);
    const [calendarDrillPage, setCalendarDrillPage] = useState(0);
    const [calendarDrillPageSize, setCalendarDrillPageSize] = useState(25);
    // Per-barrier trend window — drives upcoming period-over-period stats (recent vs
    // prior) for the main barrier table and the Top N "was/now" tooltip. Default
    // mid-point so analysts get an immediately useful split without configuring.
    const [barrierTrendWindow, setBarrierTrendWindow] = useState<BarrierTrendWindow>(DEFAULT_BARRIER_TREND_WINDOW);
    // Minimum recent-window challenges required for a barrier to appear in Top N.
    // 0 = no filter ("All"). Matches Report Clustering's "Min in window" semantics.
    const [topMinSample, setTopMinSample] = useState<number>(0);
    const [barrierSearch, setBarrierSearch] = useState('');
    const [topN, setTopN] = useState<number>(3);
    const [topStatusFilter, setTopStatusFilter] = useState<'all' | 'critical' | 'warning'>('all');
    // Sort mode for Top N. Default `failureExposure` preserves the worker-provided order
    // (system-wide failure impact). Trend-based modes re-sort by the current trend window.
    type TopNSort = 'failureExposure' | 'mostDegraded' | 'mostImproved';
    const [topSort, setTopSort] = useState<TopNSort>('failureExposure');
    // View mode is derived from the trend-window selector — `'allTime'` flips every
    // column to its cumulative form; any other value keeps Top N in window mode.
    const topViewMode: 'window' | 'allTime' = barrierTrendWindow === 'allTime' ? 'allTime' : 'window';
    // Human label for the active trend window — surfaced in column tooltips so analysts
    // can see which window the recent-vs-prior slopes were fit over without scrolling up
    // to the selector. 'allTime' is handled separately (no window).
    const recentWindowLabel = barrierTrendWindow === 'midpoint' ? 'Mid-point' : `${barrierTrendWindow} mo`;
    // Main barrier table in window mode: default = "dim" submode (show all columns,
    // dim prior period, draw divider). `true` switches to "hide" submode (filter to
    // in-window only — what the table did before this control existed).
    const [hideOutOfWindow, setHideOutOfWindow] = useState<boolean>(false);
    // "Hide out-of-window" for the Health-map pass-through trend, mirroring the RC
    // Temporal Distribution control: clip the line to the in-window months only.
    const [hidePtoOutOfWindow, setHidePtoOutOfWindow] = useState<boolean>(false);
    const [colorBlindMode, setColorBlindMode] = useState(false);
    const [showUnmappedDialog, setShowUnmappedDialog] = useState(false);
    const [unmappedPage, setUnmappedPage] = useState(0);
    const [drillBarrier, setDrillBarrier] = useState<{nodeId: number; name: string} | null>(null);
    // Failure-gap distribution dialog — opened by clicking the MTBF cell. Carries
    // identifying info for the barrier we're showing the distribution for.
    const [gapDistBarrier, setGapDistBarrier] = useState<{nodeId: number; name: string; uniqueId: string} | null>(null);
    // Explore → "Failure pattern" view state. Mirrors the Report Clustering Temporal
    // Distribution panel controls (sort + min-in-window + hover sync), but plotting each
    // barrier's FAILURE events over time instead of cluster reports.
    const [failurePatternSort, setFailurePatternSort] = useState<TemporalSort>('count');
    const [failurePatternMinSample, setFailurePatternMinSample] = useState<number>(1);
    const [failurePatternHoveredKey, setFailurePatternHoveredKey] = useState<string | null>(null);
    // Weibull-fit overlay toggle inside the gap-distribution dialog. Default OFF
    // so analysts see the empirical view first; opt-in to the parametric overlay.
    const [showWeibullFit, setShowWeibullFit] = useState(false);
    // Time horizon (in days) for the interactive "P(failure within N days)" lookup
    // in the Weibull stats block. Default 30 — common planning-horizon length.
    const [predictionDays, setPredictionDays] = useState<number>(30);
    const [drillPage, setDrillPage] = useState(0);
    const [drillPageSize, setDrillPageSize] = useState(UNMAPPED_PAGE_SIZE);

    // Active success/failure colors — colorblind-safe pair when enabled.
    const colorSuccess = colorBlindMode ? COLOR_SUCCESS_CVD : COLOR_SUCCESS;
    const colorFailure = colorBlindMode ? COLOR_FAILURE_CVD : COLOR_FAILURE;

    const headerCanvasRef = useRef<HTMLCanvasElement>(null);
    const bodyCanvasRef = useRef<HTMLCanvasElement>(null);
    const matrixScrollRef = useRef<HTMLDivElement>(null);
    const barrierColumnRef = useRef<HTMLDivElement>(null);
    const chartPanelRef = useRef<HTMLDivElement>(null);
    const overviewCanvasRef = useRef<HTMLCanvasElement>(null);
    const overviewContainerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // Last page scroll position recorded while NOT fullscreen — restored on fullscreen exit
    // (a panel going position:fixed leaves the flow and clamps scrollTop to ~0).
    const lastScrollTopRef = useRef(0);
    const anyFullscreenRef = useRef(false);

    const anyFullscreen = detailedFullscreen || fullscreenChart !== null;
    anyFullscreenRef.current = anyFullscreen;

    // Record the page scroll position only while not fullscreen (so we keep the
    // pre-fullscreen value, not the clamped one the browser produces when a panel
    // becomes position:fixed).
    const handleContainerScroll = useCallback((): void => {
        if (!anyFullscreenRef.current && scrollContainerRef.current) {
            lastScrollTopRef.current = scrollContainerRef.current.scrollTop;
        }
    }, []);

    // Restore the page scroll position when leaving any fullscreen panel.
    useEffect(() => {
        if (anyFullscreen) return;
        const target = lastScrollTopRef.current;
        const id = requestAnimationFrame(() => {
            if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = target;
        });
        return (): void => cancelAnimationFrame(id);
    }, [anyFullscreen]);

    // Heavy aggregation runs off-thread in a Web Worker. `analysis` holds the
    // deserialized results; `computing` is true while a recompute is in flight.
    const workerRef = useRef<Worker | null>(null);
    const reqIdRef = useRef(0);
    const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
    const [computing, setComputing] = useState(false);

    // Create the aggregation worker once; tear it down on unmount.
    useEffect(() => {
        const worker = new Worker(new URL('../../../../../../../utils/barrier-analysis.worker.ts', import.meta.url));
        worker.onmessage = (e: MessageEvent): void => {
            const {requestId, result} = e.data as {requestId: number; result: SerializedAnalysis};
            if (requestId !== reqIdRef.current) return; // ignore stale responses
            setAnalysis(deserializeAnalysis(result));
            setComputing(false);
        };
        workerRef.current = worker;
        return (): void => {
            worker.terminate();
            workerRef.current = null;
        };
    }, []);

    // Escape key exits chart / detailed fullscreen
    useEscapeKey(!!fullscreenChart, (): void => setFullscreenChart(null));
    useEscapeKey(detailedFullscreen, (): void => setDetailedFullscreen(false));

    // Sub-tabs render mutually-exclusively so no scroll-into-view is needed — the
    // active panel always sits below the sticky header.

    // Auto-scroll the success/failure chart panel into view whenever the user adds a
    // barrier (single click on the chart icon, or "Select All"). The panel renders
    // below the heatmap, so without this the user can click the chart icon and not
    // see anything happen — looks like the toggle is broken. Triggers on size growth
    // only; removing a barrier doesn't scroll.
    const prevChartSelectedSizeRef = useRef(chartSelectedBarriers.size);
    useEffect(() => {
        const prev = prevChartSelectedSizeRef.current;
        prevChartSelectedSizeRef.current = chartSelectedBarriers.size;
        if (chartSelectedBarriers.size <= prev) return undefined;
        // requestAnimationFrame waits for the chart panel to mount on the first add
        // (the panel only renders once chartData becomes non-null).
        const id = requestAnimationFrame(() => {
            chartPanelRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        });
        return (): void => cancelAnimationFrame(id);
    }, [chartSelectedBarriers]);

    useEffect(() => {
        if (!dataSource || barrierAnalysisCache !== null) return;
        const descriptorIds = primaryFilter.selectedDescriptors.size > 0
            ? Array.from(primaryFilter.selectedDescriptors)
            : null;
        dataSource.getWorkbookBarrierAnalysis(primaryFilter.dateFrom, primaryFilter.dateTo, descriptorIds)
            .then(setBarrierAnalysisCache)
            .catch(() => { /* errors surfaced via global handler */ });
    }, [dataSource, barrierAnalysisCache, primaryFilter, setBarrierAnalysisCache]);

    // Extract fault tree nodes
    const faultTreeNodes = useMemo((): FaultTreeNode[] => {
        const initiatingEvent = eventSequenceData?.events?.[0];
        const faultTree = initiatingEvent?.faultTree;
        return faultTree?.faultTreeNodes || [];
    }, [eventSequenceData]);

    // Build barrier hierarchy
    const {childrenMap, topLevelIds, nodeById, hasBarrierDescendantsMap} = useMemo(() => {
        const result = buildBarrierHierarchy(faultTreeNodes);
        const nById = new Map<number, FaultTreeNode>();
        faultTreeNodes.forEach((n) => nById.set(n.id, n));

        const topIds = result.rows.map((r) => r.node.id);

        // Build hasBarrierDescendants map
        const hbdMap = new Map<number, boolean>();
        result.rows.forEach((r) => hbdMap.set(r.node.id, r.hasBarrierDescendants));
        // Also fill in descendants that aren't top-level
        function fillDescendants(ids: number[]): void {
            ids.forEach((id) => {
                const children = result.childrenMap.get(id) || [];
                children.forEach((childId) => {
                    const childNode = nById.get(childId);
                    if (childNode) {
                        const childChildren = result.childrenMap.get(childId) || [];
                        hbdMap.set(childId, childChildren.length > 0);
                    }
                });
                fillDescendants(children);
            });
        }
        fillDescendants(topIds);

        return {
            childrenMap: result.childrenMap,
            topLevelIds: topIds,
            nodeById: nById,
            hasBarrierDescendantsMap: hbdMap,
        };
    }, [faultTreeNodes]);

    // All barrier nodes (expand-state independent) — used for system-level metrics
    const allBarrierNodes = useMemo(
        () => faultTreeNodes.filter((n) => n.nodeClass === 'BARRIER'),
        [faultTreeNodes]
    );

    // Set of barrier IDs — used to filter report.nodeMappings (which can include non-barrier nodes)
    const allBarrierIdSet = useMemo(
        () => new Set(allBarrierNodes.map((b) => b.id)),
        [allBarrierNodes]
    );

    // Get visible rows based on expanded state
    const allVisibleRows = useMemo(
        () => {
            const rows = getVisibleRows(topLevelIds, childrenMap, nodeById, expanded, hasBarrierDescendantsMap);
            if (!topLevelOnly) return rows;
            const topLevelSet = new Set(topLevelIds);
            return rows.filter((r) => topLevelSet.has(r.node.id));
        },
        [topLevelIds, childrenMap, nodeById, expanded, hasBarrierDescendantsMap, topLevelOnly]
    );

    // BFF returns only MAPPED reports — sort by date desc then identifier asc
    const allMappedReports = useMemo((): IrpBarrierReportDTO[] => {
        if (!barrierAnalysisCache) return [];
        return barrierAnalysisCache.reports
            .slice()
            .sort((a, b) => {
                const dateA = a.dateTime ? new Date(a.dateTime).getTime() : 0;
                const dateB = b.dateTime ? new Date(b.dateTime).getTime() : 0;
                if (dateB !== dateA) return dateB - dateA;
                return (a.uniqueId || '').localeCompare(b.uniqueId || '');
            });
    }, [barrierAnalysisCache]);

    // Send the resident dataset to the worker whenever the report set or barriers change.
    useEffect(() => {
        const worker = workerRef.current;
        if (!worker || !barrierAnalysisCache) return;
        worker.postMessage({
            type: 'init',
            reports: barrierAnalysisCache.reports,
            barriers: allBarrierNodes.map((b) => ({id: b.id, uniqueId: b.uniqueId ?? null, name: b.name ?? null})),
            topLevelIds,
        });
    }, [barrierAnalysisCache, allBarrierNodes, topLevelIds]);

    // Request a recompute whenever the dataset or any aggregation-affecting config changes.
    useEffect(() => {
        const worker = workerRef.current;
        if (!worker || !barrierAnalysisCache) return;
        const requestId = ++reqIdRef.current;
        setComputing(true);
        worker.postMessage({
            type: 'compute',
            requestId,
            config: {selectedSource, topLevelOnly, timeGranularity},
        });
    }, [barrierAnalysisCache, allBarrierNodes, topLevelIds, selectedSource, topLevelOnly, timeGranularity]);

    // Worker-derived aggregates (null until the first compute returns).
    const availableSources = analysis?.availableSources ?? EMPTY_STRING_ARRAY;
    const barrierTotals = analysis?.barrierTotals ?? EMPTY_BARRIER_TOTALS;

    // Report count per source — for demand denominators in the source-comparison view/export.
    const sourceReportCounts = useMemo((): Map<string, number> => {
        const counts = new Map<string, number>();
        allMappedReports.forEach((r) => {
            const s = r.sourceName || '';
            counts.set(s, (counts.get(s) ?? 0) + 1);
        });
        return counts;
    }, [allMappedReports]);

    // Apply source filter
    const sourceFilteredReports = useMemo((): IrpBarrierReportDTO[] => {
        if (selectedSource === 'All') return allMappedReports;
        return allMappedReports.filter((r) => r.sourceName === selectedSource);
    }, [allMappedReports, selectedSource]);

    // Reset source filter if selected source no longer has reports
    useEffect(() => {
        if (selectedSource !== 'All' && availableSources.length > 0 && !availableSources.includes(selectedSource)) {
            setSelectedSource('All');
        }
    }, [availableSources, selectedSource]);

    // Barrier rows for the Explore views — optionally narrowed by the barrier search box.
    const visibleRows = useMemo(() => {
        const q = barrierSearch.trim().toLowerCase();
        if (!q) return allVisibleRows;
        return allVisibleRows.filter((row) =>
            (row.node.name || '').toLowerCase().includes(q) || (row.node.uniqueId || '').toLowerCase().includes(q)
        );
    }, [allVisibleRows, barrierSearch]);

    const mappedReports = sourceFilteredReports;

    // Calendar view aggregation: per-day demand across all currently-visible barriers,
    // intersected with the IRP date filter. Each report contributes 1 to `demand` on
    // its calendar day; its visible-barrier mappings contribute to success/failure
    // (so one report with 1 ✓ + 2 ✗ adds 1 demand, 1 success, 2 failures on that day).
    const calendarVisibleBarrierIdSet = useMemo(
        () => new Set(visibleRows.map((r) => r.node.id)),
        [visibleRows]
    );
    const dailyDemand = useMemo((): Map<number, {demand: number; success: number; failure: number}> => {
        const result = new Map<number, {demand: number; success: number; failure: number}>();
        if (viewMode !== 'calendar') return result;
        const fromTs = primaryFilter.dateFrom?.getTime();
        const toTs = primaryFilter.dateTo?.getTime();
        for (const report of mappedReports) {
            if (!report.dateTime) continue;
            const t = new Date(report.dateTime).getTime();
            if (isNaN(t)) continue;
            if (fromTs != null && t < fromTs) continue;
            if (toTs != null && t > toTs) continue;
            let s = 0;
            let f = 0;
            let challenged = false;
            for (const nm of report.nodeMappings) {
                if (!calendarVisibleBarrierIdSet.has(nm.nodeId)) continue;
                if (nm.positiveInteraction === true) { s++; challenged = true; }
                else if (nm.positiveInteraction === false) { f++; challenged = true; }
            }
            if (!challenged) continue;
            const d = new Date(t);
            d.setHours(0, 0, 0, 0);
            const dayTs = d.getTime();
            const entry = result.get(dayTs);
            if (entry) { entry.demand++; entry.success += s; entry.failure += f; }
            else result.set(dayTs, {demand: 1, success: s, failure: f});
        }
        return result;
    }, [viewMode, mappedReports, calendarVisibleBarrierIdSet, primaryFilter.dateFrom, primaryFilter.dateTo]);

    // Global max demand — color scale denominator. Computed across the entire IRP filter
    // range so intensity stays comparable across pagination pages.
    const globalMaxDemand = useMemo((): number => {
        let max = 0;
        dailyDemand.forEach((v) => { if (v.demand > max) max = v.demand; });
        return max;
    }, [dailyDemand]);

    // Reports for the day the user clicked in the calendar. One row per report; each
    // row lists the visible-barrier interactions (success/failure) inside that report.
    const calendarDrillReports = useMemo((): Array<{
        reportId: number;
        uniqueId: string;
        sourceName: string;
        barriers: Array<{nodeId: number; uniqueId: string; name: string; outcome: 'SUCCESS' | 'FAILURE'}>;
    }> => {
        if (!calendarDrillDate) return [];
        const dayStart = new Date(calendarDrillDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayStartTs = dayStart.getTime();
        const dayEndTs = dayStartTs + 86400000;
        const barrierById = new Map<number, FaultTreeNode>();
        allBarrierNodes.forEach((b) => barrierById.set(b.id, b));
        const result: Array<{
            reportId: number;
            uniqueId: string;
            sourceName: string;
            barriers: Array<{nodeId: number; uniqueId: string; name: string; outcome: 'SUCCESS' | 'FAILURE'}>;
        }> = [];
        for (const report of mappedReports) {
            if (!report.dateTime) continue;
            const t = new Date(report.dateTime).getTime();
            if (isNaN(t) || t < dayStartTs || t >= dayEndTs) continue;
            const barriers: Array<{nodeId: number; uniqueId: string; name: string; outcome: 'SUCCESS' | 'FAILURE'}> = [];
            for (const nm of report.nodeMappings) {
                if (!calendarVisibleBarrierIdSet.has(nm.nodeId)) continue;
                if (nm.positiveInteraction !== true && nm.positiveInteraction !== false) continue;
                const node = barrierById.get(nm.nodeId);
                barriers.push({
                    nodeId: nm.nodeId,
                    uniqueId: node?.uniqueId ?? '',
                    name: node?.name ?? '',
                    outcome: nm.positiveInteraction ? 'SUCCESS' : 'FAILURE',
                });
            }
            if (barriers.length === 0) continue;
            result.push({
                reportId: report.reportId,
                uniqueId: report.uniqueId ?? '',
                sourceName: report.sourceName ?? '',
                barriers,
            });
        }
        return result;
    }, [calendarDrillDate, mappedReports, calendarVisibleBarrierIdSet, allBarrierNodes]);

    // Period-over-period boundaries for the barrier trend window. `start` is the prior
    // window's lower bound, `recentStart` is the split, `end` is "now". Anchors to the
    // IRP To-Date when set; falls back to the latest report date.
    //
    // Numeric windows snap to **calendar months** rather than running on 30.4-day
    // durations: "3 mo" = "the last 3 full calendar months ending at end's month."
    // This keeps the visual divider aligned with cell boundaries — when end is in
    // Feb 2026, recent = Dec/Jan/Feb and prior = Sep/Oct/Nov, 3 cells per side.
    const barrierTrendBoundary = useMemo((): TrendWindowBoundary | null => {
        return computeTrendWindowBoundary(
            barrierTrendWindow,
            primaryFilter.dateFrom ? primaryFilter.dateFrom.getTime() : null,
            primaryFilter.dateTo ? primaryFilter.dateTo.getTime() : null,
            () => sourceFilteredReports.map((r) => (r.dateTime ? new Date(r.dateTime).getTime() : NaN)),
        );
    }, [barrierTrendWindow, primaryFilter.dateFrom, primaryFilter.dateTo, sourceFilteredReports]);

    // System-level KPIs computed for the recent window — mirror of the worker's
    // systemMetrics but scoped to in-window reports.
    //   totalReports        — reports with a date in [recentStart, end].
    //   challengedReports   — of those, the ones with at least one challenged barrier.
    //   allFailedReports    — challenged reports where every challenged barrier failed.
    //   passThroughRate     — allFailedReports / challengedReports.
    const windowSystemMetrics = useMemo((): {
        totalReports: number;
        challengedReports: number;
        unmappedReports: number;
        allFailedReports: number;
        passThroughRate: number | null;
    } => {
        if (!barrierTrendBoundary) return {totalReports: 0, challengedReports: 0, unmappedReports: 0, allFailedReports: 0, passThroughRate: null};
        const {recentStart, end} = barrierTrendBoundary;
        let totalReports = 0;
        let challengedReports = 0;
        let allFailedReports = 0;
        for (const report of sourceFilteredReports) {
            if (!report.dateTime) continue;
            const t = new Date(report.dateTime).getTime();
            if (isNaN(t)) continue;
            if (t < recentStart || t > end) continue;
            totalReports++;
            // Match the worker: a barrier mapping with a success/failure outcome counts as
            // "challenged"; null (no-outcome) mappings are ignored. Reports with no
            // success/failure barrier mapping contribute to the "without barrier data" total.
            let challenged = false;
            let anySuccess = false;
            for (const nm of report.nodeMappings) {
                if (!allBarrierIdSet.has(nm.nodeId)) continue;
                if (nm.positiveInteraction === true) { challenged = true; anySuccess = true; }
                else if (nm.positiveInteraction === false) { challenged = true; }
            }
            if (!challenged) continue;
            challengedReports++;
            if (!anySuccess) allFailedReports++;
        }
        const passThroughRate = challengedReports > 0 ? allFailedReports / challengedReports : null;
        return {totalReports, challengedReports, unmappedReports: totalReports - challengedReports, allFailedReports, passThroughRate};
    }, [sourceFilteredReports, barrierTrendBoundary, allBarrierIdSet]);

    // Per-barrier recent/prior success+failure counts, effectiveness delta, AND the
    // regression slopes of effectiveness rate and demand rate WITHIN the recent
    // window (monthly buckets). Source-filter-aware. Single-pass over reports for
    // the aggregate counts; the recent-slopes are fitted from per-month sub-buckets
    // accumulated in the same pass. Demand rate uses the same definition as the
    // worker's all-time `demandSlope` (challenged-in-month / reports-in-month, in pp/mo).
    const barrierTrendStats = useMemo((): Map<number, {
        recentSuccess: number;
        recentFailure: number;
        priorSuccess: number;
        priorFailure: number;
        recentChallenges: number;
        priorChallenges: number;
        recentEffRate: number | null;
        priorEffRate: number | null;
        deltaEffRate: number | null;
        recentDemand: number;
        priorDemand: number;
        deltaDemand: number;
        recentSlope: number | null;
        recentDemandSlope: number | null;
        windowAwareStatus: 'critical' | 'warning' | 'healthy' | null;
    }> => {
        type StatEntry = {
            recentSuccess: number; recentFailure: number;
            priorSuccess: number; priorFailure: number;
            recentChallenges: number; priorChallenges: number;
            recentEffRate: number | null; priorEffRate: number | null; deltaEffRate: number | null;
            recentDemand: number; priorDemand: number; deltaDemand: number;
            recentSlope: number | null;
            recentDemandSlope: number | null;
            windowAwareStatus: 'critical' | 'warning' | 'healthy' | null;
            _recentMonthly?: Map<number, {success: number; failure: number}>;
        };
        const stats = new Map<number, StatEntry>();
        allBarrierNodes.forEach((b) => stats.set(b.id, {
            recentSuccess: 0, recentFailure: 0, priorSuccess: 0, priorFailure: 0,
            recentChallenges: 0, priorChallenges: 0,
            recentEffRate: null, priorEffRate: null, deltaEffRate: null,
            recentDemand: 0, priorDemand: 0, deltaDemand: 0,
            recentSlope: null,
            recentDemandSlope: null,
            windowAwareStatus: null,
            _recentMonthly: new Map(),
        }));
        // Window-restricted aggregates skip themselves when there's no boundary
        // (e.g. trend window = 'allTime' → `barrierTrendBoundary` is null).
        const start = barrierTrendBoundary?.start;
        const recentStart = barrierTrendBoundary?.recentStart;
        const end = barrierTrendBoundary?.end;
        // Per-month report totals (recent window only). Used as the demand-slope
        // denominator: monthly demand rate = challenged_in_month / reports_in_month.
        const recentMonthlyReportCount = new Map<number, number>();

        // Single pass: window-restricted aggregates and monthly buckets for in-window
        // reports. All-time slopes come from the worker; this loop only feeds recent-window
        // stats used by the Top N panel and the main-table window-mode columns.
        for (const report of sourceFilteredReports) {
            if (!report.dateTime) continue;
            const d = new Date(report.dateTime);
            const t = d.getTime();
            if (isNaN(t)) continue;
            let bucket: 'recent' | 'prior' | null = null;
            if (recentStart != null && end != null && t >= recentStart && t <= end) bucket = 'recent';
            else if (start != null && recentStart != null && t >= start && t < recentStart) bucket = 'prior';
            const monthKey = d.getFullYear() * 12 + d.getMonth();
            if (bucket === 'recent') {
                recentMonthlyReportCount.set(monthKey, (recentMonthlyReportCount.get(monthKey) ?? 0) + 1);
            }
            for (const nm of report.nodeMappings) {
                const s = stats.get(nm.nodeId);
                if (!s) continue;
                const isSuccess = nm.positiveInteraction === true;
                const isFailure = nm.positiveInteraction === false;
                if (!isSuccess && !isFailure) continue;
                if (bucket === 'recent') {
                    if (isSuccess) { s.recentSuccess++; s.recentDemand++; }
                    else { s.recentFailure++; s.recentDemand++; }
                    const rm = s._recentMonthly!.get(monthKey) ?? {success: 0, failure: 0};
                    if (isSuccess) rm.success++; else rm.failure++;
                    s._recentMonthly!.set(monthKey, rm);
                } else if (bucket === 'prior') {
                    if (isSuccess) { s.priorSuccess++; s.priorDemand++; }
                    else { s.priorFailure++; s.priorDemand++; }
                }
            }
        }
        // OLS slope of effectiveness rate over (monthIndex, effRate) — units: pp / month.
        const fitSlope = (points: Array<{x: number; y: number}>): number | null => {
            if (points.length < 2) return null;
            let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
            for (const p of points) {
                sumX += p.x; sumY += p.y; sumXY += p.x * p.y; sumX2 += p.x * p.x;
            }
            const n = points.length;
            const denom = n * sumX2 - sumX * sumX;
            return denom === 0 ? null : (n * sumXY - sumX * sumY) / denom;
        };
        stats.forEach((s) => {
            const rTotal = s.recentSuccess + s.recentFailure;
            const pTotal = s.priorSuccess + s.priorFailure;
            s.recentChallenges = rTotal;
            s.priorChallenges = pTotal;
            s.recentEffRate = rTotal > 0 ? s.recentSuccess / rTotal : null;
            s.priorEffRate = pTotal > 0 ? s.priorSuccess / pTotal : null;
            s.deltaEffRate = (s.recentEffRate != null && s.priorEffRate != null)
                ? s.recentEffRate - s.priorEffRate
                : null;
            s.deltaDemand = s.recentDemand - s.priorDemand;
            // Window-aware status: same thresholds (< 70% critical, < 90% warning) but
            // computed from the recent window only. Null when there are no recent
            // challenges to evaluate.
            if (s.recentEffRate != null) {
                s.windowAwareStatus = s.recentEffRate < 0.7 ? 'critical'
                    : s.recentEffRate < 0.9 ? 'warning'
                        : 'healthy';
            }
            // Recent-window monthly slopes (effectiveness and demand). Both filter out
            // months when the barrier had zero challenges, mirroring the worker's
            // all-time slope methodology — otherwise dormant months would emit phantom
            // zeros that flatten the slope.
            //
            // Min-points threshold = 3 for window slopes (vs 2 for all-time): a 2-point
            // OLS over a 3-month window is too noisy to read as a trend signal.
            const WINDOW_MIN_SLOPE_POINTS = 3;
            const recent = s._recentMonthly!;
            const sortedRecent = Array.from(recent.entries()).sort((a, b) => a[0] - b[0]);
            if (sortedRecent.length >= 2) {
                const baseMonth = sortedRecent[0][0];
                const effPoints = sortedRecent
                    .map(([mk, mv]) => {
                        const total = mv.success + mv.failure;
                        return total > 0 ? {x: mk - baseMonth, y: mv.success / total} : null;
                    })
                    .filter((p): p is {x: number; y: number} => p != null);
                s.recentSlope = effPoints.length >= WINDOW_MIN_SLOPE_POINTS ? fitSlope(effPoints) : null;
                // Demand rate = challenged_in_month / reports_in_month. Same units
                // (pp/mo) as the worker's all-time demandSlope.
                const demandPoints = sortedRecent
                    .map(([mk, mv]) => {
                        const total = mv.success + mv.failure;
                        const reportsInMonth = recentMonthlyReportCount.get(mk) ?? 0;
                        return total > 0 && reportsInMonth > 0
                            ? {x: mk - baseMonth, y: total / reportsInMonth}
                            : null;
                    })
                    .filter((p): p is {x: number; y: number} => p != null);
                s.recentDemandSlope = demandPoints.length >= WINDOW_MIN_SLOPE_POINTS ? fitSlope(demandPoints) : null;
            }
            delete s._recentMonthly;
        });
        return stats;
    }, [allBarrierNodes, sourceFilteredReports, barrierTrendBoundary]);

    // Window-aware MTBF per barrier. Splits each barrier's failure events into the
    // recent and prior windows by `dateTime`, sorts each, and returns the mean gap in
    // days. Null when a window contains fewer than 2 failures (no gap to average).
    // Wear-out reading: recent ≪ prior means the gap between failures shortened —
    // failures arriving more frequently lately.
    const barrierWindowMtbf = useMemo((): Map<number, {recent: number | null; prior: number | null}> => {
        const out = new Map<number, {recent: number | null; prior: number | null}>();
        if (!barrierTrendBoundary) {
            allBarrierNodes.forEach((b) => out.set(b.id, {recent: null, prior: null}));
            return out;
        }
        const {start, recentStart, end} = barrierTrendBoundary;
        const failuresByBarrier = new Map<number, {recent: number[]; prior: number[]}>();
        allBarrierNodes.forEach((b) => failuresByBarrier.set(b.id, {recent: [], prior: []}));
        for (const report of sourceFilteredReports) {
            if (!report.dateTime) continue;
            const t = new Date(report.dateTime).getTime();
            if (isNaN(t)) continue;
            let bucket: 'recent' | 'prior' | null = null;
            if (t >= recentStart && t <= end) bucket = 'recent';
            else if (t >= start && t < recentStart) bucket = 'prior';
            if (!bucket) continue;
            for (const nm of report.nodeMappings) {
                if (nm.positiveInteraction !== false) continue; // explicit failures only
                const entry = failuresByBarrier.get(nm.nodeId);
                if (!entry) continue;
                entry[bucket].push(t);
            }
        }
        const MS_PER_DAY = 86400000;
        const meanGapDays = (sorted: number[]): number | null => {
            if (sorted.length < 2) return null;
            let totalGap = 0;
            for (let i = 1; i < sorted.length; i++) {
                totalGap += sorted[i] - sorted[i - 1];
            }
            return Math.round(totalGap / (sorted.length - 1) / MS_PER_DAY);
        };
        failuresByBarrier.forEach((failures, id) => {
            failures.recent.sort((a, b) => a - b);
            failures.prior.sort((a, b) => a - b);
            out.set(id, {recent: meanGapDays(failures.recent), prior: meanGapDays(failures.prior)});
        });
        return out;
    }, [allBarrierNodes, sourceFilteredReports, barrierTrendBoundary]);

    // Main barrier table rows. In window mode + "hide" submode we drop barriers with
    // zero in-band activity (recent + prior); the band region is the only thing visible
    // so a row that contributes nothing to it would be blank. In "show out-of-band"
    // submode (default) we keep all rows. All-time: no filter.
    const tableRows = useMemo(() => {
        if (topViewMode !== 'window' || !hideOutOfWindow) return visibleRows;
        return visibleRows.filter((row) => {
            const s = barrierTrendStats.get(row.node.id);
            return ((s?.recentChallenges ?? 0) + (s?.priorChallenges ?? 0)) > 0;
        });
    }, [visibleRows, topViewMode, hideOutOfWindow, barrierTrendStats]);

    // Bucket cutoff keys at the current bucket granularity. `band` is the trend-window
    // lower bound (start of prior half); `recent` is the split between prior and recent
    // halves. Buckets with key >= these strings sit inside the respective region.
    const bandBucketCutoffKeys = useMemo((): {band: string; recent: string} | null => {
        if (topViewMode !== 'window' || !barrierTrendBoundary) return null;
        const keyFor = (ts: number): string => {
            const d = new Date(ts);
            if (timeGranularity === 'monthly') {
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            }
            if (timeGranularity === 'quarterly') {
                const q = Math.ceil((d.getMonth() + 1) / 3);
                return `${d.getFullYear()}-Q${q}`;
            }
            return `${d.getFullYear()}`;
        };
        return {band: keyFor(barrierTrendBoundary.start), recent: keyFor(barrierTrendBoundary.recentStart)};
    }, [topViewMode, barrierTrendBoundary, timeGranularity]);

    // Reports shown in the effectiveness-view canvas, sorted newest-first (column 0
    // = newest report) so the recent half sits on the left where analysts look first.
    // "Hide" submode additionally filters to reports inside the trend-window band
    // [start, end]; reports with no date sink to the right alongside the oldest.
    const tableMappedReports = useMemo(() => {
        const sorted = [...mappedReports].sort((a, b) => {
            const ta = a.dateTime ? new Date(a.dateTime).getTime() : -Infinity;
            const tb = b.dateTime ? new Date(b.dateTime).getTime() : -Infinity;
            return tb - ta;
        });
        if (topViewMode === 'window' && hideOutOfWindow && barrierTrendBoundary) {
            const {start, end} = barrierTrendBoundary;
            return sorted.filter((r) => {
                if (!r.dateTime) return false;
                const t = new Date(r.dateTime).getTime();
                return !isNaN(t) && t >= start && t <= end;
            });
        }
        return sorted;
    }, [mappedReports, topViewMode, hideOutOfWindow, barrierTrendBoundary]);

    // Time buckets shown in the timeline-view canvas, reversed so the newest bucket
    // sits on the left. In window mode + "hide" submode we additionally filter to
    // buckets inside the band [start, end].
    const tableTimeBuckets = useMemo(() => {
        const buckets = analysis?.timeBuckets ?? EMPTY_TIME_BUCKETS;
        const filtered = topViewMode === 'window' && hideOutOfWindow && bandBucketCutoffKeys
            ? buckets.filter((b) => b.key >= bandBucketCutoffKeys.band)
            : buckets;
        return [...filtered].reverse();
    }, [analysis?.timeBuckets, topViewMode, hideOutOfWindow, bandBucketCutoffKeys]);

    // Window-mode barrier-health distribution (counts of healthy / warning / critical /
    // unchallenged) — same buckets as the worker's executiveSummary but driven by
    // `windowAwareStatus`. Respects topLevelOnly so the count matches what the table
    // displays.
    const windowBarrierHealth = useMemo((): {
        healthyCount: number;
        warningCount: number;
        criticalCount: number;
        unchallengedCount: number;
        totalBarriers: number;
    } => {
        const topLevelSet = new Set(topLevelIds);
        let healthy = 0;
        let warning = 0;
        let critical = 0;
        let unchallenged = 0;
        let total = 0;
        for (const barrier of allBarrierNodes) {
            if (topLevelOnly && !topLevelSet.has(barrier.id)) continue;
            total++;
            const ws = barrierTrendStats.get(barrier.id)?.windowAwareStatus;
            if (ws === 'healthy') healthy++;
            else if (ws === 'warning') warning++;
            else if (ws === 'critical') critical++;
            else unchallenged++;
        }
        return {healthyCount: healthy, warningCount: warning, criticalCount: critical, unchallengedCount: unchallenged, totalBarriers: total};
    }, [allBarrierNodes, topLevelIds, topLevelOnly, barrierTrendStats]);

    // Σ Failure Exposure across the visible barriers — the average number of barrier
    // failures per report. Mirrors the topLevelOnly toggle so the KPI matches the
    // barriers shown in the heatmap. `total` is total failure events; `perReport` is
    // the average; `denom` is the report population used (so the tooltip can show the
    // raw fraction). Returns null for `perReport` when the denominator is 0.
    const avgFailuresPerReport = useMemo((): {
        allTime: {total: number; perReport: number | null; denom: number};
        window: {total: number; perReport: number | null; denom: number};
    } => {
        const topLevelSet = new Set(topLevelIds);
        let allTimeFailures = 0;
        let windowFailures = 0;
        for (const barrier of allBarrierNodes) {
            if (topLevelOnly && !topLevelSet.has(barrier.id)) continue;
            allTimeFailures += barrierTotals.get(barrier.id)?.failure ?? 0;
            windowFailures += barrierTrendStats.get(barrier.id)?.recentFailure ?? 0;
        }
        // Denominator = CHALLENGED reports (≥1 barrier exercised), NOT all reports.
        // Unchallenged reports aren't defense events, so counting them as zeros would
        // dilute "barriers failing per report" — and this keeps the KPI as the exact
        // complement of the Challenged-vs-Failed scatter's "Avg held" (held + failed =
        // challenged, per challenged report). This now matches Σ Failure Exposure's
        // denominator (scored reports), since per-barrier F.Exp was rebased to scored reports too.
        const allTimeDenom = analysis?.systemMetrics?.passThrough?.total ?? 0;
        const windowDenom = windowSystemMetrics.challengedReports;
        return {
            allTime: {
                total: allTimeFailures,
                perReport: allTimeDenom > 0 ? allTimeFailures / allTimeDenom : null,
                denom: allTimeDenom,
            },
            window: {
                total: windowFailures,
                perReport: windowDenom > 0 ? windowFailures / windowDenom : null,
                denom: windowDenom,
            },
        };
    }, [allBarrierNodes, topLevelIds, topLevelOnly, barrierTotals, barrierTrendStats, analysis?.systemMetrics?.passThrough?.total, windowSystemMetrics]);

    // Year range for the calendar: prefer the IRP filter range; fall back to the actual
    // span of report dates when the filter is unset.
    const calendarYearRange = useMemo((): {start: number; end: number} | null => {
        if (viewMode !== 'calendar') return null;
        if (primaryFilter.dateFrom && primaryFilter.dateTo) {
            return {start: primaryFilter.dateFrom.getFullYear(), end: primaryFilter.dateTo.getFullYear()};
        }
        let s: number | null = null;
        let e: number | null = null;
        for (const report of mappedReports) {
            if (!report.dateTime) continue;
            const d = new Date(report.dateTime);
            if (isNaN(d.getTime())) continue;
            const y = d.getFullYear();
            if (s == null || y < s) s = y;
            if (e == null || y > e) e = y;
        }
        if (s == null || e == null) return null;
        return {start: s, end: e};
    }, [viewMode, primaryFilter.dateFrom, primaryFilter.dateTo, mappedReports]);

    const calendarTotalPages = useMemo((): number => {
        if (!calendarYearRange) return 1;
        const span = calendarYearRange.end - calendarYearRange.start + 1;
        return Math.max(1, Math.ceil(span / calendarYearsPerPage));
    }, [calendarYearRange, calendarYearsPerPage]);

    // Years shown on the current page, sorted newest-first.
    const calendarVisibleYears = useMemo((): number[] => {
        if (!calendarYearRange) return [];
        const {start, end} = calendarYearRange;
        const pageEnd = end - calendarPage * calendarYearsPerPage;
        const pageStart = Math.max(start, pageEnd - calendarYearsPerPage + 1);
        if (pageEnd < start) return [];
        const years: number[] = [];
        for (let y = pageEnd; y >= pageStart; y--) years.push(y);
        return years;
    }, [calendarYearRange, calendarPage, calendarYearsPerPage]);

    // Reset to page 0 when the underlying year range or visibility set changes — keeps
    // pagination from landing on a stale page if filters change.
    useEffect(() => {
        setCalendarPage(0);
    }, [calendarYearRange?.start, calendarYearRange?.end, calendarVisibleBarrierIdSet]);

    // System pass-through rate — computed off-thread by the worker.
    const systemPassThrough = analysis?.systemMetrics.passThrough ?? DEFAULT_PASS_THROUGH;
    const unmappedReports = analysis?.systemMetrics.unmappedReports ?? EMPTY_UNMAPPED_REPORTS;

    // Unmapped-reports list scoped to the active mode — all-time list in all-time mode,
    // restricted to the recent window in window mode. Drives the "Reports without barrier
    // data" dialog and the link's count on the Reports Assessed card.
    const displayedUnmappedReports = useMemo(() => {
        if (topViewMode === 'allTime' || !barrierTrendBoundary) return unmappedReports;
        const {recentStart, end} = barrierTrendBoundary;
        return unmappedReports.filter((r) => {
            if (!r.dateTime) return false;
            const t = new Date(r.dateTime).getTime();
            return !isNaN(t) && t >= recentStart && t <= end;
        });
    }, [unmappedReports, topViewMode, barrierTrendBoundary]);

    // Executive summary metrics — computed off-thread by the worker.
    const executiveSummary = analysis?.executiveSummary ?? EMPTY_EXECUTIVE_SUMMARY;

    // Extend the worker-supplied Top N pool with barriers that are cumulatively HEALTHY
    // but window-aware critical/warning (the V1 limitation we tracked). For each such
    // barrier we synthesize a BarrierHealthEntry from the source-filtered `barrierTotals`
    // and the `allBarrierNodes` registry. Synthesized rows carry the cumulative status
    // (`'healthy'`) — the row's Status chip later reads `windowAwareStatus` and shows
    // the recent-window classification.
    const extendedTopCritical = useMemo(() => {
        const existing = new Set(executiveSummary.topCritical.map((b) => b.nodeId));
        const topLevelSet = new Set(topLevelIds);
        // Metric denominator = scored (challenged) reports, NOT all reports — reports the
        // mapper couldn't score carry no signal and must not dilute Demand Coverage / F.Exp.
        const scoredReports = systemPassThrough.total;
        const additions: typeof executiveSummary.topCritical = [];
        for (const barrier of allBarrierNodes) {
            if (existing.has(barrier.id)) continue;
            if (topLevelOnly && !topLevelSet.has(barrier.id)) continue;
            const stats = barrierTrendStats.get(barrier.id);
            const ws = stats?.windowAwareStatus;
            // Silent-killer test for window mode: a barrier whose RECENT F.Exp is
            // above the threshold deserves attention even when its window-aware
            // status is Healthy. Without this branch, a 92%-effective barrier
            // challenged in 80% of recent reports would never enter the pool.
            const recentFExp = windowSystemMetrics.challengedReports > 0 ? (stats?.recentFailure ?? 0) / windowSystemMetrics.challengedReports : 0;
            const isSilentKillerRecent = recentFExp >= SILENT_KILLER_FEXP_THRESHOLD;
            if (ws !== 'critical' && ws !== 'warning' && !isSilentKillerRecent) continue;
            const totals = barrierTotals.get(barrier.id);
            const successCount = totals?.success ?? 0;
            const failureCount = totals?.failure ?? 0;
            const challenged = successCount + failureCount;
            if (challenged === 0) continue;
            const name = barrier.uniqueId || barrier.name || `ID:${barrier.id}`;
            const fullName = barrier.name || barrier.uniqueId || `ID:${barrier.id}`;
            additions.push({
                nodeId: barrier.id,
                name,
                fullName,
                successCount,
                failureCount,
                demandCoverage: scoredReports > 0 ? challenged / scoredReports : 0,
                effectivenessRate: successCount / challenged,
                failureExposure: scoredReports > 0 ? failureCount / scoredReports : 0,
                status: 'healthy',
            });
        }
        return [...executiveSummary.topCritical, ...additions];
    }, [executiveSummary.topCritical, systemPassThrough.total, allBarrierNodes, topLevelIds, topLevelOnly, barrierTrendStats, barrierTotals, windowSystemMetrics]);

    // Effective status helper — depends on view mode. Window mode prefers window-aware;
    // all-time mode always uses cumulative.
    const effectiveStatusOf = (b: {nodeId: number; status: 'critical' | 'warning' | 'healthy'}): 'critical' | 'warning' | 'healthy' => {
        if (topViewMode === 'allTime') return b.status;
        return barrierTrendStats.get(b.nodeId)?.windowAwareStatus ?? b.status;
    };

    // Counts for the Status dropdown labels — based on the active mode's source pool and
    // effective status. In all-time mode the worker's `topCritical` is the pool, with
    // cumulative status drivers; in window mode we extend the pool with window-aware
    // critical/warning rows that the worker would otherwise have dropped.
    const extendedStatusCounts = useMemo(() => {
        const pool = topViewMode === 'allTime' ? executiveSummary.topCritical : extendedTopCritical;
        let critical = 0;
        let warning = 0;
        for (const b of pool) {
            const es = effectiveStatusOf(b);
            if (es === 'critical') critical++;
            else if (es === 'warning') warning++;
        }
        return {critical, warning, total: pool.length};
        // effectiveStatusOf closure depends on topViewMode + barrierTrendStats.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extendedTopCritical, executiveSummary.topCritical, barrierTrendStats, topViewMode]);

    // Top N "requiring attention" list, optionally narrowed by status (critical/warning)
    // and re-sorted by the chosen `topSort`. The worker pre-sorts by cumulative
    // failureExposure desc, so all-time mode is a free pass-through; window mode
    // must re-sort by window-aware F.Exp because (a) the worker's order is cumulative,
    // and (b) `extendedTopCritical` appends window-only additions to the tail without
    // re-sorting. Trend-based modes re-sort by `barrierTrendStats.deltaEffRate`,
    // with null deltas pushed to the bottom.
    const filteredTopCritical = useMemo(() => {
        // View-mode-aware F.Exp used by sort and tiebreakers. In all-time mode this
        // is the worker's cumulative value; in window mode it's recomputed from the
        // recent-window failure count so the Top N order matches the visible F.Exp pill.
        const fExpOf = (b: {nodeId: number; failureExposure: number}): number => {
            if (topViewMode === 'allTime') return b.failureExposure;
            const stats = barrierTrendStats.get(b.nodeId);
            return windowSystemMetrics.challengedReports > 0 ? (stats?.recentFailure ?? 0) / windowSystemMetrics.challengedReports : 0;
        };
        // In window mode the pool includes window-aware additions; in all-time mode we
        // use the worker's cumulative pool (no window-aware additions make sense).
        const sourcePool = topViewMode === 'allTime' ? executiveSummary.topCritical : extendedTopCritical;
        let filtered = topStatusFilter === 'all'
            ? sourcePool
            : sourcePool.filter((b) => effectiveStatusOf(b) === topStatusFilter);
        // Hard filter (min recent sample) only applies in window mode — it's a window-specific
        // concept and would erase the all-time view.
        if (topMinSample > 0 && topViewMode === 'window') {
            filtered = filtered.filter((b) => {
                const stats = barrierTrendStats.get(b.nodeId);
                return (stats?.recentChallenges ?? 0) >= topMinSample;
            });
        }
        if (topSort === 'failureExposure') {
            // All-time mode is already sorted by the worker; window mode needs an
            // explicit re-sort so window-only additions don't sink to the tail.
            if (topViewMode === 'allTime') return filtered;
            const sorted = [...filtered];
            sorted.sort((a, b) => fExpOf(b) - fExpOf(a));
            return sorted;
        }
        // Trend-based sorts only make sense in window mode; in all-time mode fall back
        // to the failureExposure pass-through.
        if (topViewMode === 'allTime') return filtered;
        const sorted = [...filtered];
        sorted.sort((a, b) => {
            const da = barrierTrendStats.get(a.nodeId)?.deltaEffRate ?? null;
            const db = barrierTrendStats.get(b.nodeId)?.deltaEffRate ?? null;
            // Null deltas (no prior-period data) sink to the bottom.
            if (da == null && db == null) return fExpOf(b) - fExpOf(a);
            if (da == null) return 1;
            if (db == null) return -1;
            // mostDegraded: ascending (most negative first).
            // mostImproved:  descending (most positive first).
            return topSort === 'mostDegraded' ? da - db : db - da;
        });
        return sorted;
    }, [extendedTopCritical, executiveSummary.topCritical, topStatusFilter, topSort, barrierTrendStats, topMinSample, topViewMode, windowSystemMetrics]);

    // Drill-down: reports that challenged the selected barrier, most recent first.
    // Computed on demand from the source-filtered reports, so it matches the table's counts.
    const drillReports = useMemo((): Array<{reportId: number; uniqueId: string; dateTime: string | undefined; sourceName: string; outcome: 'SUCCESS' | 'FAILURE'}> => {
        if (!drillBarrier) return [];
        const out: Array<{reportId: number; uniqueId: string; dateTime: string | undefined; sourceName: string; outcome: 'SUCCESS' | 'FAILURE'}> = [];
        for (const r of sourceFilteredReports) {
            const nm = r.nodeMappings.find((m) => m.nodeId === drillBarrier.nodeId);
            if (!nm) continue;
            out.push({
                reportId: r.reportId,
                uniqueId: r.uniqueId,
                dateTime: r.dateTime,
                sourceName: r.sourceName,
                outcome: nm.positiveInteraction === true ? 'SUCCESS' : 'FAILURE',
            });
        }
        out.sort((a, b) => {
            const ta = a.dateTime ? new Date(a.dateTime).getTime() : 0;
            const tb = b.dateTime ? new Date(b.dateTime).getTime() : 0;
            if (tb !== ta) return tb - ta;
            return (a.uniqueId || '').localeCompare(b.uniqueId || '');
        });
        return out;
    }, [drillBarrier, sourceFilteredReports]);

    // Per-barrier failure-gap distribution. MTBF gives the mean gap between failures;
    // this exposes the full shape — a histogram (Probability Density Function approx.)
    // plus a cumulative percentage line (empirical Cumulative Distribution Function).
    // The same MTBF can hide very different shapes (tight clusters vs uniform spacing),
    // so the drill-down lets analysts judge whether MTBF is representative or misleading.
    const gapDistData = useMemo((): GapDistData | null => {
        if (!gapDistBarrier) return null;
        // Gather failure timestamps for this barrier from source-filtered reports.
        const failureTs: number[] = [];
        for (const r of sourceFilteredReports) {
            if (!r.dateTime) continue;
            const nm = r.nodeMappings.find((m) => m.nodeId === gapDistBarrier.nodeId);
            if (!nm || nm.positiveInteraction !== false) continue;
            const t = new Date(r.dateTime).getTime();
            if (isNaN(t)) continue;
            failureTs.push(t);
        }
        failureTs.sort((a, b) => a - b);
        if (failureTs.length < 2) return null;
        // Consecutive gaps in days.
        const MS_PER_DAY = 86400000;
        const gaps: number[] = [];
        for (let i = 1; i < failureTs.length; i++) {
            gaps.push((failureTs[i] - failureTs[i - 1]) / MS_PER_DAY);
        }
        gaps.sort((a, b) => a - b);
        const stats = fiveNumberSummary(gaps);
        // Sturges' formula for bin count — adaptive to sample size, ceiling so small
        // datasets still get at least one bin. NB: gap bins start at min (not 0), so
        // this keeps its own binning rather than using buildDistribution.
        const binCount = Math.max(1, Math.ceil(Math.log2(gaps.length) + 1));
        const range = stats.max - stats.min || 1;  // guard against single-value-repeated edge case
        const binWidth = range / binCount;
        const bins: {start: number; end: number; count: number}[] = [];
        for (let i = 0; i < binCount; i++) {
            bins.push({start: stats.min + i * binWidth, end: stats.min + (i + 1) * binWidth, count: 0});
        }
        for (const g of gaps) {
            const idx = Math.min(Math.floor((g - stats.min) / binWidth), binCount - 1);
            bins[idx].count++;
        }
        // Empirical CDF: each sorted gap contributes 1/n to the cumulative fraction.
        const cdfPoints = gaps.map((g, i) => ({x: g, y: ((i + 1) / gaps.length) * 100}));
        return {gaps, bins, cdfPoints, stats};
    }, [gapDistBarrier, sourceFilteredReports]);

    // Crow-AMSAA (power-law NHPP) reliability-growth fit for the drill-down barrier — the
    // trend-model companion to the Weibull-on-gaps view. Observation window = the analysis
    // date range when set, else the full span of report dates (origin is the earliest
    // report, strictly before the first failure, so every time-from-origin > 0). Auto-opens
    // the "growth" tab on a MEANINGFUL trend (β outside the 0.9–1.1 "random" band), not on
    // a bare significance test — at large n a significance gate flags negligible trends, so
    // we key the tab on Crow's own effect size for consistency with the regime chip.
    const crowAmsaa = useMemo((): {fit: CrowAmsaaFit | null; initialTab: 'gap' | 'growth'} => {
        if (!gapDistBarrier) return {fit: null, initialTab: 'gap'};
        const failureTs: number[] = [];
        let minMs = Infinity;
        let maxMs = -Infinity;
        for (const r of sourceFilteredReports) {
            if (!r.dateTime) continue;
            const t = new Date(r.dateTime).getTime();
            if (isNaN(t)) continue;
            if (t < minMs) minMs = t;
            if (t > maxMs) maxMs = t;
            const nm = r.nodeMappings.find((m) => m.nodeId === gapDistBarrier.nodeId);
            if (nm && nm.positiveInteraction === false) failureTs.push(t);
        }
        const originMs = primaryFilter.dateFrom?.getTime() ?? minMs;
        const endMs = primaryFilter.dateTo?.getTime() ?? maxMs;
        if (!isFinite(originMs) || !isFinite(endMs)) return {fit: null, initialTab: 'gap'};
        const fit = fitCrowAmsaa(failureTs, originMs, endMs);
        // For now, always open on the Gap distribution tab (predictable landing while
        // Crow-AMSAA beds in). To restore effect-size auto-select, replace the line below:
        //   const meaningfulTrend = fit != null && (fit.beta < 0.9 || fit.beta > 1.1);
        //   return {fit, initialTab: meaningfulTrend ? 'growth' : 'gap'};
        return {fit, initialTab: 'gap'};
    }, [gapDistBarrier, sourceFilteredReports, primaryFilter.dateFrom, primaryFilter.dateTo]);

    // Adapter feeding the generic TemporalDistributionPanel for the Explore → "Failure
    // pattern" view: one row per barrier, plotting each report where THAT barrier failed
    // (positiveInteraction === false). Builds a per-barrier failure-timestamp map in a
    // single pass over the reports (O(reports × mappings)) so we never rescan per barrier.
    const failurePatternEntities = useMemo((): TemporalEntity[] => {
        const failureTsByBarrier = new Map<number, number[]>();
        for (const r of sourceFilteredReports) {
            if (!r.dateTime) continue;
            const t = new Date(r.dateTime).getTime();
            if (isNaN(t)) continue;
            for (const m of r.nodeMappings) {
                if (m.positiveInteraction !== false) continue;
                const list = failureTsByBarrier.get(m.nodeId);
                if (list) list.push(t);
                else failureTsByBarrier.set(m.nodeId, [t]);
            }
        }
        let entities = allVisibleRows.map((row): TemporalEntity => {
            const node = row.node;
            const events = failureTsByBarrier.get(node.id) ?? [];
            return {
                key: String(node.id),
                label: node.uniqueId || node.name || '',
                title: node.name || node.uniqueId || '',
                events,
                count: events.length,
                trend: computeCountTrend(events, barrierTrendBoundary),
            };
        });
        entities = entities.sort(makeTemporalEntityComparator(failurePatternSort));
        if (barrierTrendBoundary) {
            entities = entities.filter((e) => (e.trend.recent + e.trend.prior) >= failurePatternMinSample);
        }
        return entities;
    }, [allVisibleRows, sourceFilteredReports, barrierTrendBoundary, failurePatternSort, failurePatternMinSample]);

    // Weibull MLE on the same gaps. Only computes when the overlay is active AND
    // we have ≥ 3 gaps; below that the fit is unstable. Stays null otherwise so
    // the dialog can show "needs more failures" cleanly.
    const weibullFit = useMemo((): WeibullFit | null => {
        if (!showWeibullFit || !gapDistData) return null;
        return fitWeibullMLE(gapDistData.gaps);
    }, [showWeibullFit, gapDistData]);

    // System-level F.Exp distribution: one F.Exp value per visible barrier (respects
    // topLevelOnly and the active trend window). Renders as a histogram + cumulative-%
    // line on the Summary sub-tab. Tells the analyst whether the IRP has a few outlier
    // barriers (sharp tail) or systemic exposure (curve shifted right).
    const fExpDistData = useMemo((): (Distribution & {aboveModerate: number; aboveCritical: number}) | null => {
        const inWindow = topViewMode === 'window';
        // Denominator = scored (challenged) reports, not all reports — see denominator rule.
        const totalReports = inWindow ? windowSystemMetrics.challengedReports : systemPassThrough.total;
        if (totalReports === 0) return null;
        const filtered = topLevelOnly
            ? allBarrierNodes.filter((n) => topLevelIds.includes(n.id))
            : allBarrierNodes;
        const values: number[] = [];
        for (const b of filtered) {
            const failures = inWindow
                ? (barrierTrendStats.get(b.id)?.recentFailure ?? 0)
                : (barrierTotals.get(b.id)?.failure ?? 0);
            values.push(failures / totalReports);
        }
        // Fixed 5-pp bins — F.Exp is bounded, and the 5-pp width matches the
        // silent-killer threshold so the first concerning bin starts at 0.05.
        const dist = buildDistribution(values, 0.05);
        if (!dist) return null;
        const aboveModerate = dist.values.filter((v) => v >= SILENT_KILLER_FEXP_THRESHOLD).length;
        const aboveCritical = dist.values.filter((v) => v >= FEXP_CRITICAL_THRESHOLD).length;
        return {...dist, aboveModerate, aboveCritical};
    }, [topViewMode, windowSystemMetrics.challengedReports, systemPassThrough.total, topLevelOnly, allBarrierNodes, topLevelIds, barrierTrendStats, barrierTotals]);

    // System-level Demand Coverage distribution: one Demand Coverage value per visible
    // barrier (challenged / scored reports; respects topLevelOnly and the active
    // trend window). Descriptive, NOT evaluative — high demand is not "bad", just
    // frequently tested — so the panel uses a neutral palette with no health thresholds.
    // Tells the analyst whether challenges concentrate on a few barriers or spread out.
    const demandCovDistData = useMemo((): Distribution | null => {
        const inWindow = topViewMode === 'window';
        // Denominator = scored (challenged) reports, not all reports — see denominator rule.
        const totalReports = inWindow ? windowSystemMetrics.challengedReports : systemPassThrough.total;
        if (totalReports === 0) return null;
        const filtered = topLevelOnly
            ? allBarrierNodes.filter((n) => topLevelIds.includes(n.id))
            : allBarrierNodes;
        const values: number[] = [];
        for (const b of filtered) {
            const challenged = inWindow
                ? (barrierTrendStats.get(b.id)?.recentChallenges ?? 0)
                : ((barrierTotals.get(b.id)?.success ?? 0) + (barrierTotals.get(b.id)?.failure ?? 0));
            values.push(challenged / totalReports);
        }
        // Fixed 10-pp bins from 0 to 100 % — Demand Coverage is bounded in [0, 1].
        return buildDistribution(values, 0.1, 10);
    }, [topViewMode, windowSystemMetrics.challengedReports, systemPassThrough.total, topLevelOnly, allBarrierNodes, topLevelIds, barrierTrendStats, barrierTotals]);

    // System-level Effectiveness Rate distribution. Per-barrier rate = success /
    // (success + failure), so unchallenged barriers are excluded (their rate is
    // undefined, not 0 %). Healthier = clustered right (90–100 %); concerning =
    // long LEFT tail or a whole-curve shift leftward.
    const effRateDistData = useMemo((): (Distribution & {belowCritical: number; belowWarning: number}) | null => {
        const inWindow = topViewMode === 'window';
        const filtered = topLevelOnly
            ? allBarrierNodes.filter((n) => topLevelIds.includes(n.id))
            : allBarrierNodes;
        const values: number[] = [];
        for (const b of filtered) {
            if (inWindow) {
                const recent = barrierTrendStats.get(b.id);
                if (recent?.recentEffRate == null) continue;
                values.push(recent.recentEffRate);
            } else {
                const t = barrierTotals.get(b.id);
                if (!t) continue;
                const total = t.success + t.failure;
                if (total === 0) continue;
                values.push(t.success / total);
            }
        }
        // Fixed 10-pp bins from 0 to 100, anchored to the status thresholds (Critical
        // < 70 %, Warning 70–90 %, Healthy ≥ 90 %) so no bin straddles a boundary.
        const dist = buildDistribution(values, 0.1, 10);
        if (!dist) return null;
        const belowCritical = dist.values.filter((v) => v < 0.7).length;
        const belowWarning = dist.values.filter((v) => v < 0.9).length;
        return {...dist, belowCritical, belowWarning};
    }, [topViewMode, topLevelOnly, allBarrierNodes, topLevelIds, barrierTrendStats, barrierTotals]);

    // Quadrant chart: Demand Coverage (X) vs Effectiveness Rate (Y) per barrier.
    // In window mode both axes are computed from the recent-window counts; in all-time
    // mode they use the worker's cumulative `barrierTotals`. Denominator = scored
    // (challenged) reports, not all reports — see denominator rule.
    const quadrantChartData = useMemo(() => {
        const points: Array<{x: number; y: number; name: string; status: 'critical' | 'warning' | 'healthy'}> = [];
        const inWindow = topViewMode === 'window';
        const totalReports = inWindow ? windowSystemMetrics.challengedReports : systemPassThrough.total;
        if (totalReports === 0) return {points, datasets: [{data: []}]};

        const topLevelSet = new Set(topLevelIds);
        for (const barrier of allBarrierNodes) {
            if (topLevelOnly && !topLevelSet.has(barrier.id)) continue;
            let s: number;
            let f: number;
            let status: 'critical' | 'warning' | 'healthy';
            if (inWindow) {
                const stats = barrierTrendStats.get(barrier.id);
                s = stats?.recentSuccess ?? 0;
                f = stats?.recentFailure ?? 0;
                const ws = stats?.windowAwareStatus;
                if (s + f === 0 || ws == null) continue;
                // No "Min recent sample" filter here: a thin-sample barrier already lands at
                // the far left (low Demand Coverage = challenges / scored reports), so the
                // X-axis reveals its sample size — filtering would just hide a real point.
                status = ws;
            } else {
                const totals = barrierTotals.get(barrier.id);
                s = totals?.success ?? 0;
                f = totals?.failure ?? 0;
                if (s + f === 0) continue;
                const effRate = (s / (s + f)) * 100;
                status = effRate < 70 ? 'critical' : effRate < 90 ? 'warning' : 'healthy';
            }
            const challenged = s + f;
            const demandCoverage = (challenged / totalReports) * 100;
            const effectivenessRate = (s / challenged) * 100;
            const name = barrier.uniqueId || barrier.name || `ID:${barrier.id}`;
            points.push({x: demandCoverage, y: effectivenessRate, name, status});
        }

        return {
            points,
            datasets: [{
                data: points.map((p) => ({x: p.x, y: p.y})),
                backgroundColor: points.map((p) =>
                    p.status === 'critical' ? 'rgba(211, 47, 47, 0.7)'
                        : p.status === 'warning' ? 'rgba(255, 152, 0, 0.7)'
                            : 'rgba(46, 125, 50, 0.7)'
                ),
                pointRadius: 8,
                pointHoverRadius: 11,
            }],
        };
    }, [allBarrierNodes, systemPassThrough.total, barrierTotals, topLevelIds, topLevelOnly, topViewMode, windowSystemMetrics.challengedReports, barrierTrendStats]);

    const quadrantChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: false},
            tooltip: {
                callbacks: {
                    title: (items: {dataIndex: number}[]): string => {
                        const p = quadrantChartData.points[items[0]?.dataIndex];
                        return p ? p.name : '';
                    },
                    label: (item: {dataIndex: number}): string => {
                        const p = quadrantChartData.points[item.dataIndex];
                        if (!p) return '';
                        return `Demand: ${p.x.toFixed(1)}%  |  Effectiveness: ${p.y.toFixed(1)}%`;
                    },
                },
            },
        },
        scales: {
            x: {
                title: {display: true, text: 'Demand Coverage (%)'},
                min: 0,
                max: Math.max(100, ...quadrantChartData.points.map((p) => p.x) , 1),
                ticks: {callback: (v: number | string): string => `${v}%`},
            },
            y: {
                title: {display: true, text: 'Effectiveness Rate (%)'},
                min: 0,
                max: 100,
                ticks: {callback: (v: number | string): string => `${v}%`},
            },
        },
    }), [quadrantChartData]);

    /** Inline plugin: draws quadrant dividers + labels on the Demand-vs-Effectiveness quadrant chart. */
    const quadrantPlugin = useMemo(() => ({
        id: 'barrierQuadrantLines',
        afterDraw(chart: ChartJS): void {
            const xScale = chart.scales['x'];
            const yScale = chart.scales['y'];
            if (!xScale || !yScale) return;
            const ctx = chart.ctx;
            const area = chart.chartArea;

            // Horizontal line at 70% effectiveness
            const y70 = yScale.getPixelForValue(70);
            // Horizontal line at 90% effectiveness
            const y90 = yScale.getPixelForValue(90);

            ctx.save();
            // 70% line (critical threshold)
            ctx.setLineDash([6, 4]);
            ctx.lineWidth = 1;
            ctx.strokeStyle = 'rgba(211, 47, 47, 0.4)';
            ctx.beginPath();
            ctx.moveTo(area.left, y70);
            ctx.lineTo(area.right, y70);
            ctx.stroke();

            // 90% line (healthy threshold)
            ctx.strokeStyle = 'rgba(46, 125, 50, 0.4)';
            ctx.beginPath();
            ctx.moveTo(area.left, y90);
            ctx.lineTo(area.right, y90);
            ctx.stroke();

            // Labels
            ctx.setLineDash([]);
            ctx.font = '10px sans-serif';
            ctx.textAlign = 'left';

            // Zone labels on the right edge
            const labelX = area.right - 4;
            ctx.textAlign = 'right';

            ctx.fillStyle = 'rgba(211, 47, 47, 0.6)';
            ctx.fillText('Critical < 70%', labelX, y70 + 12);

            ctx.fillStyle = 'rgba(255, 152, 0, 0.6)';
            const yMidWarn = (y70 + y90) / 2;
            ctx.fillText('Warning', labelX, yMidWarn + 4);

            ctx.fillStyle = 'rgba(46, 125, 50, 0.6)';
            ctx.fillText('Healthy ≥ 90%', labelX, y90 - 5);

            ctx.restore();
        },
    }), []);

    // Per-report challenged-vs-failed scatter (Health map): X = barriers challenged in the
    // report, Y = barriers failed. Grouped by (challenged, failed) position with the report
    // list, sized by count. Window-aware (recent-window reports in window mode) and respects
    // the Source filter + Top-level-only. Gated to the Health map view for perf — it's an
    // O(reports × mappings) pass.
    const defenseDepthGroups = useMemo((): Array<{x: number; y: number; reports: string[]}> => {
        if (activeSubTab !== 'healthChart') return [];
        const considered = topLevelOnly ? new Set(topLevelIds) : allBarrierIdSet;
        const useWindow = topViewMode === 'window' && barrierTrendBoundary != null;
        const recentStart = barrierTrendBoundary?.recentStart ?? 0;
        const end = barrierTrendBoundary?.end ?? 0;
        const groups = new Map<string, {x: number; y: number; reports: string[]}>();
        for (const r of sourceFilteredReports) {
            if (useWindow) {
                if (!r.dateTime) continue;
                const t = new Date(r.dateTime).getTime();
                if (isNaN(t) || t < recentStart || t > end) continue;
            }
            let challenged = 0;
            let failed = 0;
            for (const nm of r.nodeMappings) {
                if (!considered.has(nm.nodeId)) continue;
                if (nm.positiveInteraction === true) challenged++;
                else if (nm.positiveInteraction === false) { challenged++; failed++; }
            }
            if (challenged === 0) continue; // only challenged reports appear
            const key = `${challenged}-${failed}`;
            let g = groups.get(key);
            if (!g) { g = {x: challenged, y: failed, reports: []}; groups.set(key, g); }
            g.reports.push(r.uniqueId || `Report ${r.reportId}`);
        }
        return Array.from(groups.values());
    }, [activeSubTab, topLevelOnly, topLevelIds, allBarrierIdSet, sourceFilteredReports, topViewMode, barrierTrendBoundary]);

    const defenseDepthData = useMemo(() => {
        const minR = 4;
        const maxR = 16;
        const maxCount = Math.max(1, ...defenseDepthGroups.map((g) => g.reports.length));
        return {
            datasets: [{
                data: defenseDepthGroups.map((g) => ({x: g.x, y: g.y})),
                backgroundColor: defenseDepthGroups.map((g) =>
                    g.y === 0 ? 'rgba(46, 125, 50, 0.7)'        // every challenged barrier held
                        : g.y === g.x ? 'rgba(211, 47, 47, 0.7)' // every challenged barrier failed (pass-through)
                            : 'rgba(245, 124, 0, 0.7)'),          // partial defense
                pointRadius: defenseDepthGroups.map((g) =>
                    minR + (g.reports.length - 1) / (maxCount - 1 || 1) * (maxR - minR)),
                pointHoverRadius: defenseDepthGroups.map((g) =>
                    minR + 2 + (g.reports.length - 1) / (maxCount - 1 || 1) * (maxR - minR)),
            }],
        };
    }, [defenseDepthGroups]);

    // Average barriers held per challenged report — the mean vertical gap below the
    // Y = X (system pass-through) diagonal. Pass-through reports sit on the diagonal
    // and contribute 0, so they pull the average down. Denominator is every challenged
    // report on the chart (i.e. every dot), matching the chart's filter scope.
    const avgBarriersHeld = useMemo((): {value: number | null; reports: number} => {
        let held = 0;
        let reports = 0;
        for (const g of defenseDepthGroups) {
            held += (g.x - g.y) * g.reports.length;
            reports += g.reports.length;
        }
        return {value: reports > 0 ? held / reports : null, reports};
    }, [defenseDepthGroups]);

    const defenseDepthOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: false},
            tooltip: {
                callbacks: {
                    title: (items: {dataIndex: number}[]): string | string[] => {
                        const g = defenseDepthGroups[items[0]?.dataIndex];
                        if (!g) return '';
                        const lines: string[] = [];
                        lines.push(`${g.x} challenged, ${g.y} failed — ${g.reports.length} report${g.reports.length !== 1 ? 's' : ''}`);
                        g.reports.slice(0, 8).forEach((id) => lines.push(`  ${id}`));
                        if (g.reports.length > 8) lines.push(`  +${g.reports.length - 8} more`);
                        return lines;
                    },
                    label: (): string => '',
                },
            },
        },
        interaction: {mode: 'nearest' as const, intersect: true},
        scales: {
            x: {title: {display: true, text: 'Barriers Challenged'}, beginAtZero: true, ticks: {precision: 0}},
            y: {title: {display: true, text: 'Barriers Failed'}, beginAtZero: true, ticks: {precision: 0}},
        },
    }), [defenseDepthGroups]);

    // Monthly System Pass-Through Rate, to trend the Challenged-vs-Failed scatter over
    // time. Pass-through = a challenged report where every challenged barrier failed
    // (a dot on the Y = X diagonal). Uses the same Top-level-only barrier set as the
    // scatter and the Source filter, but spans the full date range regardless of the
    // recent trend window — the point is to see the whole history.
    const passThroughTimeSeries = useMemo((): {labels: string[]; keys: string[]; rates: Array<number | null>; counts: number[]} => {
        if (activeSubTab !== 'healthChart') return {labels: [], keys: [], rates: [], counts: []};
        const considered = topLevelOnly ? new Set(topLevelIds) : allBarrierIdSet;
        const buckets = new Map<string, {challenged: number; allFailed: number}>();
        for (const r of sourceFilteredReports) {
            if (!r.dateTime) continue;
            const d = new Date(r.dateTime);
            if (isNaN(d.getTime())) continue;
            let challenged = 0;
            let failed = 0;
            for (const nm of r.nodeMappings) {
                if (!considered.has(nm.nodeId)) continue;
                if (nm.positiveInteraction === true) challenged++;
                else if (nm.positiveInteraction === false) { challenged++; failed++; }
            }
            if (challenged === 0) continue;
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            let b = buckets.get(key);
            if (!b) { b = {challenged: 0, allFailed: 0}; buckets.set(key, b); }
            b.challenged++;
            if (failed === challenged) b.allFailed++;
        }
        const labels: string[] = [];
        const keys: string[] = [];
        const rates: Array<number | null> = [];
        const counts: number[] = [];
        // "Hide out-of-window" clips to in-window months (>= the prior-window start).
        // Only meaningful for a fixed-month window — not Mid-point or All Time.
        const hideActive = hidePtoOutOfWindow && typeof barrierTrendWindow === 'number' && !!barrierTrendBoundary;
        const startKey = hideActive && barrierTrendBoundary
            ? `${new Date(barrierTrendBoundary.start).getFullYear()}-${String(new Date(barrierTrendBoundary.start).getMonth() + 1).padStart(2, '0')}`
            : null;
        // Newest-first ordering (matches the BE Timeline chart) so the shared
        // recent/prior divider plugin and the newest-on-left reading both work
        // off a normal axis — no reversed x-scale needed.
        for (const key of Array.from(buckets.keys()).sort().reverse()) {
            if (startKey && key < startKey) continue;
            const b = buckets.get(key);
            if (!b) continue;
            const [y, m] = key.split('-');
            labels.push(new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', {month: 'short', year: '2-digit'}));
            keys.push(key);
            rates.push(b.challenged > 0 ? b.allFailed / b.challenged : null);
            counts.push(b.challenged);
        }
        return {labels, keys, rates, counts};
    }, [activeSubTab, topLevelOnly, topLevelIds, allBarrierIdSet, sourceFilteredReports, hidePtoOutOfWindow, barrierTrendWindow, barrierTrendBoundary]);

    // Recent-window divider for the pass-through trend, mirroring the BE Timeline
    // overlay. Data is newest-first, so `splitCatIndex` is the first month from the
    // left that falls before the recent cutoff (i.e. the first prior month). -1 (no
    // divider) in All-Time mode, when there's no boundary, or when every visible
    // month sits on the same side of the boundary.
    const passThroughSplit = useMemo((): {splitCatIndex: number; bandEndCatIndex: number; showBand: boolean; pillText: string} => {
        if (topViewMode !== 'window' || !barrierTrendBoundary || passThroughTimeSeries.keys.length === 0) {
            return {splitCatIndex: -1, bandEndCatIndex: -1, showBand: false, pillText: ''};
        }
        const monthKey = (ts: number): string => {
            const d = new Date(ts);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        };
        const recentKey = monthKey(barrierTrendBoundary.recentStart);
        const startKey = monthKey(barrierTrendBoundary.start);
        // Newest-first: first month from the left that falls before each cutoff.
        // splitCatIndex = recent|prior boundary; bandEndCatIndex = window|out-of-window.
        let splitIdx = -1;
        let bandEndIdx = -1;
        for (let i = 0; i < passThroughTimeSeries.keys.length; i++) {
            if (splitIdx === -1 && passThroughTimeSeries.keys[i] < recentKey) splitIdx = i;
            if (bandEndIdx === -1 && passThroughTimeSeries.keys[i] < startKey) bandEndIdx = i;
        }
        const windowLabel = barrierTrendWindow === 'midpoint' ? 'Mid-point' : barrierTrendWindow === 'allTime' ? 'All time' : `${barrierTrendWindow} mo`;
        const pillText = `${windowLabel} · ${fmtSplitDate(barrierTrendBoundary.recentStart)}`;
        // Suppress the band in Mid-point mode (it spans the whole range) and when
        // out-of-window months are already hidden (band would be redundant) — mirrors
        // the RC Temporal Distribution convention.
        const showBand = barrierTrendWindow !== 'midpoint' && !(hidePtoOutOfWindow && typeof barrierTrendWindow === 'number');
        return {splitCatIndex: splitIdx, bandEndCatIndex: bandEndIdx, showBand, pillText};
    }, [topViewMode, barrierTrendBoundary, passThroughTimeSeries.keys, barrierTrendWindow, hidePtoOutOfWindow]);

    const passThroughSparklineData = useMemo(() => ({
        labels: passThroughTimeSeries.labels,
        datasets: [{
            data: passThroughTimeSeries.rates.map((r) => (r == null ? null : r * 100)),
            borderColor: '#d32f2f',
            backgroundColor: '#d32f2f',
            borderWidth: 2,
            pointRadius: passThroughTimeSeries.labels.length > 24 ? 0 : 2,
            pointHoverRadius: 4,
            tension: 0.3,
            spanGaps: true,
            fill: false,
        }],
    }), [passThroughTimeSeries]);

    const passThroughSparklineOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        // Reserve a strip above the plot for the recent/prior pill so it sits outside
        // chartArea, otherwise it covers the topmost data point.
        layout: {padding: {top: passThroughSplit.splitCatIndex > 0 ? 22 : 0}},
        plugins: {
            legend: {display: false},
            trendWindowOverlay: {
                splitCatIndex: passThroughSplit.splitCatIndex,
                pillText: passThroughSplit.pillText,
                bandEndCatIndex: passThroughSplit.bandEndCatIndex,
                showBand: passThroughSplit.showBand,
            },
            tooltip: {
                callbacks: {
                    title: (items: {label: string}[]): string => items[0]?.label ?? '',
                    label: (item: {dataIndex: number}): string => {
                        const i = item.dataIndex;
                        const rate = passThroughTimeSeries.rates[i];
                        const n = passThroughTimeSeries.counts[i];
                        return rate == null
                            ? 'No challenged reports'
                            : `${Math.round(rate * 100)}% pass-through (${n} challenged report${n !== 1 ? 's' : ''})`;
                    },
                },
            },
        },
        scales: {
            x: {grid: {display: false}, ticks: {maxRotation: 0, autoSkip: true, font: {size: 9}}},
            y: {beginAtZero: true, max: 100, grid: {display: true}, ticks: {maxTicksLimit: 4, font: {size: 9}, callback: (v: number | string): string => `${v}%`}},
        },
    }), [passThroughTimeSeries, passThroughSplit]);

    // Shades the trend window with the same translucent blue band + dashed edge as the
    // RC Temporal Distribution, for a consistent "this is the comparison window" cue.
    // Data is newest-first, so the window runs from the left edge (now) out to the
    // `bandEndCatIndex` boundary on the right; the amber divider (drawn by the shared
    // overlay plugin) splits recent from prior inside it.
    const passThroughBandPlugin = useMemo(() => ({
        id: 'passThroughTrendBand',
        beforeDatasetsDraw(chart: ChartJS<'line'>): void {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const channel = (chart.options.plugins as any)?.trendWindowOverlay;
            if (!channel?.showBand) return;
            const x = chart.scales['x'];
            if (!x) return;
            const {chartArea, ctx} = chart;
            const bandEndIdx = channel.bandEndCatIndex as number | undefined;
            // Right (older) edge: boundary between in-window and out-of-window months.
            // -1 means every visible month is in-window, so the band runs to the plot edge.
            let right = chartArea.right;
            if (bandEndIdx != null && bandEndIdx > 0) {
                const px = x.getPixelForValue(bandEndIdx - 0.5);
                if (px > chartArea.left && px < chartArea.right) right = px;
            }
            const left = chartArea.left;
            ctx.save();
            // Only the older boundary gets a dashed edge; the newer edge coincides with
            // the plot's left edge ("now") and is skipped by the plot-boundary test.
            drawTrendWindowBand(ctx, {left, right, top: chartArea.top, bottom: chartArea.bottom}, [right], chartArea.left, chartArea.right);
            ctx.restore();
        },
    }), []);

    // Worker-derived aggregates (null until the first compute returns).
    const sourceComparisonData = analysis?.sourceComparison ?? EMPTY_SOURCE_COMPARISON;
    const timeBuckets = analysis?.timeBuckets ?? EMPTY_TIME_BUCKETS;
    const timelineCellLookup = analysis?.timelineCellLookup ?? EMPTY_TIMELINE_LOOKUP;
    const barrierMtbf = analysis?.barrierMtbf ?? EMPTY_BARRIER_MTBF;
    const barrierTrends = analysis?.barrierTrends ?? EMPTY_BARRIER_TRENDS;

    // Recent-window per-(source, barrier) success/failure + per-source report counts,
    // so the Source Comparison view/export can be window-aware like every other view.
    // The worker's `sourceComparison` is all-time and has no recent/prior split, so we
    // recompute over `allMappedReports` (all sources, bypassing the source filter — same
    // population the all-time view uses) restricted to the recent window. Uses the same
    // boundary convention as barrierTrendStats (t >= recentStart && t <= end). Null when
    // there's no window (all-time mode), in which case the all-time data is used.
    const windowSourceComparison = useMemo((): {
        data: Map<string, Map<number, {success: number; failure: number; total: number}>>;
        reportCounts: Map<string, number>;
    } | null => {
        // Only needed by the Source Comparison view/export — skip the O(reports×mappings)
        // pass on every other view.
        if (!barrierTrendBoundary || viewMode !== 'sourceComparison') return null;
        const {recentStart, end} = barrierTrendBoundary;
        const data = new Map<string, Map<number, {success: number; failure: number; total: number}>>();
        const reportCounts = new Map<string, number>();
        for (const r of allMappedReports) {
            if (!r.dateTime) continue;
            const t = new Date(r.dateTime).getTime();
            if (isNaN(t) || t < recentStart || t > end) continue;
            const src = r.sourceName || '';
            reportCounts.set(src, (reportCounts.get(src) ?? 0) + 1);
            let srcMap = data.get(src);
            if (!srcMap) { srcMap = new Map(); data.set(src, srcMap); }
            for (const nm of r.nodeMappings) {
                const isSuccess = nm.positiveInteraction === true;
                const isFailure = nm.positiveInteraction === false;
                if (!isSuccess && !isFailure) continue;
                let m = srcMap.get(nm.nodeId);
                if (!m) { m = {success: 0, failure: 0, total: 0}; srcMap.set(nm.nodeId, m); }
                if (isSuccess) m.success++; else m.failure++;
                m.total++;
            }
        }
        return {data, reportCounts};
    }, [barrierTrendBoundary, allMappedReports, viewMode]);

    // Window-aware selectors the Source Comparison view + export read: recent-window
    // metrics in window mode, the worker's all-time aggregates otherwise.
    const sourceComparisonForView = topViewMode === 'window' && windowSourceComparison
        ? windowSourceComparison.data
        : sourceComparisonData;
    const sourceReportCountsForView = topViewMode === 'window' && windowSourceComparison
        ? windowSourceComparison.reportCounts
        : sourceReportCounts;

    // Build chart data for selected barriers
    const chartData = useMemo(() => {
        if (chartSelectedBarriers.size === 0 || timeBuckets.length === 0) return null;

        // Window-aware: in window mode restrict the chart to buckets inside the
        // trend-window band [start, end], so the success/failure lines line up with
        // every other window-scoped surface (heatmap, KPIs, Top N). All-time mode
        // shows the full bucket sequence. Descending order (newest on the left,
        // oldest on the right) so the chart aligns with the heatmap matrix above
        // it — users don't have to flip direction when looking between them.
        const filtered = topViewMode === 'window' && bandBucketCutoffKeys
            ? timeBuckets.filter((b) => b.key >= bandBucketCutoffKeys.band)
            : timeBuckets;
        const chartBuckets = [...filtered].reverse();
        if (chartBuckets.length === 0) return null;

        const labels = chartBuckets.map((b) => b.label);
        const datasets: ChartDataset<'line'>[] = [];
        let colorIdx = 0;

        chartSelectedBarriers.forEach((barrierId) => {
            const node = nodeById.get(barrierId);
            const color = CHART_COLORS[colorIdx % CHART_COLORS.length];
            colorIdx++;

            const successData = chartBuckets.map((bucket) => {
                const cell = timelineCellLookup.get(`${barrierId}-${bucket.key}`);
                return cell?.success ?? 0;
            });
            const failureData = chartBuckets.map((bucket) => {
                const cell = timelineCellLookup.get(`${barrierId}-${bucket.key}`);
                return cell?.failure ?? 0;
            });

            const id = node?.uniqueId || node?.name || `Barrier ${barrierId}`;
            if (chartShowSuccess) {
                datasets.push({
                    label: `${id} (S)`,
                    data: successData,
                    borderColor: color,
                    borderDash: [],
                    pointRadius: 3,
                    tension: 0.3,
                    fill: false,
                });
            }
            if (chartShowFailure) {
                datasets.push({
                    label: `${id} (F)`,
                    data: failureData,
                    borderColor: color,
                    borderDash: [5, 3],
                    pointRadius: 3,
                    tension: 0.3,
                    fill: false,
                });
            }
        });

        return {labels, datasets};
    }, [chartSelectedBarriers, timeBuckets, timelineCellLookup, nodeById, chartShowSuccess, chartShowFailure, topViewMode, bandBucketCutoffKeys]);

    // Recent/prior split position for the timeline success/failure chart, computed
    // against the same band-filtered + REVERSED bucket list chartData uses. In the
    // reversed (newest-first) ordering, the boundary is the first bucket whose key
    // drops below the recent cutoff (the first prior-half bucket from the left).
    // -1 when no split should be drawn (all-time, missing window, or every visible
    // bucket sits on the same side of the boundary).
    const chartSplit = useMemo((): {splitCatIndex: number; pillText: string} => {
        if (topViewMode !== 'window' || !bandBucketCutoffKeys || timeBuckets.length === 0) {
            return {splitCatIndex: -1, pillText: ''};
        }
        const reversed = [...timeBuckets.filter((b) => b.key >= bandBucketCutoffKeys.band)].reverse();
        let splitIdx = -1;
        for (let i = 0; i < reversed.length; i++) {
            if (reversed[i].key < bandBucketCutoffKeys.recent) { splitIdx = i; break; }
        }
        const windowLabel = barrierTrendWindow === 'midpoint' ? 'Mid-point' : `${barrierTrendWindow} mo`;
        const dateLabel = barrierTrendBoundary ? fmtSplitDate(barrierTrendBoundary.recentStart) : '';
        const pillText = dateLabel ? `${windowLabel} · ${dateLabel}` : windowLabel;
        return {splitCatIndex: splitIdx, pillText};
    }, [topViewMode, bandBucketCutoffKeys, timeBuckets, barrierTrendWindow, barrierTrendBoundary]);

    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        // Reserve a strip above the plot for the recent/prior pill so it sits
        // outside chartArea — otherwise the pill covers the topmost data point.
        layout: {padding: {top: chartSplit.splitCatIndex > 0 ? 22 : 0}},
        plugins: {
            legend: {
                display: false,
            },
            title: {display: false},
            // Channel for the timelineTrendWindowOverlay plugin defined below.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            trendWindowOverlay: {
                splitCatIndex: chartSplit.splitCatIndex,
                pillText: chartSplit.pillText,
            } as any,
        },
        scales: {
            x: {
                ticks: {font: {size: 9}, maxRotation: 45, minRotation: 45},
            },
            y: {
                beginAtZero: true,
                title: {display: true, text: 'Count', font: {size: 10}},
                ticks: {stepSize: 1, font: {size: 9}},
            },
        },
        animation: {duration: 300},
        interaction: {intersect: false, mode: 'index' as const},
    }), [chartSplit]);

    // Amber divider + window-label pill at the recent/prior split. Mirrors the
    // trend-window overlay used in the Report Clustering dotplot and the Barrier
    // Effectiveness heatmap so all three reads as one consistent visual language.
    const chartTimelineOverlayPlugin = useMemo(() => ({
        id: 'timelineTrendWindowOverlay',
        afterDatasetsDraw(chart: ChartJS<'line'>): void {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const channel = (chart.options.plugins as any)?.trendWindowOverlay;
            const splitIdx = channel?.splitCatIndex as number | undefined;
            const pillText = channel?.pillText as string | undefined;
            if (splitIdx == null || splitIdx <= 0) return;
            const x = chart.scales['x'];
            if (!x) return;
            const {chartArea, ctx} = chart;
            // Position between (splitIdx - 1) and splitIdx — the boundary between
            // the last prior bucket and the first recent bucket.
            const xPos = x.getPixelForValue(splitIdx - 0.5);
            if (xPos <= chartArea.left || xPos >= chartArea.right) return;
            ctx.save();
            drawTrendWindowDivider(ctx, xPos, chartArea.top, chartArea.bottom);
            if (pillText) {
                drawTrendWindowPill(ctx, xPos, chartArea.top, chartArea.left, chartArea.right, pillText);
            }
            ctx.restore();
        },
    }), []);

    // Toggle expand/collapse
    const toggleExpand = (nodeId: number): void => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    };

    const toggleChartBarrier = (nodeId: number): void => {
        setChartSelectedBarriers((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) next.delete(nodeId);
            else next.add(nodeId);
            return next;
        });
    };

    const chartHeightRef = useRef(chartHeight);
    chartHeightRef.current = chartHeight;

    const handleChartResizeStart = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const startY = e.clientY;
        const startHeight = chartHeightRef.current;

        // Find the closest scrollable ancestor and freeze it during resize
        let scrollParent: HTMLElement | null = e.currentTarget as HTMLElement;
        while (scrollParent) {
            const style = window.getComputedStyle(scrollParent);
            if (style.overflow === 'auto' || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll') break;
            scrollParent = scrollParent.parentElement;
        }
        const savedOverflow = scrollParent ? scrollParent.style.overflow : '';
        if (scrollParent) scrollParent.style.overflow = 'hidden';

        const onMouseMove = (moveEvent: MouseEvent): void => {
            moveEvent.preventDefault();
            const newHeight = Math.max(100, Math.min(500, startHeight + moveEvent.clientY - startY));
            setChartHeight(newHeight);
        };

        const onMouseUp = (): void => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            if (scrollParent) scrollParent.style.overflow = savedOverflow;
        };

        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, []);

    // Compute sticky column width dynamically based on visible columns
    const stickyColumnWidth = useMemo((): number => {
        let width = 24 + 4; // expand button + left padding
        if (viewMode === 'timeline') width += 24 + 8; // chart icon + gap
        COLUMN_CONFIG.forEach((col) => {
            if (visibleColumns.has(col.key) && (viewMode === 'timeline' || !TIMELINE_ONLY_COLUMNS.has(col.key))) {
                width += col.width + 8; // column width + gap
            }
        });
        return Math.max(width, 52);
    }, [visibleColumns, viewMode]);

    const toggleColumn = (key: BarrierColumn): void => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleExportCsv = (): void => {
        let headers: string[];
        let rows: string[][];

        if (viewMode === 'calendar') {
            // Calendar view shows per-day demand for the visible barriers — export one
            // row per day (the data behind the grid), not the effectiveness matrix.
            headers = ['Date', 'Reports (demand)', 'Barrier Successes', 'Barrier Failures'];
            rows = [...dailyDemand.entries()]
                .sort((a, b) => a[0] - b[0])
                .map(([dayTs, v]) => {
                    const date = new Date(dayTs).toLocaleDateString('en-CA'); // YYYY-MM-DD, spreadsheet-sortable
                    return [date, String(v.demand), String(v.success), String(v.failure)];
                });
        } else if (viewMode === 'failurePattern') {
            // Failure pattern view plots each barrier's failure events over time — export
            // one row per barrier with its failure count and recent/prior window split.
            headers = ['Barrier ID', 'Barrier Name', 'Failures', 'Recent', 'Prior', 'Trend'];
            rows = failurePatternEntities.map((e) => [
                e.label,
                e.title,
                String(e.count),
                String(e.trend.recent),
                String(e.trend.prior),
                e.trend.trend ?? '',
            ]);
        } else if (viewMode === 'sourceComparison') {
            // Mirror the on-screen table: honor column visibility for ID/Name and for
            // the per-source metric columns (same keys/order as the rendered view).
            const allSrcMetricCols: {key: BarrierColumn; label: string}[] = [
                {key: 'success', label: 'S'},
                {key: 'failure', label: 'F'},
                {key: 'demand', label: 'Demand'},
                {key: 'effectiveness', label: 'Effectiveness'},
                {key: 'failureExposure', label: 'F.Exposure'},
            ];
            const srcMetricCols = allSrcMetricCols.filter((c) => visibleColumns.has(c.key));

            headers = [];
            if (visibleColumns.has('id')) headers.push('Barrier ID');
            if (visibleColumns.has('name')) headers.push('Barrier Name');
            availableSources.forEach((source) => {
                srcMetricCols.forEach((c) => headers.push(`${source} ${c.label}`));
            });

            rows = visibleRows.map((row) => {
                const cells: string[] = [];
                if (visibleColumns.has('id')) cells.push(row.node.uniqueId || '');
                if (visibleColumns.has('name')) cells.push(row.node.name || '');
                availableSources.forEach((source) => {
                    const metrics = sourceComparisonForView.get(source)?.get(row.node.id);
                    const s = metrics?.success ?? 0;
                    const f = metrics?.failure ?? 0;
                    const total = s + f;
                    const sourceReportCount = sourceReportCountsForView.get(source) ?? 0;
                    const demandPct = sourceReportCount > 0 ? `${Math.round((total / sourceReportCount) * 100)}%` : '';
                    const effPct = total > 0 ? `${Math.round((s / total) * 100)}%` : '';
                    const demandRatio = sourceReportCount > 0 ? total / sourceReportCount : 0;
                    const effRatio = total > 0 ? s / total : 0;
                    const fExpPct = total > 0 && sourceReportCount > 0 ? `${Math.round(demandRatio * (1 - effRatio) * 100)}%` : '';
                    const valueByKey: Record<BarrierColumn, string> = {
                        id: '', name: '', mtbf: '', effectivenessTrend: '', demandTrend: '',
                        success: String(s), failure: String(f), demand: demandPct, effectiveness: effPct, failureExposure: fExpPct,
                    };
                    srcMetricCols.forEach((c) => cells.push(valueByKey[c.key]));
                });
                return cells;
            });
        } else {
            const inWindowMode = topViewMode === 'window';
            const exportRows = tableRows;
            const exportReports = tableMappedReports;
            const exportBuckets = tableTimeBuckets;
            // Demand Coverage / F.Exp denominator = scored (challenged) reports, not all
            // reports — matches the on-screen table and the denominator rule.
            const exportDenom = inWindowMode ? windowSystemMetrics.challengedReports : systemPassThrough.total;
            // Trend columns are now window-aware: in all-time mode the slopes are fit
            // across the full history (from the worker); in window mode they're fit over
            // recent-window months only (from `barrierTrendStats`).
            // MTBF stays window-aware (recent + prior) when a trend window is selected.
            // Trend slopes are exported as signed numbers (percentage points per period)
            // so the columns stay sortable/analyzable in a spreadsheet — the unit lives
            // in the header rather than as an arrow glyph in each cell. Window slopes are
            // always monthly; all-time slopes follow the granularity selection.
            const allTimeUnit = timeGranularity === 'monthly' ? 'mo' : timeGranularity === 'quarterly' ? 'q' : 'yr';
            // Concrete window for the trend-column headers so the CSV says which window the
            // recent-vs-prior slope was fit over (e.g. "3 mo" or "mid-point"), not just "window".
            const windowLabel = barrierTrendWindow === 'midpoint' ? 'mid-point' : `${barrierTrendWindow} mo`;
            // Attribute columns mirror the on-screen matrix: honor column visibility, and
            // MTBF/trend columns only exist in timeline view (TIMELINE_ONLY_COLUMNS). The
            // report / time-bucket matrix columns always follow. Trend slopes export as
            // signed pp/period numbers (unit in the header); MTBF stays window-aware
            // (recent + prior) when a trend window is selected.
            const showCol = (key: BarrierColumn): boolean =>
                visibleColumns.has(key) && (viewMode === 'timeline' || !TIMELINE_ONLY_COLUMNS.has(key));

            headers = [];
            if (showCol('id')) headers.push('Barrier ID');
            if (showCol('name')) headers.push('Barrier Name');
            if (showCol('success')) headers.push('Success');
            if (showCol('failure')) headers.push('Failure');
            if (showCol('demand')) headers.push('Demand Coverage');
            if (showCol('effectiveness')) headers.push('Effectiveness Rate');
            if (showCol('failureExposure')) headers.push('Failure Exposure');
            if (showCol('mtbf')) {
                if (inWindowMode) headers.push('Recent MTBF (days)', 'Prior MTBF (days)');
                else headers.push('MTBF (days)');
            }
            if (showCol('effectivenessTrend')) headers.push(inWindowMode ? `Effectiveness Trend (%/mo, ${windowLabel} window)` : `Effectiveness Trend (%/${allTimeUnit})`);
            if (showCol('demandTrend')) headers.push(inWindowMode ? `Demand Trend (%/mo, ${windowLabel} window)` : `Demand Trend (%/${allTimeUnit})`);

            if (viewMode === 'timeline') {
                exportBuckets.forEach((bucket) => headers.push(bucket.label));
            } else {
                exportReports.forEach((r) => headers.push(r.uniqueId || `Report ${r.reportId}`));
            }

            // Build a per-cell status lookup on demand (only for the effectiveness export).
            const exportLookup = new Map<string, 'SUCCESS' | 'FAILURE'>();
            if (viewMode !== 'timeline') {
                exportReports.forEach((report) => {
                    report.nodeMappings.forEach((nm) => {
                        if (!allBarrierIdSet.has(nm.nodeId)) return;
                        exportLookup.set(`${nm.nodeId}-${report.reportId}`, nm.positiveInteraction === true ? 'SUCCESS' : 'FAILURE');
                    });
                });
            }

            // Signed slope as percentage points per period (e.g. 5.00, -3.00), blank when none.
            const fmtTrend = (s: number | null | undefined): string =>
                s == null ? '' : (s * 100).toFixed(2);

            rows = exportRows.map((row) => {
                const allTimeTotals = barrierTotals.get(row.node.id) || {success: 0, failure: 0};
                const periodStats = barrierTrendStats.get(row.node.id);
                const totals = inWindowMode
                    ? {success: periodStats?.recentSuccess ?? 0, failure: periodStats?.recentFailure ?? 0}
                    : allTimeTotals;
                const challenged = totals.success + totals.failure;
                const demand = exportDenom > 0 ? `${Math.round((challenged / exportDenom) * 100)}%` : '';
                const effectiveness = challenged > 0 ? `${Math.round((totals.success / challenged) * 100)}%` : '';
                const demandRatio = exportDenom > 0 ? challenged / exportDenom : 0;
                const effectivenessRatio = challenged > 0 ? totals.success / challenged : 0;
                const failureExposure = challenged > 0 && exportDenom > 0 ? `${Math.round(demandRatio * (1 - effectivenessRatio) * 100)}%` : '';

                const cells: string[] = [];
                if (showCol('id')) cells.push(row.node.uniqueId || '');
                if (showCol('name')) cells.push(row.node.name || '');
                if (showCol('success')) cells.push(String(totals.success));
                if (showCol('failure')) cells.push(String(totals.failure));
                if (showCol('demand')) cells.push(demand);
                if (showCol('effectiveness')) cells.push(effectiveness);
                if (showCol('failureExposure')) cells.push(failureExposure);
                if (showCol('mtbf')) {
                    if (inWindowMode) {
                        const wm = barrierWindowMtbf.get(row.node.id);
                        cells.push(wm?.recent != null ? String(wm.recent) : '', wm?.prior != null ? String(wm.prior) : '');
                    } else {
                        const mtbfDays = barrierMtbf.get(row.node.id);
                        cells.push(mtbfDays != null ? String(mtbfDays) : '');
                    }
                }
                if (showCol('effectivenessTrend')) {
                    cells.push(fmtTrend(inWindowMode ? periodStats?.recentSlope : barrierTrends.get(row.node.id)?.effectivenessSlope));
                }
                if (showCol('demandTrend')) {
                    cells.push(fmtTrend(inWindowMode ? periodStats?.recentDemandSlope : barrierTrends.get(row.node.id)?.demandSlope));
                }

                if (viewMode === 'timeline') {
                    exportBuckets.forEach((bucket) => {
                        const cellData = timelineCellLookup.get(`${row.node.id}-${bucket.key}`);
                        if (cellData && (cellData.success > 0 || cellData.failure > 0)) {
                            cells.push(`${cellData.success}/${cellData.failure}`);
                        } else {
                            cells.push('');
                        }
                    });
                } else {
                    exportReports.forEach((report) => {
                        const status = exportLookup.get(`${row.node.id}-${report.reportId}`);
                        cells.push(status || '');
                    });
                }
                return cells;
            });
        }

        const viewSuffix = viewMode === 'calendar' ? 'calendar' : viewMode === 'failurePattern' ? 'failure-pattern' : viewMode === 'sourceComparison' ? 'source-comparison' : viewMode === 'timeline' ? 'timeline' : 'effectiveness';
        downloadCsvFile(`barrier-${viewSuffix}-${irpDetails?.name || 'export'}.csv`, rowsToCsv(headers, rows));
    };

    // Column indices that anchor the trend-window overlay on the canvas, expressed in
    // the new descending order (newest cell = column 0):
    //   split   — first column whose date sits in the prior half (i.e. t < recentStart).
    //             Cells [0, split] are recent; cells [split, ...] are prior or older.
    //   bandEnd — first column outside the band (t < start). Cells [0, bandEnd] are
    //             in-band; cells [bandEnd, N] are older history (dimmed).
    // Returns null outside window mode or when there's no trend boundary; the divider
    // at `split` is meaningful in BOTH "show out-of-band" and "hide out-of-band"
    // submodes. The band overlay itself is gated separately by `showTrendBand` below.
    const trendOverlayCols = useMemo((): {
        bandEnd: number;
        split: number;
        bandEndTimeFrac: number | null;
        splitTimeFrac: number | null;
    } | null => {
        if (topViewMode !== 'window' || !barrierTrendBoundary) return null;
        if (viewMode === 'effectiveness') {
            const {start, recentStart} = barrierTrendBoundary;
            let split = -1;
            let bandEnd = -1;
            // Also track the actual displayed date range so we can place the divider at
            // the visual time midpoint of the band (like the dotplot's date axis) rather
            // than at the cell-index midpoint, which skews with report density.
            let dMax = -Infinity;
            let dMin = Infinity;
            for (let i = 0; i < tableMappedReports.length; i++) {
                const r = tableMappedReports[i];
                if (!r.dateTime) continue;
                const t = new Date(r.dateTime).getTime();
                if (isNaN(t)) continue;
                if (t > dMax) dMax = t;
                if (t < dMin) dMin = t;
                if (split < 0 && t < recentStart) split = i;
                if (bandEnd < 0 && t < start) bandEnd = i;
            }
            let splitTimeFrac: number | null = null;
            let bandEndTimeFrac: number | null = null;
            if (dMax > dMin) {
                const span = dMax - dMin;
                splitTimeFrac = (dMax - recentStart) / span;
                bandEndTimeFrac = (dMax - start) / span;
            }
            return {bandEnd, split, bandEndTimeFrac, splitTimeFrac};
        }
        if (viewMode === 'timeline' && bandBucketCutoffKeys) {
            let split = -1;
            let bandEnd = -1;
            for (let i = 0; i < tableTimeBuckets.length; i++) {
                const k = tableTimeBuckets[i].key;
                if (split < 0 && k < bandBucketCutoffKeys.recent) split = i;
                if (bandEnd < 0 && k < bandBucketCutoffKeys.band) { bandEnd = i; break; }
            }
            // Buckets are already roughly time-proportional (each bucket = one period
            // of fixed duration), so cell-aligned positions are accurate enough — no
            // separate time fractions needed.
            return {bandEnd, split, bandEndTimeFrac: null, splitTimeFrac: null};
        }
        return null;
    }, [topViewMode, barrierTrendBoundary, viewMode, tableMappedReports, tableTimeBuckets, bandBucketCutoffKeys]);

    // Band overlay only renders when both halves AND out-of-band history are visible
    // (the band highlights the focus region against surrounding cells). In hide mode
    // the whole visible canvas already IS the band, so the overlay would be redundant.
    // Mid-point also suppresses it (band would cover everything).
    const showTrendBand = trendOverlayCols != null && !hideOutOfWindow && barrierTrendWindow !== 'midpoint';

    // Short label drawn inside the trend-window pill on the canvas. Empty string when
    // no pill should be drawn (no overlay).
    const splitWindowLabel = ((): string => {
        if (trendOverlayCols == null) return '';
        const windowLabel = barrierTrendWindow === 'midpoint' ? 'Mid-point' : `${barrierTrendWindow} mo`;
        const dateLabel = barrierTrendBoundary ? fmtSplitDate(barrierTrendBoundary.recentStart) : '';
        return dateLabel ? `${windowLabel} · ${dateLabel}` : windowLabel;
    })();

    // Store latest data in a ref so draw/scroll/mouse callbacks remain stable
    // and don't trigger effect/observer re-creation when hook references change.
    const dataRef = useRef({visibleRows: tableRows, mappedReports: tableMappedReports, viewMode, timeBuckets: tableTimeBuckets, timelineCellLookup, allBarrierIdSet, colorSuccess, colorFailure, trendOverlayCols, showTrendBand, splitWindowLabel});
    dataRef.current = {visibleRows: tableRows, mappedReports: tableMappedReports, viewMode, timeBuckets: tableTimeBuckets, timelineCellLookup, allBarrierIdSet, colorSuccess, colorFailure, trendOverlayCols, showTrendBand, splitWindowLabel};

    // Stable redraw function — reads data from ref, never changes identity
    const redraw = useCallback((): void => {
        const {
            visibleRows: rows,
            mappedReports: reports,
            viewMode: mode,
            timeBuckets: buckets,
            timelineCellLookup: tlLookup,
            allBarrierIdSet: barrierIds,
            colorSuccess: cSuccess,
            colorFailure: cFailure,
            trendOverlayCols: overlay,
            showTrendBand: bandOn,
            splitWindowLabel: pillText,
        } = dataRef.current;
        const scrollEl = matrixScrollRef.current;
        if (!scrollEl) return;

        const dpr = window.devicePixelRatio || 1;
        const vw = scrollEl.clientWidth;
        const vh = scrollEl.clientHeight;
        const sl = scrollEl.scrollLeft;
        const st = scrollEl.scrollTop;

        const isTimeline = mode === 'timeline';
        const cellW = isTimeline ? TIMELINE_CELL_WIDTH : CELL_WIDTH;
        const colCount = isTimeline ? buckets.length : reports.length;

        // --- Body canvas ---
        const bodyCanvas = bodyCanvasRef.current;
        if (bodyCanvas && rows.length > 0 && colCount > 0) {
            const cw = Math.ceil(vw * dpr);
            const ch = Math.ceil(vh * dpr);
            if (bodyCanvas.width !== cw || bodyCanvas.height !== ch) {
                bodyCanvas.width = cw;
                bodyCanvas.height = ch;
                bodyCanvas.style.width = `${vw}px`;
                bodyCanvas.style.height = `${vh}px`;
            }

            const ctx = bodyCanvas.getContext('2d');
            if (ctx) {
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                ctx.clearRect(0, 0, vw, vh);

                const firstCol = Math.floor(sl / cellW);
                const lastCol = Math.min(Math.ceil((sl + vw) / cellW), colCount);
                const firstRow = Math.floor(st / CELL_HEIGHT);
                const lastRow = Math.min(Math.ceil((st + vh) / CELL_HEIGHT), rows.length);

                if (isTimeline) {
                    ctx.textBaseline = 'middle';
                    ctx.textAlign = 'center';

                    for (let r = firstRow; r < lastRow; r++) {
                        const row = rows[r];
                        for (let c = firstCol; c < lastCol; c++) {
                            const bucket = buckets[c];
                            const cellData = tlLookup.get(`${row.node.id}-${bucket.key}`);
                            const x = c * cellW - sl;
                            const y = r * CELL_HEIGHT - st;
                            const w = cellW - 1;
                            const h = CELL_HEIGHT - 1;

                            if (!cellData || (cellData.success === 0 && cellData.failure === 0)) {
                                ctx.fillStyle = COLOR_NO_MAPPING;
                                ctx.fillRect(x, y, w, h);
                            } else if (cellData.failure === 0) {
                                ctx.fillStyle = cSuccess;
                                ctx.fillRect(x, y, w, h);
                            } else if (cellData.success === 0) {
                                ctx.fillStyle = cFailure;
                                ctx.fillRect(x, y, w, h);
                            } else {
                                const total = cellData.success + cellData.failure;
                                const successH = Math.round((cellData.success / total) * h);
                                ctx.fillStyle = cSuccess;
                                ctx.fillRect(x, y, w, successH);
                                ctx.fillStyle = cFailure;
                                ctx.fillRect(x, y + successH, w, h - successH);
                            }

                            if (cellData && (cellData.success > 0 || cellData.failure > 0)) {
                                ctx.fillStyle = '#fff';
                                ctx.font = '9px monospace';
                                ctx.fillText(`${cellData.success}/${cellData.failure}`, x + w / 2, y + h / 2);
                            }
                        }
                    }
                } else {
                    // Column-major: build a per-report status map from nodeMappings once
                    // per visible column (no global cellLookup), then fill each row's cell.
                    for (let c = firstCol; c < lastCol; c++) {
                        const report = reports[c];
                        const repStatus = new Map<number, 'SUCCESS' | 'FAILURE'>();
                        const mappings = report.nodeMappings;
                        for (let m = 0; m < mappings.length; m++) {
                            const nm = mappings[m];
                            if (!barrierIds.has(nm.nodeId)) continue;
                            repStatus.set(nm.nodeId, nm.positiveInteraction === true ? 'SUCCESS' : 'FAILURE');
                        }
                        const x = c * CELL_WIDTH - sl;
                        for (let r = firstRow; r < lastRow; r++) {
                            const status = repStatus.get(rows[r].node.id);

                            if (status === 'SUCCESS') {
                                ctx.fillStyle = cSuccess;
                            } else if (status === 'FAILURE') {
                                ctx.fillStyle = cFailure;
                            } else {
                                ctx.fillStyle = COLOR_NO_MAPPING;
                            }

                            ctx.fillRect(
                                x,
                                r * CELL_HEIGHT - st,
                                CELL_WIDTH - 1,
                                CELL_HEIGHT - 1
                            );
                        }
                    }
                }

                // Trend-window overlay: subtle blue band over the in-band cells on the
                // left + whitewash dim over out-of-band cells on the right + amber
                // divider at the recent/prior split inside the band. Mirrors the
                // dotplot's trendWindowOverlay focus, flipped for descending-order cells.
                if (overlay) {
                    const contentW = colCount * cellW;
                    // Time-based positions place the overlay at the visual time midpoint
                    // of the band (effectiveness view). Timeline view falls back to the
                    // cell-aligned bandEnd index since buckets are already time-proportional.
                    const bandEndContent = overlay.bandEndTimeFrac != null
                        ? overlay.bandEndTimeFrac * contentW
                        : (overlay.bandEnd >= 0 ? overlay.bandEnd * cellW : null);
                    if (bandOn && bandEndContent != null && bandEndContent > 0) {
                        const x1 = Math.min(vw, bandEndContent - sl);
                        if (x1 > 0) {
                            ctx.fillStyle = TREND_BAND_FILL;
                            ctx.fillRect(0, 0, x1, vh);
                        }
                    }
                    if (bandOn && bandEndContent != null && bandEndContent < contentW) {
                        const dimX0 = bandEndContent - sl;
                        const dimX1 = contentW - sl;
                        const x0 = Math.max(0, dimX0);
                        const x1 = Math.min(vw, dimX1);
                        if (x1 > x0) {
                            ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
                            ctx.fillRect(x0, 0, x1 - x0, vh);
                        }
                    }
                    if (overlay.split > 0 || overlay.splitTimeFrac != null) {
                        const splitContent = overlay.splitTimeFrac != null
                            ? overlay.splitTimeFrac * contentW
                            : overlay.split * cellW;
                        const lineX = splitContent - sl - 0.5;
                        if (lineX >= -2 && lineX <= vw + 2) {
                            drawTrendWindowDivider(ctx, lineX, 0, vh);
                        }
                    }
                }
            }
        }

        // --- Header canvas ---
        const headerCanvas = headerCanvasRef.current;
        if (headerCanvas && colCount > 0) {
            const hcw = Math.ceil(vw * dpr);
            const hch = Math.ceil(HEADER_HEIGHT * dpr);
            if (headerCanvas.width !== hcw || headerCanvas.height !== hch) {
                headerCanvas.width = hcw;
                headerCanvas.height = hch;
                headerCanvas.style.width = `${vw}px`;
                headerCanvas.style.height = `${HEADER_HEIGHT}px`;
            }

            const hctx = headerCanvas.getContext('2d');
            if (hctx) {
                hctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                hctx.clearRect(0, 0, vw, HEADER_HEIGHT);

                hctx.fillStyle = '#333';
                hctx.font = '10px monospace';
                hctx.textBaseline = 'middle';

                const firstCol = Math.floor(sl / cellW);
                const lastCol = Math.min(Math.ceil((sl + vw) / cellW), colCount);

                for (let c = firstCol; c < lastCol; c++) {
                    const label = isTimeline
                        ? buckets[c].label
                        : (reports[c].uniqueId || `Report ${reports[c].reportId}`);

                    hctx.save();
                    hctx.translate(c * cellW - sl + cellW / 2, HEADER_HEIGHT);
                    hctx.rotate(-Math.PI / 2);
                    hctx.fillText(label, 4, 0, HEADER_HEIGHT - 8);
                    hctx.restore();
                }

                // Trend-window divider in the header + pill label above the rotated
                // column labels. Same amber palette as the dotplot's trendWindowOverlay.
                if (overlay && (overlay.split > 0 || overlay.splitTimeFrac != null)) {
                    const contentW = colCount * cellW;
                    const splitContent = overlay.splitTimeFrac != null
                        ? overlay.splitTimeFrac * contentW
                        : overlay.split * cellW;
                    const lineX = splitContent - sl - 0.5;
                    if (lineX >= -2 && lineX <= vw + 2) {
                        drawTrendWindowDivider(hctx, lineX, 22, HEADER_HEIGHT);

                        if (pillText) {
                            hctx.save();
                            hctx.font = 'bold 10px sans-serif';
                            const tw = hctx.measureText(pillText).width;
                            const pillW = tw + 12;
                            const pillH = 16;
                            const pillY = 3;
                            // Anchor pill on divider but clamp inside the viewport so it
                            // stays visible while the user scrolls horizontally.
                            const pillX = Math.max(2, Math.min(vw - pillW - 2, lineX - pillW / 2));
                            hctx.fillStyle = '#ff6f00';
                            const r = 8;
                            hctx.beginPath();
                            hctx.moveTo(pillX + r, pillY);
                            hctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, r);
                            hctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, r);
                            hctx.arcTo(pillX, pillY + pillH, pillX, pillY, r);
                            hctx.arcTo(pillX, pillY, pillX + pillW, pillY, r);
                            hctx.closePath();
                            hctx.fill();
                            hctx.fillStyle = '#fff';
                            hctx.textBaseline = 'middle';
                            hctx.textAlign = 'center';
                            hctx.fillText(pillText, pillX + pillW / 2, pillY + pillH / 2);
                            hctx.restore();
                        }
                    }
                }
            }
        }
    }, []);

    // Synchronous redraw after every render — runs before the browser paints,
    // ensuring the canvas never shows stale content (e.g. after switching IRPs).
    useLayoutEffect(() => {
        redraw();
    });

    // When the trend overlay is visible, nudge the horizontal scroll so the split
    // divider lands inside the viewport — otherwise on a wide date range the user
    // lands at scrollLeft=0 (prior side) and the line sits off-screen right.
    // Re-runs when the split position changes (window selection, view, mode switch).
    useEffect(() => {
        if (!trendOverlayCols) return;
        const el = matrixScrollRef.current;
        if (!el) return;
        const cellW = viewMode === 'timeline' ? TIMELINE_CELL_WIDTH : CELL_WIDTH;
        // Prefer time-based position when available; matches the divider drawn on canvas.
        const splitX = trendOverlayCols.splitTimeFrac != null
            ? trendOverlayCols.splitTimeFrac * el.scrollWidth
            : (trendOverlayCols.split > 0 ? trendOverlayCols.split * cellW : -1);
        if (splitX <= 0) return;
        const vw = el.clientWidth;
        if (vw === 0) return;
        // Already visible? Leave the scroll alone.
        if (splitX >= el.scrollLeft && splitX <= el.scrollLeft + vw) return;
        // Place the divider ~30% from the left so most of the viewport shows recent data.
        el.scrollLeft = Math.max(0, Math.min(el.scrollWidth - vw, splitX - vw * 0.3));
    }, [trendOverlayCols, viewMode]);

    // Stable resize observer — created once, never recreated
    useEffect(() => {
        const el = matrixScrollRef.current;
        if (!el) return undefined;

        let rafId = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(redraw);
        });
        observer.observe(el);

        return (): void => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [redraw]);

    // Stable scroll handler — syncs barrier column and redraws
    const handleMatrixScroll = useCallback((): void => {
        const matrixEl = matrixScrollRef.current;
        if (!matrixEl) return;

        if (barrierColumnRef.current) {
            barrierColumnRef.current.scrollTop = matrixEl.scrollTop;
        }

        redraw();
    }, [redraw]);

    // Overview mode: render the entire barrier × report heatmap scaled to fit a single
    // canvas (no labels). When report-columns go sub-pixel, each pixel-column aggregates
    // the reports it covers and is colored by their failure proportion.
    const drawOverview = useCallback((): void => {
        const container = overviewContainerRef.current;
        const canvas = overviewCanvasRef.current;
        if (!container || !canvas) return;

        const {visibleRows: rows, mappedReports: reports, allBarrierIdSet: barrierIds, colorSuccess: cSuccess, colorFailure: cFailure, trendOverlayCols: overlay, showTrendBand: bandOn, splitWindowLabel: pillText} = dataRef.current;
        const numRows = rows.length;
        const numCols = reports.length;
        const successRgb = hexToRgb(cSuccess);
        const failureRgb = hexToRgb(cFailure);

        const dpr = window.devicePixelRatio || 1;
        const W = container.clientWidth;
        const H = container.clientHeight;
        if (W === 0 || H === 0 || numRows === 0 || numCols === 0) return;

        canvas.width = Math.ceil(W * dpr);
        canvas.height = Math.ceil(H * dpr);
        canvas.style.width = `${W}px`;
        canvas.style.height = `${H}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, W, H);

        // Time-based positions for the overlay so the divider lands at the visual middle
        // of the trend window (band) regardless of cell density — matches the dotplot's
        // date-axis behavior. Falls back to cell-based positioning when dates are
        // unavailable (timeline view, missing report dates).
        const splitXTime: number | null = overlay?.splitTimeFrac != null ? overlay.splitTimeFrac * W : null;
        const bandEndXTime: number | null = overlay?.bandEndTimeFrac != null ? overlay.bandEndTimeFrac * W : null;

        // Reserve a thin strip at the top for the trend-window pill, but only when a
        // pill is actually going to be drawn. Keeps the heatmap area unchanged when
        // there is no overlay (all-time, hide submode, source comparison, etc.).
        const hasSplit = (splitXTime != null && splitXTime > 0 && splitXTime < W)
            || (overlay != null && overlay.split > 0 && overlay.split < numCols);
        const TOP_PAD = hasSplit && pillText ? 24 : 0;
        const heatH = Math.max(1, H - TOP_PAD);
        const cellH = heatH / numRows;
        const rowH = Math.max(1, Math.ceil(cellH));
        const pxW = Math.floor(W);

        for (let px = 0; px < pxW; px++) {
            const cStart = Math.floor((px / W) * numCols);
            const cEnd = Math.min(numCols, Math.max(cStart + 1, Math.ceil(((px + 1) / W) * numCols)));

            // Aggregate per-barrier success/failure across the reports this pixel covers,
            // reading report.nodeMappings directly (no global cellLookup).
            const colCounts = new Map<number, {s: number; f: number}>();
            for (let c = cStart; c < cEnd; c++) {
                const mappings = reports[c].nodeMappings;
                for (let m = 0; m < mappings.length; m++) {
                    const nm = mappings[m];
                    if (!barrierIds.has(nm.nodeId)) continue;
                    let cc = colCounts.get(nm.nodeId);
                    if (!cc) {
                        cc = {s: 0, f: 0};
                        colCounts.set(nm.nodeId, cc);
                    }
                    if (nm.positiveInteraction === true) cc.s++;
                    else cc.f++;
                }
            }

            for (let r = 0; r < numRows; r++) {
                const cc = colCounts.get(rows[r].node.id);
                const challenged = cc ? cc.s + cc.f : 0;
                ctx.fillStyle = challenged === 0 ? COLOR_NO_MAPPING : blendSuccessFailure(cc!.f / challenged, successRgb, failureRgb);
                ctx.fillRect(px, TOP_PAD + r * cellH, 1, rowH);
            }
        }

        // Trend-window overlay: subtle blue band over in-band region on the left +
        // whitewash dim over out-of-band region on the right + amber divider at the
        // recent/prior split. Pill sits in the reserved top strip; the rest spans only
        // the heatmap area below it. Date-based positioning (when dates are available)
        // matches the dotplot — divider lands at the actual time midpoint of the band.
        if (overlay) {
            const bandEndX = bandEndXTime != null
                ? Math.max(0, Math.min(W, bandEndXTime))
                : (overlay.bandEnd >= 0 ? (overlay.bandEnd / numCols) * W : null);
            if (bandOn && bandEndX != null && bandEndX > 0) {
                ctx.fillStyle = TREND_BAND_FILL;
                ctx.fillRect(0, TOP_PAD, bandEndX, H - TOP_PAD);
            }
            if (bandOn && bandEndX != null && bandEndX < W) {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
                ctx.fillRect(bandEndX, TOP_PAD, W - bandEndX, H - TOP_PAD);
            }
            if (hasSplit) {
                const splitX = splitXTime != null
                    ? splitXTime
                    : (overlay.split / numCols) * W;
                drawTrendWindowDivider(ctx, splitX, TOP_PAD, H);

                if (pillText) {
                    ctx.save();
                    ctx.font = 'bold 10px sans-serif';
                    const tw = ctx.measureText(pillText).width;
                    const pillW = tw + 12;
                    const pillH = 16;
                    const pillY = 3;
                    const pillX = Math.max(2, Math.min(W - pillW - 2, splitX - pillW / 2));
                    ctx.fillStyle = '#ff6f00';
                    const r = 8;
                    ctx.beginPath();
                    ctx.moveTo(pillX + r, pillY);
                    ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, r);
                    ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, r);
                    ctx.arcTo(pillX, pillY + pillH, pillX, pillY, r);
                    ctx.arcTo(pillX, pillY, pillX + pillW, pillY, r);
                    ctx.closePath();
                    ctx.fill();
                    ctx.fillStyle = '#fff';
                    ctx.textBaseline = 'middle';
                    ctx.textAlign = 'center';
                    ctx.fillText(pillText, pillX + pillW / 2, pillY + pillH / 2);
                    ctx.restore();
                }
            }
        }
    }, []);

    // Draw overview on activation / data change, and redraw on container resize.
    useEffect(() => {
        if (!overviewMode || viewMode !== 'effectiveness') return undefined;
        const container = overviewContainerRef.current;
        if (!container) return undefined;

        let rafId = requestAnimationFrame(drawOverview);
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(drawOverview);
        });
        observer.observe(container);

        return (): void => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [overviewMode, viewMode, tableRows, tableMappedReports, analysis, detailedFullscreen, colorBlindMode, trendOverlayCols, showTrendBand, splitWindowLabel, drawOverview]);

    // Guard: if the store's data belongs to a different IRP (shared singleton store),
    // render nothing — the parent loading mechanism will handle reloading.
    if (irpDetails && irpDetails.id !== irpId) {
        return null;
    }

    // Loading state — show progress while data is being fetched or the worker computes.
    if (!barrierAnalysisCache && !dataSource) return null;
    if (!barrierAnalysisCache || !analysis) {
        return (
            <Box sx={{p: 4, textAlign: 'center'}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {!barrierAnalysisCache ? 'Loading barrier effectiveness data...' : 'Building barrier analysis...'}
                </Typography>
                <LinearProgress sx={{maxWidth: 300, mx: 'auto'}} />
            </Box>
        );
    }

    // Empty state — only when the fault tree genuinely has no barriers (not when a
    // search/filter has narrowed the visible rows to none).
    if (allBarrierNodes.length === 0) {
        return (
            <Box sx={{p: 3, textAlign: 'center'}}>
                <Typography variant="body1" color="text.secondary">
                    No barrier nodes found in the fault tree.
                </Typography>
            </Box>
        );
    }

    if (mappedReports.length === 0) {
        return (
            <Box sx={{p: 3, textAlign: 'center'}}>
                <Typography variant="body1" color="text.secondary">
                    No mapped reports found. Reports must be processed and mapped before barrier effectiveness can be analyzed.
                </Typography>
            </Box>
        );
    }

    const totalWidth = viewMode === 'effectiveness'
        ? tableMappedReports.length * CELL_WIDTH
        : tableTimeBuckets.length * TIMELINE_CELL_WIDTH;
    const totalHeight = tableRows.length * CELL_HEIGHT;

    return (
        <Box ref={scrollContainerRef} onScroll={handleContainerScroll} sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
            {/* Sticky toolbar wrapper — keeps minimal header and detailed toolbar visible while scrolling */}
            <Box sx={{
                position: 'sticky', top: 0, zIndex: 2,
                backgroundColor: 'background.paper',
                pt: 0.5,
            }}>
                {/* Thin indicator while the worker recomputes (stale results stay visible) */}
                {computing && <LinearProgress sx={{height: 2, mb: 0.5}} />}
                {/* Sub-tab nav + shared global controls. Tabs are mutually exclusive — each
                sub-tab owns its own toolbar inside its content area. Trend window stays
                here because it affects every sub-tab; Min recent sample appears only on
                the Needs attention sub-tab (the only surface it applies to) in window mode. */}
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1, gap: 1}}>
                    <Tabs
                        value={activeSubTab}
                        onChange={(_e, v): void => setActiveSubTab(v as BeSubTab)}
                        variant="standard"
                        sx={{minHeight: 36, '& .MuiTab-root': {minHeight: 36, fontSize: '0.75rem', textTransform: 'none', py: 0}}}
                    >
                        <Tab value="summary" icon={<DashboardIcon fontSize="small" />} iconPosition="start" label="Dashboard" />
                        <Tab value="healthChart" icon={<ScatterPlotIcon fontSize="small" />} iconPosition="start" label="Health map" />
                        <Tab value="topN" icon={<WarningAmberIcon fontSize="small" />} iconPosition="start" label="Needs attention" />
                        <Tab value="detail" icon={<ManageSearchIcon fontSize="small" />} iconPosition="start" label="Explore" />
                    </Tabs>
                    <Box sx={{flexGrow: 1}} />
                    {/* Trend window — drives period-over-period stats (recent vs prior)
                    for the Top N "was/now" tooltip and upcoming barrier table columns.
                    Always visible regardless of which panels the user has expanded. */}
                    <TrendWindowSelect
                        value={barrierTrendWindow}
                        onChange={setBarrierTrendWindow}
                        tooltip="Trend window — splits the date range into recent vs. prior halves for period-over-period analysis. Mid-point splits the IRP range at its midpoint; numeric values use the last N months."
                    />
                    {/* Min recent sample — drops barriers with too few recent-window challenges
                    from the Needs attention list (the only surface that ranks barriers
                    without visually encoding their sample size). Shown only there, in window
                    mode. The Health map already reveals sample via its Demand-Coverage axis,
                    and the inventory views always show all barriers. */}
                    {topViewMode === 'window' && activeSubTab === 'topN' && (
                        <>
                            <Tooltip title="Drops barriers with fewer than this many challenges in the recent window from the Needs attention list — a thin recent sample makes their trend unreliable. Only applies to this list; the Health map (sample shows on its Demand-Coverage axis) and the Explore views show all barriers." arrow>
                                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', cursor: 'help', ml: 1}}>
                                Min recent sample
                                </Typography>
                            </Tooltip>
                            <FormControl size="small" sx={{minWidth: 70}}>
                                <Select
                                    value={topMinSample}
                                    onChange={(e): void => setTopMinSample(Number(e.target.value))}
                                    sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                                >
                                    <MenuItem value={0} sx={{fontSize: '0.75rem'}}>All</MenuItem>
                                    <MenuItem value={1} sx={{fontSize: '0.75rem'}}>≥1</MenuItem>
                                    <MenuItem value={2} sx={{fontSize: '0.75rem'}}>≥2</MenuItem>
                                    <MenuItem value={3} sx={{fontSize: '0.75rem'}}>≥3</MenuItem>
                                    <MenuItem value={5} sx={{fontSize: '0.75rem'}}>≥5</MenuItem>
                                    <MenuItem value={10} sx={{fontSize: '0.75rem'}}>≥10</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}
                    {/* Source filter — global to BE: applies to every sub-tab's data EXCEPT the
                     Source Comparison view, which deliberately compares across all sources.
                     Hidden there since it has no effect (and would trigger a pointless
                     recompute); the selection persists and re-applies on leaving that view. */}
                    {!(activeSubTab === 'detail' && viewMode === 'sourceComparison') && (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                endIcon={<ArrowDropDownIcon />}
                                onClick={(e): void => setSourceMenuAnchor(e.currentTarget)}
                                sx={{ml: 1}}
                            >
                            Source: {selectedSource}
                            </Button>
                            <Menu
                                anchorEl={sourceMenuAnchor}
                                open={Boolean(sourceMenuAnchor)}
                                onClose={(): void => setSourceMenuAnchor(null)}
                            >
                                <MenuItem
                                    selected={selectedSource === 'All'}
                                    onClick={(): void => { setSelectedSource('All'); setSourceMenuAnchor(null); }}
                                    dense
                                >
                                All
                                </MenuItem>
                                {availableSources.map((source) => (
                                    <MenuItem
                                        key={source}
                                        selected={selectedSource === source}
                                        onClick={(): void => { setSelectedSource(source); setSourceMenuAnchor(null); }}
                                        dense
                                    >
                                        {source}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                    {/* Top-level barriers only — global filter (excludes sub-barriers from the
                    barrier set everywhere: KPIs, Needs attention, Health map, Explore). Lives
                    in the header so it's visible/adjustable from every sub-tab; shown only when
                    the model actually has sub-barriers to exclude. */}
                    {topLevelIds.length < allBarrierNodes.length && (
                        <Tooltip title="Show only top-level barriers — exclude sub-barriers from the barrier set across all sub-tabs (KPIs, Needs attention, Health map, and the Explore views).">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={topLevelOnly}
                                        onChange={(e): void => setTopLevelOnly(e.target.checked)}
                                        size="small"
                                        sx={{py: 0}}
                                    />
                                }
                                label={<Typography variant="caption">Top-level barriers only</Typography>}
                                sx={{ml: 1, mr: 0}}
                            />
                        </Tooltip>
                    )}
                </Box>

                {/* Detailed toolbar — only visible when detailed view is active */}
                {activeSubTab === 'detail' && (
                    <Box sx={{display: 'flex', alignItems: 'center', mb: 1, gap: 1}}>
                        <TextField
                            size="small"
                            placeholder="Search barriers…"
                            value={barrierSearch}
                            onChange={(e): void => setBarrierSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: <SearchIcon fontSize="small" sx={{color: 'text.disabled', mr: 0.5}}/>,
                                    endAdornment: barrierSearch ? (
                                        <IconButton size="small" sx={{p: 0}} onClick={(): void => setBarrierSearch('')}>
                                            <CloseIcon fontSize="small"/>
                                        </IconButton>
                                    ) : null,
                                }
                            }}
                            sx={{width: 200, '& .MuiInputBase-input': {fontSize: '0.75rem', py: 0.5}}}
                        />
                        <Box sx={{flexGrow: 1}} />
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={(e): void => setViewMenuAnchor(e.currentTarget)}
                        >
                    View: {viewMode === 'effectiveness' ? 'Effectiveness' : viewMode === 'timeline' ? 'Timeline' : viewMode === 'calendar' ? 'Calendar' : viewMode === 'failurePattern' ? 'Failure pattern' : 'Source Comparison'}
                        </Button>
                        <Menu
                            anchorEl={viewMenuAnchor}
                            open={Boolean(viewMenuAnchor)}
                            onClose={(): void => setViewMenuAnchor(null)}
                        >
                            <MenuItem
                                selected={viewMode === 'effectiveness'}
                                onClick={(): void => { setViewMode('effectiveness'); setChartSelectedBarriers(new Set()); setVisibleColumns(new Set<BarrierColumn>(['id', 'name'])); setViewMenuAnchor(null); }}
                                dense
                            >
                        Effectiveness
                            </MenuItem>
                            <MenuItem
                                selected={viewMode === 'timeline'}
                                onClick={(): void => { setViewMode('timeline'); setVisibleColumns(new Set<BarrierColumn>(['id', 'name'])); setViewMenuAnchor(null); }}
                                dense
                            >
                        Timeline
                            </MenuItem>
                            <MenuItem
                                selected={viewMode === 'sourceComparison'}
                                onClick={(): void => {
                                    setViewMode('sourceComparison');
                                    setChartSelectedBarriers(new Set());
                                    setVisibleColumns((prev) => {
                                        if (prev.has('success') || prev.has('failure') || prev.has('demand') || prev.has('effectiveness') || prev.has('failureExposure')) return prev;
                                        const next = new Set(prev);
                                        next.add('success');
                                        next.add('failure');
                                        return next;
                                    });
                                    setViewMenuAnchor(null);
                                }}
                                dense
                            >
                        Source Comparison
                            </MenuItem>
                            <MenuItem
                                selected={viewMode === 'calendar'}
                                onClick={(): void => {
                                    setViewMode('calendar');
                                    setChartSelectedBarriers(new Set());
                                    setViewMenuAnchor(null);
                                }}
                                dense
                            >
                        Calendar
                            </MenuItem>
                            <MenuItem
                                selected={viewMode === 'failurePattern'}
                                onClick={(): void => {
                                    setViewMode('failurePattern');
                                    setChartSelectedBarriers(new Set());
                                    setViewMenuAnchor(null);
                                }}
                                dense
                            >
                        Failure pattern
                            </MenuItem>
                        </Menu>
                        {viewMode === 'timeline' && (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    endIcon={<ArrowDropDownIcon />}
                                    onClick={(e): void => setGranularityMenuAnchor(e.currentTarget)}
                                >
                                    {timeGranularity === 'monthly' ? 'Monthly' : timeGranularity === 'quarterly' ? 'Quarterly' : 'Yearly'}
                                </Button>
                                <Menu
                                    anchorEl={granularityMenuAnchor}
                                    open={Boolean(granularityMenuAnchor)}
                                    onClose={(): void => setGranularityMenuAnchor(null)}
                                >
                                    <MenuItem
                                        selected={timeGranularity === 'monthly'}
                                        onClick={(): void => { setTimeGranularity('monthly'); setGranularityMenuAnchor(null); }}
                                        dense
                                    >
                                Monthly
                                    </MenuItem>
                                    <MenuItem
                                        selected={timeGranularity === 'quarterly'}
                                        onClick={(): void => { setTimeGranularity('quarterly'); setGranularityMenuAnchor(null); }}
                                        dense
                                    >
                                Quarterly
                                    </MenuItem>
                                    <MenuItem
                                        selected={timeGranularity === 'yearly'}
                                        onClick={(): void => { setTimeGranularity('yearly'); setGranularityMenuAnchor(null); }}
                                        dense
                                    >
                                Yearly
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                        {/* Column visibility only applies to the matrix / source-comparison tables.
                    The Calendar and Failure pattern views are chart-only (no barrier-attribute
                    columns), so the control is hidden there (matching the timeline-only
                    granularity control). */}
                        {viewMode !== 'calendar' && viewMode !== 'failurePattern' && (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ViewColumnIcon />}
                                    onClick={(e): void => setColumnMenuAnchor(e.currentTarget)}
                                >
                            Columns
                                </Button>
                                <Menu
                                    anchorEl={columnMenuAnchor}
                                    open={Boolean(columnMenuAnchor)}
                                    onClose={(): void => setColumnMenuAnchor(null)}
                                >
                                    {COLUMN_CONFIG
                                        .filter((col) => viewMode === 'timeline' || !TIMELINE_ONLY_COLUMNS.has(col.key))
                                        .map((col) => (
                                            <MenuItem key={col.key} onClick={(): void => toggleColumn(col.key)} dense>
                                                <Checkbox checked={visibleColumns.has(col.key)} size="small" sx={{p: 0, mr: 1}} />
                                                <ListItemText primary={col.label} />
                                            </MenuItem>
                                        ))}
                                </Menu>
                            </>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportCsv}
                        >
                    Export CSV
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Executive Summary */}
            {activeSubTab === 'summary' && (
                <Paper
                    variant="outlined"
                    sx={{
                        order: 2, mt: 2, mb: 1.5, px: 2, py: 1.5, display: 'flex', flexDirection: 'column', scrollMarginTop: '96px',
                        ...(fullscreenChart === 'kpi' && {
                            ...FULLSCREEN_OVERLAY_SX,
                            overflow: 'auto',
                        }),
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', mb: 1.5}}>
                        <Typography variant="subtitle2" sx={{flexGrow: 1}}>Key Performance Indicators</Typography>
                        <Tooltip title="KPIs reflect the trend window selected in the icon row above. 'All time' shows lifetime values; a months/Mid-point window shows recent-window values." arrow>
                            <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', cursor: 'help', mr: 1, whiteSpace: 'nowrap'}}>
                                {barrierTrendWindow === 'allTime'
                                    ? 'All time'
                                    : `Recent window: ${barrierTrendWindow === 'midpoint' ? 'Mid-point' : `${barrierTrendWindow} mo`}`}
                            </Typography>
                        </Tooltip>
                        <FullscreenToggle active={fullscreenChart === 'kpi'} onToggle={(): void => setFullscreenChart((p) => (p === 'kpi' ? null : 'kpi'))} />
                    </Box>
                    <Stack direction="row" spacing={2} sx={{mb: 3.5}} flexWrap="wrap" useFlexGap>
                        {/* Reports Assessed — total reports in the active scope (all-time or window). */}
                        <Paper elevation={0} sx={{px: 2, py: 1.5, minWidth: 140, textAlign: 'center', border: '1px solid', borderColor: 'divider'}}>
                            <Typography variant="caption" color="text.secondary">Reports Assessed</Typography>
                            <Typography variant="h5" sx={{fontWeight: 'bold', color: 'text.primary'}}>
                                {(topViewMode === 'allTime' ? executiveSummary.totalReports : windowSystemMetrics.totalReports).toLocaleString()}
                            </Typography>
                            {topViewMode === 'allTime' ? (
                                systemPassThrough.unmapped > 0 ? (
                                    <Tooltip title="View the reports without barrier data" arrow>
                                        <Typography
                                            variant="caption"
                                            onClick={(): void => { setUnmappedPage(0); setShowUnmappedDialog(true); }}
                                            sx={{color: 'primary.main', cursor: 'pointer', textDecoration: 'underline'}}
                                        >
                                            {systemPassThrough.unmapped.toLocaleString()} without barrier data
                                        </Typography>
                                    </Tooltip>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        all reports have barrier data
                                    </Typography>
                                )
                            ) : (
                                displayedUnmappedReports.length > 0 ? (
                                    <Tooltip title="View the reports in the recent window without barrier data" arrow>
                                        <Typography
                                            variant="caption"
                                            onClick={(): void => { setUnmappedPage(0); setShowUnmappedDialog(true); }}
                                            sx={{color: 'primary.main', cursor: 'pointer', textDecoration: 'underline'}}
                                        >
                                            {displayedUnmappedReports.length.toLocaleString()} without barrier data
                                        </Typography>
                                    </Tooltip>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        all reports in window have barrier data
                                    </Typography>
                                )
                            )}
                        </Paper>

                        {/* Challenged Reports — reports where at least one barrier was challenged. */}
                        <KpiCard
                            tooltip="Reports where at least one barrier was challenged (had a SUCCESS or FAILURE outcome). Excludes reports without any barrier data."
                            label="Challenged Reports"
                            value={(topViewMode === 'allTime' ? systemPassThrough.total : windowSystemMetrics.challengedReports).toLocaleString()}
                            subtext={((): string => {
                                const total = topViewMode === 'allTime' ? executiveSummary.totalReports : windowSystemMetrics.totalReports;
                                const challenged = topViewMode === 'allTime' ? systemPassThrough.total : windowSystemMetrics.challengedReports;
                                return total > 0 ? `${Math.round((challenged / total) * 100)}% of assessed` : '—';
                            })()}
                        />

                        {/* Effectiveness Bands — barriers counted into healthy/warning/critical effectiveness tiers; counts switch between window-aware and cumulative. */}
                        <Paper elevation={0} sx={{px: 2, py: 1.5, minWidth: 140, textAlign: 'center', border: '1px solid', borderColor: 'divider'}}>
                            <Typography variant="caption" color="text.secondary">Effectiveness Bands</Typography>
                            {((): React.ReactElement => {
                                const health = topViewMode === 'allTime'
                                    ? {healthyCount: executiveSummary.healthyCount, warningCount: executiveSummary.warningCount, criticalCount: executiveSummary.criticalCount, unchallengedCount: executiveSummary.unchallengedCount, totalBarriers: executiveSummary.totalBarriers}
                                    : windowBarrierHealth;
                                const tipScope = topViewMode === 'allTime' ? '' : ' in recent window';
                                return (
                                    <>
                                        <Stack direction="row" spacing={1} justifyContent="center" sx={{mt: 0.5}}>
                                            <Tooltip title={`${health.healthyCount} barrier${health.healthyCount !== 1 ? 's' : ''} with effectiveness ≥ 90%${tipScope}`} arrow>
                                                <Chip label={String(health.healthyCount)} size="small" sx={{backgroundColor: 'rgba(46, 125, 50, 0.15)', color: '#2e7d32', fontWeight: 'bold', cursor: 'help'}} />
                                            </Tooltip>
                                            <Tooltip title={`${health.warningCount} barrier${health.warningCount !== 1 ? 's' : ''} with effectiveness 70–90%${tipScope}`} arrow>
                                                <Chip label={String(health.warningCount)} size="small" sx={{backgroundColor: 'rgba(255, 152, 0, 0.15)', color: '#f57c00', fontWeight: 'bold', cursor: 'help'}} />
                                            </Tooltip>
                                            <Tooltip title={`${health.criticalCount} barrier${health.criticalCount !== 1 ? 's' : ''} with effectiveness < 70%${tipScope}`} arrow>
                                                <Chip label={String(health.criticalCount)} size="small" sx={{backgroundColor: 'rgba(211, 47, 47, 0.15)', color: '#d32f2f', fontWeight: 'bold', cursor: 'help'}} />
                                            </Tooltip>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
                                            {health.totalBarriers} {topLevelOnly ? 'top-level barriers' : 'total'}{health.unchallengedCount > 0 ? ` (${health.unchallengedCount} unchallenged)` : ''}
                                        </Typography>
                                    </>
                                );
                            })()}
                        </Paper>

                        {/* Pass-Through Rate — % of challenged reports where every challenged barrier failed. */}
                        <Tooltip title="Percentage of challenged reports where every applicable barrier failed for that report. Lower is better." arrow>
                            <Paper
                                elevation={0}
                                sx={{
                                    px: 2, py: 1.5, minWidth: 140, textAlign: 'center', cursor: 'help',
                                    backgroundColor: ((): string => {
                                        const r = topViewMode === 'allTime' ? systemPassThrough.rate : windowSystemMetrics.passThroughRate;
                                        if (r == null) return '#f5f5f5';
                                        return r <= 0.05 ? 'rgba(46, 125, 50, 0.08)'
                                            : r <= 0.15 ? 'rgba(255, 152, 0, 0.08)'
                                                : 'rgba(211, 47, 47, 0.08)';
                                    })(),
                                    border: '1px solid',
                                    borderColor: ((): string => {
                                        const r = topViewMode === 'allTime' ? systemPassThrough.rate : windowSystemMetrics.passThroughRate;
                                        if (r == null) return 'divider';
                                        return r <= 0.05 ? 'rgba(46, 125, 50, 0.3)'
                                            : r <= 0.15 ? 'rgba(255, 152, 0, 0.3)'
                                                : 'rgba(211, 47, 47, 0.3)';
                                    })(),
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">System Pass-Through Rate</Typography>
                                {((): React.ReactElement => {
                                    const rate = topViewMode === 'allTime' ? systemPassThrough.rate : windowSystemMetrics.passThroughRate;
                                    const failed = topViewMode === 'allTime' ? systemPassThrough.count : windowSystemMetrics.allFailedReports;
                                    const total = topViewMode === 'allTime' ? systemPassThrough.total : windowSystemMetrics.challengedReports;
                                    const color = rate == null ? 'text.disabled' : rate <= 0.05 ? '#2e7d32' : rate <= 0.15 ? '#f57c00' : '#d32f2f';
                                    return (
                                        <>
                                            <Typography variant="h5" sx={{fontWeight: 'bold', color}}>
                                                {rate != null ? `${Math.round(rate * 100)}%` : '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {failed} of {total} challenged reports
                                            </Typography>
                                        </>
                                    );
                                })()}
                            </Paper>
                        </Tooltip>

                        {/* Avg. Failures per Challenged Report — total barrier-failure events ÷
                            CHALLENGED reports (≥1 barrier exercised). Unchallenged reports are
                            excluded (not defense events). The complement of the Health map's
                            "Avg held": held + failed = challenged, per challenged report.
                            1.0 = a typical challenged report has one barrier fail; > 1 = multiple
                            barriers fail together in a typical challenged report. */}
                        {((): React.ReactElement => {
                            const stats = topViewMode === 'allTime' ? avgFailuresPerReport.allTime : avgFailuresPerReport.window;
                            return (
                                <KpiCard
                                    tooltip={`Total barrier-failure events across ${topLevelOnly ? 'top-level barriers' : 'all barriers'} ÷ challenged reports (reports where at least one barrier was exercised). The average number of barriers that fail per challenged report — the complement of the Health map's "Barriers Held per Report". > 1 means a typical challenged report has multiple barriers failing at once.`}
                                    label="Avg. Failures / Report"
                                    value={stats.perReport != null ? stats.perReport.toFixed(2) : '—'}
                                    subtext={`${stats.total.toLocaleString()} failure${stats.total !== 1 ? 's' : ''} / ${stats.denom.toLocaleString()} challenged report${stats.denom !== 1 ? 's' : ''}`}
                                />
                            );
                        })()}
                    </Stack>
                </Paper>
            )}

            {/* Demand Coverage distribution panel — first of the three core-metric
                distributions (Demand → Effectiveness → Failure Exposure). Neutral
                coloring: Demand Coverage is descriptive, not a health signal. */}
            {activeSubTab === 'summary' && demandCovDistData && demandCovDistData.values.length > 1 && (
                <MetricDistributionPanel
                    order={3}
                    title="Demand Coverage Distribution Across Barriers"
                    metricName="Demand Coverage"
                    bins={demandCovDistData.bins}
                    values={demandCovDistData.values}
                    stats={demandCovDistData.stats}
                    countLabel="Barriers"
                    meanLabel="Mean"
                    xAxisLabel="Demand Coverage band"
                    barColor={(): string => 'rgba(120, 120, 120, 0.55)'}
                    barBorder={(): string => 'rgba(120, 120, 120, 0.9)'}
                    howToRead={
                        <>
                            <Box>Demand Coverage = how often a barrier is challenged: (Success + Failure) ÷ scored reports (reports with ≥1 barrier decision; mapper-unscored reports are excluded).</Box>
                            <Box>Bars: count of barriers in each demand band — the empirical Probability Density Function across barriers. Coloring is neutral on purpose: high demand is not "bad", just frequently tested.</Box>
                            <Box>Line (blue): Cumulative Distribution Function — at a given band, what % of barriers sit at or below.</Box>
                            <Box sx={{mt: 0.5}}>A right-shifted curve = most barriers are challenged often; a left cluster = many barriers are rarely demanded.</Box>
                        </>
                    }
                />
            )}

            {/* F.Exp distribution panel — system-level view showing how Failure Exposure
                is spread across the barrier set. Inline (not a dialog) because the
                question "is this IRP a few bad barriers or systemic?" naturally belongs
                in Summary alongside the headline KPIs. */}
            {activeSubTab === 'summary' && fExpDistData && fExpDistData.values.length > 1 && (
                <MetricDistributionPanel
                    order={5}
                    title="Failure Exposure Distribution Across Barriers"
                    metricName="Failure Exposure"
                    bins={fExpDistData.bins}
                    values={fExpDistData.values}
                    stats={fExpDistData.stats}
                    countLabel="Barriers"
                    meanLabel="Mean F.Exp"
                    xAxisLabel="Failure Exposure band"
                    barColor={(b): string =>
                        b.start >= FEXP_CRITICAL_THRESHOLD ? 'rgba(211, 47, 47, 0.7)'
                            : b.start >= SILENT_KILLER_FEXP_THRESHOLD ? 'rgba(245, 124, 0, 0.7)'
                                : 'rgba(158, 158, 158, 0.6)'}
                    barBorder={(b): string =>
                        b.start >= FEXP_CRITICAL_THRESHOLD ? 'rgba(211, 47, 47, 1)'
                            : b.start >= SILENT_KILLER_FEXP_THRESHOLD ? 'rgba(245, 124, 0, 1)'
                                : 'rgba(158, 158, 158, 0.9)'}
                    howToRead={
                        <>
                            <Box>Bars: count of barriers in each F.Exp band, tinted by severity (gray below the moderate threshold, orange at/above it, red at/above critical — see the legend) — the empirical Probability Density Function across barriers.</Box>
                            <Box>Line (blue): Cumulative Distribution Function. At a given F.Exp band, what % of barriers sit at or below.</Box>
                            <Box sx={{mt: 0.5}}>Most barriers should cluster near 0 %. A long right tail = many barriers sit at high Failure Exposure (concerning). A whole-curve shift to the right means systemic exposure rather than a few bad barriers.</Box>
                        </>
                    }
                    extraStats={
                        <>
                            {/* Mutually-exclusive bands (match the bar legend); the moderate
                                band excludes critical so a ≥10% barrier isn't counted twice. */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">Moderate ({Math.round(SILENT_KILLER_FEXP_THRESHOLD * 100)}–{Math.round(FEXP_CRITICAL_THRESHOLD * 100)} %)</Typography>
                                <Typography variant="body2" fontWeight="bold" sx={{color: (fExpDistData.aboveModerate - fExpDistData.aboveCritical) > 0 ? '#f57c00' : 'text.primary'}}>{fExpDistData.aboveModerate - fExpDistData.aboveCritical}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Critical (≥ {Math.round(FEXP_CRITICAL_THRESHOLD * 100)} %)</Typography>
                                <Typography variant="body2" fontWeight="bold" sx={{color: fExpDistData.aboveCritical > 0 ? '#d32f2f' : 'text.primary'}}>{fExpDistData.aboveCritical}</Typography>
                            </Box>
                        </>
                    }
                    colorLegend={
                        <>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{width: 10, height: 10, borderRadius: 0.5, backgroundColor: 'rgba(158, 158, 158, 0.6)'}} />
                                <Typography variant="caption" color="text.secondary">Below {Math.round(SILENT_KILLER_FEXP_THRESHOLD * 100)}%</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{width: 10, height: 10, borderRadius: 0.5, backgroundColor: 'rgba(245, 124, 0, 0.7)'}} />
                                <Typography variant="caption" color="text.secondary">Moderate ({Math.round(SILENT_KILLER_FEXP_THRESHOLD * 100)}–{Math.round(FEXP_CRITICAL_THRESHOLD * 100)}%)</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{width: 10, height: 10, borderRadius: 0.5, backgroundColor: 'rgba(211, 47, 47, 0.7)'}} />
                                <Typography variant="caption" color="text.secondary">Critical (≥ {Math.round(FEXP_CRITICAL_THRESHOLD * 100)}%)</Typography>
                            </Stack>
                        </>
                    }
                />
            )}

            {/* Effectiveness Rate distribution panel — companion to the Failure Exposure
                distribution. Healthy = clustered right (near 100 %); concerning = a left
                tail or whole-curve shift toward 0 %. Bin coloring inverts the F.Exp
                palette so high effectiveness reads green, low reads red. */}
            {activeSubTab === 'summary' && effRateDistData && effRateDistData.values.length > 1 && (
                <MetricDistributionPanel
                    order={4}
                    title="Effectiveness Rate Distribution Across Barriers"
                    metricName="Effectiveness Rate"
                    bins={effRateDistData.bins}
                    values={effRateDistData.values}
                    stats={effRateDistData.stats}
                    countLabel="Challenged barriers"
                    meanLabel="Mean rate"
                    xAxisLabel="Effectiveness Rate band"
                    barColor={(b): string =>
                        b.start < 0.7 ? 'rgba(211, 47, 47, 0.7)'
                            : b.start < 0.9 ? 'rgba(245, 124, 0, 0.7)'
                                : 'rgba(46, 125, 50, 0.7)'}
                    barBorder={(b): string =>
                        b.start < 0.7 ? 'rgba(211, 47, 47, 1)'
                            : b.start < 0.9 ? 'rgba(245, 124, 0, 1)'
                                : 'rgba(46, 125, 50, 1)'}
                    howToRead={
                        <>
                            <Box>Bars: count of barriers in each effectiveness band, tinted by health (red &lt; 70%, orange 70–90%, green ≥ 90% — see the legend) — the empirical Probability Density Function across barriers.</Box>
                            <Box>Line (blue): Cumulative Distribution Function. At a given effectiveness band, what % of barriers sit at or below.</Box>
                            <Box sx={{mt: 0.5}}>Healthy barriers cluster near 100 %. A long LEFT tail = many barriers with low effectiveness (concerning). A whole-curve shift toward 0 % means systemic effectiveness problems rather than a few weak barriers.</Box>
                            <Box sx={{mt: 0.5, fontStyle: 'italic'}}>Unchallenged barriers (zero success + failure) are excluded — their effectiveness rate is undefined, not 0 %.</Box>
                        </>
                    }
                    extraStats={
                        <>
                            {/* Mutually-exclusive bands (match the bar legend); the warning band
                                excludes critical so a <70% barrier isn't counted twice. */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">Warning (70–90 %)</Typography>
                                <Typography variant="body2" fontWeight="bold" sx={{color: (effRateDistData.belowWarning - effRateDistData.belowCritical) > 0 ? '#f57c00' : 'text.primary'}}>{effRateDistData.belowWarning - effRateDistData.belowCritical}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Critical (&lt; 70 %)</Typography>
                                <Typography variant="body2" fontWeight="bold" sx={{color: effRateDistData.belowCritical > 0 ? '#d32f2f' : 'text.primary'}}>{effRateDistData.belowCritical}</Typography>
                            </Box>
                        </>
                    }
                    colorLegend={
                        <>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{width: 10, height: 10, borderRadius: 0.5, backgroundColor: 'rgba(46, 125, 50, 0.7)'}} />
                                <Typography variant="caption" color="text.secondary">Healthy (≥ 90%)</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{width: 10, height: 10, borderRadius: 0.5, backgroundColor: 'rgba(245, 124, 0, 0.7)'}} />
                                <Typography variant="caption" color="text.secondary">Warning (70–90%)</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{width: 10, height: 10, borderRadius: 0.5, backgroundColor: 'rgba(211, 47, 47, 0.7)'}} />
                                <Typography variant="caption" color="text.secondary">Critical (&lt; 70%)</Typography>
                            </Stack>
                        </>
                    }
                />
            )}

            {/* Top Barriers Requiring Attention */}
            {/* "Needs attention" panel — extracted to TopBarriersPanel. */}
            {activeSubTab === 'topN' && extendedTopCritical.length > 0 && (
                <TopBarriersPanel
                    filteredTopCritical={filteredTopCritical}
                    extendedStatusCounts={extendedStatusCounts}
                    topN={topN}
                    setTopN={setTopN}
                    topStatusFilter={topStatusFilter}
                    setTopStatusFilter={setTopStatusFilter}
                    topSort={topSort}
                    setTopSort={setTopSort}
                    topViewMode={topViewMode}
                    barrierTrendWindow={barrierTrendWindow}
                    allTimeScoredReports={systemPassThrough.total}
                    windowScoredReports={windowSystemMetrics.challengedReports}
                    barrierTrendStats={barrierTrendStats}
                    barrierTrends={barrierTrends}
                    barrierWindowMtbf={barrierWindowMtbf}
                    barrierMtbf={barrierMtbf}
                    timeGranularity={timeGranularity}
                    timeBuckets={timeBuckets}
                    bandBucketCutoffKeys={bandBucketCutoffKeys}
                    fullscreen={fullscreenChart === 'topBarriers'}
                    onToggleFullscreen={(): void => setFullscreenChart((prev) => (prev === 'topBarriers' ? null : 'topBarriers'))}
                    setGapDistBarrier={setGapDistBarrier}
                    setDrillBarrier={setDrillBarrier}
                    setDrillPage={setDrillPage}
                />
            )}

            {/* Demand-vs-Effectiveness quadrant chart + challenged-vs-failed scatter, stacked */}
            {activeSubTab === 'healthChart' && (
                <>
                    <Paper
                        variant="outlined"
                        sx={{
                            order: 4, mb: 1.5, p: 1.5, display: 'flex', flexDirection: 'column', scrollMarginTop: '96px',
                            ...(fullscreenChart === 'quadrant' && {
                                ...FULLSCREEN_OVERLAY_SX,
                            }),
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                            <Typography variant="subtitle2" sx={{flexGrow: 1}}>
                                {topLevelOnly ? 'Top-level: Demand Coverage vs. Effectiveness' : 'Demand Coverage vs. Effectiveness'}
                            </Typography>
                            <Tooltip title="Both axes reflect the trend window selected in the icon row above. 'All time' shows lifetime values; a months/Mid-point window shows recent-window values." arrow>
                                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', cursor: 'help', mr: 1, whiteSpace: 'nowrap'}}>
                                    {barrierTrendWindow === 'allTime'
                                        ? 'All time'
                                        : `Recent window: ${barrierTrendWindow === 'midpoint' ? 'Mid-point' : `${barrierTrendWindow} mo`}`}
                                </Typography>
                            </Tooltip>
                            <FullscreenToggle active={fullscreenChart === 'quadrant'} onToggle={(): void => setFullscreenChart((p) => (p === 'quadrant' ? null : 'quadrant'))} />
                        </Box>
                        {quadrantChartData.points.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
                            No challenged barriers to display.
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{height: fullscreenChart === 'quadrant' ? '100%' : 300, flex: fullscreenChart === 'quadrant' ? 1 : undefined}}>
                                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                    <Scatter data={quadrantChartData as any} options={quadrantChartOptions as any} plugins={[quadrantPlugin]} />
                                </Box>
                                <Stack direction="row" spacing={2} justifyContent="center" sx={{mt: 0.5}}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(46, 125, 50, 0.7)'}} />
                                        <Typography variant="caption" color="text.secondary">Healthy (≥90%)</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(255, 152, 0, 0.7)'}} />
                                        <Typography variant="caption" color="text.secondary">Warning (70–90%)</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(211, 47, 47, 0.7)'}} />
                                        <Typography variant="caption" color="text.secondary">Critical (&lt;70%)</Typography>
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </Paper>

                    {/* Per-report Challenged vs. Failed barriers (the distribution behind the
                    System Pass-Through Rate KPI). Shares the dashed y=x diagonal plugin with
                    the Report Clustering Challenged-vs-Failed chart. NB: deliberately NOT
                    titled "defense-in-depth" — that implies a Swiss-cheese layered structure
                    we can't assume the fault tree follows. */}
                    <Paper
                        variant="outlined"
                        sx={{
                            order: 4, mb: 1.5, p: 1.5, display: 'flex', flexDirection: 'column', scrollMarginTop: '96px',
                            ...(fullscreenChart === 'challengedFailed' && {
                                ...FULLSCREEN_OVERLAY_SX,
                            }),
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                            <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, flexGrow: 1}}>
                                <Typography variant="subtitle2">Challenged vs. Failed barriers per report</Typography>
                                <Tooltip
                                    arrow
                                    slotProps={{tooltip: {sx: {maxWidth: 480}}}}
                                    title={
                                        <Box sx={{fontSize: '0.7rem', lineHeight: 1.5}}>
                                            <Typography variant="caption" sx={{fontWeight: 'bold', display: 'block', mb: 0.5}}>How to read this chart</Typography>
                                            <Box>Each dot is one or more reports sharing the same counts of barriers challenged (X) and failed (Y). Dot size = number of reports.</Box>
                                            <Box sx={{mt: 0.5}}>Dots on the dashed diagonal (Y = X) had <strong>every</strong> challenged barrier fail — system pass-through. The further a dot sits below the diagonal, the more challenged barriers held.</Box>
                                            <Box sx={{mt: 0.5}}>The diagonal is the system pass-through condition. The System Pass-Through Rate KPI counts it across <strong>all</strong> barriers; this chart respects the Top-level-only filter, so the two coincide when it's off. Reflects the Source filter and the trend window.</Box>
                                        </Box>
                                    }
                                >
                                    <HelpOutlineIcon sx={{fontSize: 15, color: 'text.disabled', cursor: 'help'}} />
                                </Tooltip>
                                {avgBarriersHeld.value != null && ((): React.ReactElement => {
                                // Higher is better — more barriers held per challenge.
                                // Thresholds are a starting point; tune once real values are seen.
                                    const v = avgBarriersHeld.value;
                                    const color = v >= 2 ? '#2e7d32' : v >= 1 ? '#f57c00' : '#d32f2f';
                                    const bg = v >= 2 ? 'rgba(46, 125, 50, 0.1)' : v >= 1 ? 'rgba(245, 124, 0, 0.1)' : 'rgba(211, 47, 47, 0.1)';
                                    const borderColor = v >= 2 ? 'rgba(46, 125, 50, 0.4)' : v >= 1 ? 'rgba(245, 124, 0, 0.4)' : 'rgba(211, 47, 47, 0.4)';
                                    return (
                                        <Tooltip
                                            arrow
                                            title={`Barriers Held per Report — the average number of challenged barriers that succeeded (held) in a typical challenged report. Geometrically, the mean vertical gap below the Y = X (system pass-through) diagonal, across all ${avgBarriersHeld.reports.toLocaleString()} challenged report${avgBarriersHeld.reports !== 1 ? 's' : ''}. Reports on the diagonal (every challenged barrier failed) count as 0 and pull the average down. Higher means more barriers held per challenge. Respects the Source filter, Top-level-only, and the trend window.`}
                                        >
                                            <Box
                                                sx={{
                                                    ml: 1, px: 1, py: 0.25, cursor: 'help', whiteSpace: 'nowrap',
                                                    display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
                                                    borderRadius: 1, border: '1px solid', borderColor,
                                                    backgroundColor: bg,
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary">Barriers Held / Report</Typography>
                                                <Typography variant="caption" sx={{fontWeight: 'bold', color}}>
                                                    {v.toFixed(1)}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    );
                                })()}
                            </Box>
                            <FullscreenToggle
                                active={fullscreenChart === 'challengedFailed'}
                                onToggle={(): void => setFullscreenChart((p) => (p === 'challengedFailed' ? null : 'challengedFailed'))}
                            />
                        </Box>
                        {defenseDepthGroups.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
                            No challenged reports to display.
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{height: fullscreenChart === 'challengedFailed' ? '100%' : 300, flex: fullscreenChart === 'challengedFailed' ? 1 : undefined}}>
                                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                    <Scatter data={defenseDepthData as any} options={defenseDepthOptions as any} plugins={[scatterDiagonalPlugin]} />
                                </Box>
                                <Stack direction="row" spacing={2} justifyContent="center" sx={{mt: 0.5}} flexWrap="wrap" useFlexGap>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(46, 125, 50, 0.7)'}} />
                                        <Typography variant="caption" color="text.secondary">All held (0 failed)</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(245, 124, 0, 0.7)'}} />
                                        <Typography variant="caption" color="text.secondary">Partial</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(211, 47, 47, 0.7)'}} />
                                        <Typography variant="caption" color="text.secondary">All failed (pass-through)</Typography>
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </Paper>

                    {/* System Pass-Through Rate over time — temporal companion to the scatter
                    above, in its own panel. Spans the full date range (ignores the recent
                    trend window); reads newest-on-left per the BE convention. */}
                    <Paper
                        variant="outlined"
                        sx={{
                            order: 5, mb: 1.5, p: 1.5, display: 'flex', flexDirection: 'column', scrollMarginTop: '96px',
                            ...(fullscreenChart === 'passThroughTrend' && {
                                ...FULLSCREEN_OVERLAY_SX,
                            }),
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                            <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, flexGrow: 1}}>
                                <Typography variant="subtitle2">System Pass-Through Rate over time</Typography>
                                <Tooltip
                                    arrow
                                    slotProps={{tooltip: {sx: {maxWidth: 420}}}}
                                    title="Monthly share of challenged reports where every challenged barrier failed (a dot on the Y = X diagonal above). Lower is better. Reads newest-on-left. Reflects the Source filter and Top-level-only, but spans the full date range regardless of the trend window."
                                >
                                    <HelpOutlineIcon sx={{fontSize: 15, color: 'text.disabled', cursor: 'help'}} />
                                </Tooltip>
                            </Box>
                            {typeof barrierTrendWindow === 'number' && (
                                <FormControlLabel
                                    sx={{mr: 1}}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={hidePtoOutOfWindow}
                                            onChange={(e): void => setHidePtoOutOfWindow(e.target.checked)}
                                            sx={{p: 0.5}}
                                        />
                                    }
                                    label={<Typography variant="caption" color="text.secondary">Hide out-of-window</Typography>}
                                />
                            )}
                            <FullscreenToggle
                                active={fullscreenChart === 'passThroughTrend'}
                                onToggle={(): void => setFullscreenChart((p) => (p === 'passThroughTrend' ? null : 'passThroughTrend'))}
                            />
                        </Box>
                        {passThroughTimeSeries.labels.length < 2 ? (
                            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
                            Not enough dated reports to plot a trend.
                            </Typography>
                        ) : (
                            <Box sx={{height: fullscreenChart === 'passThroughTrend' ? '100%' : 160, flex: fullscreenChart === 'passThroughTrend' ? 1 : undefined}}>
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                <Line data={passThroughSparklineData as any} options={passThroughSparklineOptions as any} plugins={[passThroughBandPlugin, chartTimelineOverlayPlugin]} />
                            </Box>
                        )}
                    </Paper>
                </>
            )}

            {/* Chart panel — visible when 1+ barriers selected, OR when the panel is
                in fullscreen (so "Deselect All" inside fullscreen shows an empty-state
                placeholder instead of unmounting the panel and yanking the user out of
                fullscreen mode). */}
            {activeSubTab === 'detail' && (chartData || fullscreenChart === 'trend') && (
                <Box
                    ref={chartPanelRef}
                    sx={{
                        height: fullscreenChart === 'trend' ? '100vh' : chartHeight,
                        minHeight: 100,
                        maxHeight: fullscreenChart === 'trend' ? '100vh' : 500,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: fullscreenChart === 'trend' ? 0 : 1,
                        mb: 1,
                        scrollMarginTop: '96px',
                        ...(fullscreenChart === 'trend' && {
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            zIndex: 1300, backgroundColor: 'background.paper',
                        }),
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', px: 1, height: 28, minHeight: 28}}>
                        <Button
                            size="small"
                            variant="text"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={(e): void => setLegendMenuAnchor(e.currentTarget)}
                            sx={{fontSize: '0.75rem', textTransform: 'none', py: 0, minHeight: 0}}
                        >
                            Legend
                        </Button>
                        <Menu
                            anchorEl={legendMenuAnchor}
                            open={Boolean(legendMenuAnchor)}
                            onClose={(): void => setLegendMenuAnchor(null)}
                        >
                            {(chartData?.datasets ?? []).map((ds, idx) => (
                                <MenuItem key={idx} dense sx={{cursor: 'default', pointerEvents: 'none', py: 0.5, minHeight: 0}}>
                                    <Box
                                        sx={{
                                            width: 24,
                                            mr: 1,
                                            flexShrink: 0,
                                            borderBottom: ds.borderDash && ds.borderDash.length > 0
                                                ? `2px dashed ${ds.borderColor as string}`
                                                : `2px solid ${ds.borderColor as string}`,
                                        }}
                                    />
                                    <Typography variant="body2" sx={{fontSize: '0.75rem'}}>{ds.label}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                        <Button
                            size="small"
                            variant="text"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={(e): void => setChartFilterMenuAnchor(e.currentTarget)}
                            sx={{fontSize: '0.75rem', textTransform: 'none', py: 0, minHeight: 0, ml: 0.5}}
                        >
                            Show
                        </Button>
                        <Menu
                            anchorEl={chartFilterMenuAnchor}
                            open={Boolean(chartFilterMenuAnchor)}
                            onClose={(): void => setChartFilterMenuAnchor(null)}
                        >
                            <MenuItem
                                dense
                                onClick={(): void => setChartShowSuccess((prev) => (!prev || chartShowFailure) ? !prev : prev)}
                            >
                                <Checkbox checked={chartShowSuccess} size="small" sx={{p: 0, mr: 1}} />
                                <Box sx={{width: 24, mr: 1, flexShrink: 0, borderBottom: '2px solid currentColor'}} />
                                <ListItemText primary="Success" />
                            </MenuItem>
                            <MenuItem
                                dense
                                onClick={(): void => setChartShowFailure((prev) => (!prev || chartShowSuccess) ? !prev : prev)}
                            >
                                <Checkbox checked={chartShowFailure} size="small" sx={{p: 0, mr: 1}} />
                                <Box sx={{width: 24, mr: 1, flexShrink: 0, borderBottom: '2px dashed currentColor'}} />
                                <ListItemText primary="Failure" />
                            </MenuItem>
                        </Menu>
                        <Button
                            size="small"
                            variant="text"
                            onClick={(): void => {
                                const allIds = tableRows.map((r) => r.node.id);
                                const allSelected = allIds.length > 0 && allIds.every((id) => chartSelectedBarriers.has(id));
                                setChartSelectedBarriers(allSelected ? new Set() : new Set(allIds));
                            }}
                            sx={{fontSize: '0.75rem', textTransform: 'none', py: 0, minHeight: 0, ml: 0.5}}
                        >
                            {tableRows.length > 0 && tableRows.every((r) => chartSelectedBarriers.has(r.node.id))
                                ? 'Deselect All'
                                : 'Select All'}
                        </Button>
                        <Box sx={{flexGrow: 1}} />
                        <FullscreenToggle active={fullscreenChart === 'trend'} onToggle={(): void => setFullscreenChart((p) => (p === 'trend' ? null : 'trend'))} />
                    </Box>
                    <Box sx={{flexGrow: 1, minHeight: 0, px: 1, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        {chartData
                            ? <Line data={chartData} options={chartOptions} plugins={[chartTimelineOverlayPlugin]} />
                            : (
                                <Typography variant="body2" sx={{color: 'text.secondary'}}>
                                    No barriers selected. Click <strong>Select All</strong> above, or exit fullscreen and pick barriers from the table.
                                </Typography>
                            )}
                    </Box>
                    {fullscreenChart !== 'trend' && <Box
                        onMouseDown={handleChartResizeStart}
                        sx={{
                            height: 12,
                            minHeight: 12,
                            cursor: 'row-resize',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover > div': {backgroundColor: 'primary.main', height: 3},
                        }}
                    >
                        <Box sx={{width: 40, height: 2, borderRadius: 1, backgroundColor: 'divider', transition: 'all 0.15s'}} />
                    </Box>}
                </Box>
            )}

            {activeSubTab === 'detail' && (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.5, gap: 0.5, scrollMarginTop: '96px'}}>
                    {topViewMode === 'window' && viewMode !== 'sourceComparison' && viewMode !== 'failurePattern' && (
                        <>
                            <Tooltip title="Table values reflect the trend window selected in the icon row above. The right-side canvas highlights the band (recent + prior halves) and marks the split between them. Switch to 'All time' to see cumulative values, MTBF, and slope trends." arrow>
                                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', cursor: 'help', mr: 1, whiteSpace: 'nowrap'}}>
                                    Trend window: {barrierTrendWindow === 'midpoint' ? 'Mid-point' : `${barrierTrendWindow} mo each half`}
                                </Typography>
                            </Tooltip>
                            <Tooltip
                                title="Restrict cells to those inside the trend-window band (recent + prior halves). When unchecked, the full history stays visible with the band highlighted."
                                arrow
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={hideOutOfWindow}
                                            onChange={(e): void => setHideOutOfWindow(e.target.checked)}
                                            size="small"
                                            sx={{py: 0}}
                                        />
                                    }
                                    label={<Typography variant="caption">Hide out-of-window</Typography>}
                                    sx={{mr: 'auto', ml: 0}}
                                />
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title={
                        viewMode !== 'effectiveness'
                            ? 'Overview is available in the Effectiveness view'
                            : overviewMode ? 'Exit overview' : 'Overview — fit the whole heatmap on screen'
                    }>
                        {/* span wrapper so the tooltip still shows while the button is disabled */}
                        <span>
                            <IconButton
                                size="small"
                                onClick={(): void => setOverviewMode((o) => !o)}
                                disabled={viewMode !== 'effectiveness'}
                                color={overviewMode ? 'primary' : 'default'}
                            >
                                <GridViewIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={colorBlindMode ? 'Use default success/failure colors' : 'Use colorblind-safe colors'}>
                        <IconButton
                            size="small"
                            onClick={(): void => setColorBlindMode((v) => !v)}
                            color={colorBlindMode ? 'primary' : 'default'}
                        >
                            <InvertColorsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <FullscreenToggle active={detailedFullscreen} onToggle={(): void => setDetailedFullscreen((f) => !f)} enterTooltip="Fullscreen detailed analysis" />
                </Box>
            )}

            {activeSubTab === 'detail' && viewMode !== 'failurePattern' && visibleRows.length === 0 && (
                <Box sx={{p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        {barrierSearch.trim() ? `No barriers match "${barrierSearch.trim()}".` : 'No barriers to display.'}
                    </Typography>
                </Box>
            )}

            {activeSubTab === 'detail' && viewMode === 'calendar' && visibleRows.length > 0 && (
                <CalendarView
                    dailyDemand={dailyDemand}
                    globalMaxDemand={globalMaxDemand}
                    calendarVisibleYears={calendarVisibleYears}
                    calendarTotalPages={calendarTotalPages}
                    calendarVisibleBarrierIdSet={calendarVisibleBarrierIdSet}
                    calendarPage={calendarPage}
                    setCalendarPage={setCalendarPage}
                    calendarYearsPerPage={calendarYearsPerPage}
                    setCalendarYearsPerPage={setCalendarYearsPerPage}
                    calendarLayout={calendarLayout}
                    setCalendarLayout={setCalendarLayout}
                    calendarTrendOverlay={calendarTrendOverlay}
                    setCalendarTrendOverlay={setCalendarTrendOverlay}
                    barrierTrendBoundary={barrierTrendBoundary}
                    calendarHover={calendarHover}
                    setCalendarHover={setCalendarHover}
                    primaryFilter={primaryFilter}
                    colorSuccess={colorSuccess}
                    colorFailure={colorFailure}
                    navigateToReportAnalysis={navigateToReportAnalysis}
                    detailedFullscreen={detailedFullscreen}
                    setDetailedFullscreen={setDetailedFullscreen}
                    calendarDrillDate={calendarDrillDate}
                    setCalendarDrillDate={setCalendarDrillDate}
                    calendarDrillReports={calendarDrillReports}
                    calendarDrillPage={calendarDrillPage}
                    setCalendarDrillPage={setCalendarDrillPage}
                    calendarDrillPageSize={calendarDrillPageSize}
                    setCalendarDrillPageSize={setCalendarDrillPageSize}
                />
            )}

            {activeSubTab === 'detail' && viewMode === 'failurePattern' && (
                <Box
                    sx={{
                        flexGrow: 1,
                        minHeight: 360,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                        ...(detailedFullscreen && {
                            ...FULLSCREEN_OVERLAY_SX,
                        }),
                    }}
                >
                    {detailedFullscreen && (
                        <Tooltip title="Exit fullscreen">
                            <IconButton
                                size="small"
                                onClick={(): void => setDetailedFullscreen(false)}
                                sx={{position: 'absolute', top: 8, right: 8, zIndex: 1, backgroundColor: 'background.paper', boxShadow: 1}}
                            >
                                <FullscreenExitIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <TemporalDistributionPanel
                        entities={failurePatternEntities}
                        totalEntityCount={allVisibleRows.length}
                        trendWindow={barrierTrendBoundary}
                        trendWindowMode={barrierTrendWindow}
                        rangeFrom={primaryFilter.dateFrom}
                        rangeTo={primaryFilter.dateTo}
                        sort={failurePatternSort}
                        onSortChange={setFailurePatternSort}
                        minClusterCount={failurePatternMinSample}
                        onMinClusterCountChange={setFailurePatternMinSample}
                        hoveredKey={failurePatternHoveredKey}
                        onHoverKey={setFailurePatternHoveredKey}
                        onEntityClick={(key): void => {
                            const row = allVisibleRows.find((r) => String(r.node.id) === key);
                            if (row) setGapDistBarrier({nodeId: row.node.id, name: row.node.name || '', uniqueId: row.node.uniqueId || ''});
                        }}
                        entityNoun="barrier"
                        eventNoun="failure"
                    />
                </Box>
            )}

            {activeSubTab === 'detail' && visibleRows.length > 0 && viewMode !== 'calendar' && viewMode !== 'failurePattern' && (viewMode === 'sourceComparison' ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        flexShrink: 0,
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        minHeight: 200,
                        ...(detailedFullscreen && {
                            ...FULLSCREEN_OVERLAY_SX,
                        }),
                    }}
                >
                    {detailedFullscreen && (
                        <Tooltip title="Exit fullscreen">
                            <IconButton
                                size="small"
                                onClick={(): void => setDetailedFullscreen(false)}
                                sx={{position: 'absolute', top: 8, right: 8, zIndex: 1, backgroundColor: 'background.paper', boxShadow: 1}}
                            >
                                <FullscreenExitIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {((): React.ReactElement => {
                        const allSrcMetricCols: {key: BarrierColumn; label: string; minWidth: number}[] = [
                            {key: 'success', label: 'S', minWidth: 28},
                            {key: 'failure', label: 'F', minWidth: 28},
                            {key: 'demand', label: 'Demand', minWidth: 52},
                            {key: 'effectiveness', label: 'Effect.', minWidth: 52},
                            {key: 'failureExposure', label: 'F.Exp', minWidth: 52},
                        ];
                        const srcMetricCols = allSrcMetricCols.filter((c) => visibleColumns.has(c.key));
                        const colsPerSource = srcMetricCols.length;
                        return (
                            <table style={{borderCollapse: 'collapse', width: '100%', fontSize: '0.75rem'}}>
                                <thead>
                                    <tr>
                                        {visibleColumns.has('id') && (
                                            <th style={{position: 'sticky', left: 0, top: 0, zIndex: 2, backgroundColor: '#fff', padding: '6px 8px', borderBottom: '2px solid #e0e0e0', textAlign: 'left', whiteSpace: 'nowrap'}}>ID</th>
                                        )}
                                        {visibleColumns.has('name') && (
                                            <th style={{position: 'sticky', left: visibleColumns.has('id') ? 100 : 0, top: 0, zIndex: 2, backgroundColor: '#fff', padding: '6px 8px', borderBottom: '2px solid #e0e0e0', textAlign: 'left', whiteSpace: 'nowrap'}}>Name</th>
                                        )}
                                        {availableSources.map((source) => (
                                            <th
                                                key={source}
                                                colSpan={colsPerSource}
                                                style={{position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff', padding: '6px 4px', borderBottom: '2px solid #e0e0e0', textAlign: 'center', fontWeight: 600}}
                                            >
                                                {source}
                                            </th>
                                        ))}
                                    </tr>
                                    {colsPerSource > 0 && (
                                        <tr>
                                            {visibleColumns.has('id') && (
                                                <th style={{position: 'sticky', left: 0, top: 32, zIndex: 2, backgroundColor: '#f5f5f5', padding: '4px 8px', borderBottom: '1px solid #e0e0e0'}} />
                                            )}
                                            {visibleColumns.has('name') && (
                                                <th style={{position: 'sticky', left: visibleColumns.has('id') ? 100 : 0, top: 32, zIndex: 2, backgroundColor: '#f5f5f5', padding: '4px 8px', borderBottom: '1px solid #e0e0e0'}} />
                                            )}
                                            {availableSources.map((source) => (
                                                <React.Fragment key={source}>
                                                    {srcMetricCols.map((col) => (
                                                        <th
                                                            key={col.key}
                                                            style={{
                                                                position: 'sticky', top: 32, zIndex: 1, backgroundColor: '#f5f5f5',
                                                                padding: '4px', borderBottom: '1px solid #e0e0e0', textAlign: 'center',
                                                                minWidth: col.minWidth, fontSize: '0.65rem',
                                                                color: col.key === 'success' ? colorSuccess : col.key === 'failure' ? colorFailure : undefined,
                                                            }}
                                                        >
                                                            {col.key === 'failureExposure' ? (
                                                                <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.25}}>
                                                                    {col.label}
                                                                    <Tooltip
                                                                        title="Failure Exposure = Demand Coverage × (1 − Effectiveness Rate). Measures how often a barrier is both challenged and fails. Tinted yellow at ≥ 5 % (moderate threshold) and red at ≥ 10 %."
                                                                        arrow
                                                                    >
                                                                        <HelpOutlineIcon sx={{fontSize: 11, color: 'text.disabled', cursor: 'help'}} />
                                                                    </Tooltip>
                                                                </Box>
                                                            ) : col.label}
                                                        </th>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    )}
                                </thead>
                                <tbody>
                                    {visibleRows.map((row) => (
                                        <tr key={row.node.id} style={{borderBottom: '1px solid #f0f0f0'}}>
                                            {visibleColumns.has('id') && (
                                                <td style={{position: 'sticky', left: 0, backgroundColor: '#fff', padding: '4px 8px', whiteSpace: 'nowrap', paddingLeft: `${row.depth * 16 + 8}px`}}>
                                                    <Tooltip title="Click to see clusters where this barrier failed" arrow>
                                                        <span
                                                            onClick={(): void => navigateToOccurrencePatternsWithSearch(row.node.uniqueId || row.node.name || `#${row.node.id}`)}
                                                            style={{color: '#1976d2', cursor: 'pointer', textDecoration: 'none'}}
                                                            onMouseEnter={(e): void => { e.currentTarget.style.textDecoration = 'underline'; }}
                                                            onMouseLeave={(e): void => { e.currentTarget.style.textDecoration = 'none'; }}
                                                        >
                                                            {row.node.uniqueId || ''}
                                                        </span>
                                                    </Tooltip>
                                                </td>
                                            )}
                                            {visibleColumns.has('name') && (
                                                <td style={{position: 'sticky', left: visibleColumns.has('id') ? 100 : 0, backgroundColor: '#fff', padding: '4px 8px', whiteSpace: 'nowrap'}}>
                                                    {row.node.name || ''}
                                                </td>
                                            )}
                                            {availableSources.map((source) => {
                                                const metrics = sourceComparisonForView.get(source)?.get(row.node.id);
                                                const s = metrics?.success ?? 0;
                                                const f = metrics?.failure ?? 0;
                                                const total = s + f;
                                                const sourceReportCount = sourceReportCountsForView.get(source) ?? 0;
                                                const demandPct = sourceReportCount > 0 ? Math.round((total / sourceReportCount) * 100) : null;
                                                const effPct = total > 0 ? Math.round((s / total) * 100) : null;
                                                const demandRatio = sourceReportCount > 0 ? total / sourceReportCount : 0;
                                                const effRatio = total > 0 ? s / total : 0;
                                                const fExpPct = total > 0 && sourceReportCount > 0 ? Math.round(demandRatio * (1 - effRatio) * 100) : null;
                                                return (
                                                    <React.Fragment key={source}>
                                                        {visibleColumns.has('success') && (
                                                            <td style={{padding: '4px', textAlign: 'center', color: colorSuccess, fontWeight: s > 0 ? 600 : 400}}>
                                                                {s > 0 ? s : '—'}
                                                            </td>
                                                        )}
                                                        {visibleColumns.has('failure') && (
                                                            <td style={{padding: '4px', textAlign: 'center', color: colorFailure, fontWeight: f > 0 ? 600 : 400}}>
                                                                {f > 0 ? f : '—'}
                                                            </td>
                                                        )}
                                                        {visibleColumns.has('demand') && (
                                                            <td style={{padding: '4px', textAlign: 'center', color: 'inherit'}}>
                                                                {demandPct != null ? `${demandPct}%` : '—'}
                                                            </td>
                                                        )}
                                                        {visibleColumns.has('effectiveness') && (
                                                            <td style={{
                                                                padding: '4px', textAlign: 'center', fontWeight: 600,
                                                                color: effPct == null ? '#bdbdbd' : effPct >= 80 ? colorSuccess : effPct >= 50 ? '#f57c00' : colorFailure,
                                                            }}>
                                                                {effPct != null ? `${effPct}%` : '—'}
                                                            </td>
                                                        )}
                                                        {visibleColumns.has('failureExposure') && ((): React.ReactElement => {
                                                            const pill = fExpPct != null ? fexpPillStyle(fExpPct) : null;
                                                            return (
                                                                <td style={{padding: '4px', textAlign: 'center', color: 'inherit'}}>
                                                                    {fExpPct != null ? (
                                                                        <span style={pill ?? undefined}>{fExpPct}%</span>
                                                                    ) : '—'}
                                                                </td>
                                                            );
                                                        })()}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        );
                    })()}
                </Box>
            ) : (overviewMode && viewMode === 'effectiveness') ? (
                <Box
                    ref={overviewContainerRef}
                    sx={{
                        flexGrow: 1,
                        flexShrink: 0,
                        position: 'relative',
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        minHeight: 200,
                        ...(detailedFullscreen && {
                            ...FULLSCREEN_OVERLAY_SX,
                        }),
                    }}
                >
                    {detailedFullscreen && (
                        <Tooltip title="Exit fullscreen">
                            <IconButton
                                size="small"
                                onClick={(): void => setDetailedFullscreen(false)}
                                sx={{position: 'absolute', top: 8, right: 8, zIndex: 1, backgroundColor: 'background.paper', boxShadow: 1}}
                            >
                                <FullscreenExitIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <canvas ref={overviewCanvasRef} style={{display: 'block', width: '100%', height: '100%'}} />
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        flexShrink: 0,
                        display: 'flex',
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        minHeight: 200,
                        ...(detailedFullscreen && {
                            ...FULLSCREEN_OVERLAY_SX,
                        }),
                    }}
                >
                    {detailedFullscreen && (
                        <Tooltip title="Exit fullscreen">
                            <IconButton
                                size="small"
                                onClick={(): void => setDetailedFullscreen(false)}
                                sx={{position: 'absolute', top: 8, right: 8, zIndex: 1, backgroundColor: 'background.paper', boxShadow: 1}}
                            >
                                <FullscreenExitIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {/* Left column: corner cell + barrier names */}
                    <Box
                        sx={{
                            width: stickyColumnWidth,
                            minWidth: stickyColumnWidth,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRight: '2px solid',
                            borderRightColor: 'divider',
                        }}
                    >
                        {/* Corner cell — column headers for barrier attributes */}
                        <Box
                            sx={{
                                height: HEADER_HEIGHT,
                                minHeight: HEADER_HEIGHT,
                                display: 'flex',
                                alignItems: 'flex-end',
                                pl: '4px',
                                pr: 0.5,
                                pb: 0.5,
                                backgroundColor: 'background.paper',
                                borderBottom: '1px solid',
                                borderBottomColor: 'divider',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                gap: 1,
                            }}
                        >
                            <Box sx={{width: 24, flexShrink: 0}} />
                            {viewMode === 'timeline' && <Box sx={{width: 24, flexShrink: 0}} />}
                            {visibleColumns.has('id') && <Box sx={{width: 100, flexShrink: 0, minWidth: 0}}>ID</Box>}
                            {visibleColumns.has('name') && <Box sx={{flexGrow: 1, minWidth: 0}}>Name</Box>}
                            {visibleColumns.has('success') && (
                                <Box sx={{width: 32, flexShrink: 0, color: colorSuccess}}>S</Box>
                            )}
                            {visibleColumns.has('failure') && (
                                <Box sx={{width: 32, flexShrink: 0, color: colorFailure}}>F</Box>
                            )}
                            {visibleColumns.has('demand') && (
                                <Box sx={{width: 70, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    Demand
                                    <Tooltip
                                        title="Demand Coverage = (Success + Failure) / Total Mapped Reports. Measures how often a barrier is challenged across all reports."
                                        arrow
                                    >
                                        <HelpOutlineIcon sx={{fontSize: 13, color: 'text.disabled', cursor: 'help'}} />
                                    </Tooltip>
                                </Box>
                            )}
                            {visibleColumns.has('effectiveness') && (
                                <Box sx={{width: 70, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    Effect.
                                    <Tooltip
                                        title="Effectiveness Rate = Success / (Success + Failure). Measures how often a barrier succeeds when challenged. Unchallenged barriers show '—'."
                                        arrow
                                    >
                                        <HelpOutlineIcon sx={{fontSize: 13, color: 'text.disabled', cursor: 'help'}} />
                                    </Tooltip>
                                </Box>
                            )}
                            {visibleColumns.has('failureExposure') && (
                                <Box sx={{width: 70, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    F.Exp
                                    <Tooltip
                                        title="Failure Exposure = Demand Coverage × (1 − Effectiveness Rate). Measures how often a barrier is both challenged and fails. Tinted yellow at ≥ 5 % (moderate threshold) and red at ≥ 10 %."
                                        arrow
                                    >
                                        <HelpOutlineIcon sx={{fontSize: 13, color: 'text.disabled', cursor: 'help'}} />
                                    </Tooltip>
                                </Box>
                            )}
                            {viewMode === 'timeline' && visibleColumns.has('mtbf') && (
                                <Box sx={{width: 70, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    MTBF
                                    <Tooltip
                                        title={topViewMode === 'allTime'
                                            ? 'Mean Time Between Failures (days). Average number of days between consecutive barrier failure events across the full history. Requires at least 2 failures with valid dates.'
                                            : 'Mean Time Between Failures (days) inside the recent window. The arrow flags wear-out: ↓ red when recent gaps are markedly shorter than prior gaps (failures arriving faster), ↑ green when longer (improving). Hover the cell for the recent / prior comparison.'}
                                        arrow
                                    >
                                        <HelpOutlineIcon sx={{fontSize: 13, color: 'text.disabled', cursor: 'help'}} />
                                    </Tooltip>
                                </Box>
                            )}
                            {viewMode === 'timeline' && visibleColumns.has('effectivenessTrend') && (
                                <Box sx={{width: 52, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    E.Trd
                                    <Tooltip
                                        title={topViewMode === 'allTime'
                                            ? 'Effectiveness Trend. Shows whether the barrier\'s effectiveness rate is improving (↑), degrading (↓), or stable (→) over time. Based on linear slope across time periods.'
                                            : `Effectiveness Trend within the recent window (${recentWindowLabel}). Same arrow vocabulary as all-time mode — ↑ improving, ↓ degrading, → stable — but the slope is fit over recent-window months only. Requires at least 3 months with challenges in the window.`}
                                        arrow
                                    >
                                        <HelpOutlineIcon sx={{fontSize: 13, color: 'text.disabled', cursor: 'help'}} />
                                    </Tooltip>
                                </Box>
                            )}
                            {viewMode === 'timeline' && visibleColumns.has('demandTrend') && (
                                <Box sx={{width: 52, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    D.Trd
                                    <Tooltip
                                        title={topViewMode === 'allTime'
                                            ? 'Demand Trend. Shows whether the barrier is being challenged more (↑) or less (↓) frequently over time. Rising demand with flat effectiveness is an early warning.'
                                            : `Demand Trend within the recent window (${recentWindowLabel}). Monthly challenged-per-report rate fit by Ordinary Least Squares over recent-window months. Rising demand inside the window with flat effectiveness is an early warning. Requires at least 3 months with challenges in the window.`}
                                        arrow
                                    >
                                        <HelpOutlineIcon sx={{fontSize: 13, color: 'text.disabled', cursor: 'help'}} />
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>

                        {/* Barrier names — synced vertical scroll */}
                        <Box
                            ref={barrierColumnRef}
                            sx={{
                                flexGrow: 1,
                                overflow: 'hidden',
                                minHeight: 0,
                            }}
                        >
                            <Box sx={{height: totalHeight}}>
                                {tableRows.map((row) => {
                                    const allTimeTotals = barrierTotals.get(row.node.id) || {success: 0, failure: 0};
                                    const periodStats = barrierTrendStats.get(row.node.id);
                                    const inWindowMode = topViewMode === 'window';
                                    // In window mode the cells use the recent-window aggregates from
                                    // barrierTrendStats; in all-time mode they use the worker's cumulative totals.
                                    const totals = inWindowMode
                                        ? {success: periodStats?.recentSuccess ?? 0, failure: periodStats?.recentFailure ?? 0}
                                        : allTimeTotals;
                                    // Demand Coverage / F.Exp denominator = scored (challenged)
                                    // reports, not all reports — see denominator rule.
                                    const denomReports = inWindowMode ? windowSystemMetrics.challengedReports : systemPassThrough.total;
                                    // Soft sample-confidence fade for a thin recent window (1–4 challenges):
                                    // dims the trend cells with a tooltip. "Min recent sample" does NOT
                                    // hard-filter here — Timeline is a complete inventory; that drop lives on
                                    // the Needs attention list and the Health map.
                                    const recentChallenges = inWindowMode ? (periodStats?.recentChallenges ?? 0) : 0;
                                    const lowConfidence = inWindowMode && recentChallenges >= 1 && recentChallenges < LOW_CONFIDENCE_CHALLENGES;
                                    const lowConfTip = `Only ${recentChallenges} challenge${recentChallenges === 1 ? '' : 's'} in the recent window — thin sample, treat the trend slope with caution.`;
                                    return (
                                        <Box
                                            key={row.node.id}
                                            sx={{
                                                height: CELL_HEIGHT,
                                                display: 'flex',
                                                alignItems: 'center',
                                                pl: `${row.depth * 16 + 4}px`,
                                                pr: 0.5,
                                                gap: 1,
                                                backgroundColor: 'background.paper',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                },
                                            }}
                                        >
                                            <Box sx={{width: 24, minWidth: 24, maxWidth: 24, flexShrink: 0, display: 'flex', justifyContent: 'center'}}>
                                                {row.hasBarrierDescendants && !topLevelOnly && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(): void => toggleExpand(row.node.id)}
                                                        sx={{p: 0}}
                                                    >
                                                        {expanded.has(row.node.id) ? (
                                                            <RemoveIcon sx={{fontSize: 16}} />
                                                        ) : (
                                                            <AddIcon sx={{fontSize: 16}} />
                                                        )}
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {viewMode === 'timeline' && (
                                                <Box sx={{width: 24, minWidth: 24, maxWidth: 24, flexShrink: 0, display: 'flex', justifyContent: 'center'}}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(): void => toggleChartBarrier(row.node.id)}
                                                        sx={{p: 0, color: chartSelectedBarriers.has(row.node.id) ? 'primary.main' : 'text.disabled'}}
                                                    >
                                                        <ShowChartIcon sx={{fontSize: 16}} />
                                                    </IconButton>
                                                </Box>
                                            )}

                                            {/* Unique ID — click to jump to Report Clustering filtered to clusters where this barrier failed. */}
                                            {visibleColumns.has('id') && (
                                                <Tooltip title={`${row.node.uniqueId || ''}${row.node.uniqueId ? ' — ' : ''}click to see clusters where this barrier failed`} arrow>
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        onClick={(): void => navigateToOccurrencePatternsWithSearch(row.node.uniqueId || row.node.name || `#${row.node.id}`)}
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            width: 100,
                                                            flexShrink: 0,
                                                            minWidth: 0,
                                                            cursor: 'pointer',
                                                            color: 'primary.main',
                                                            '&:hover': {textDecoration: 'underline'},
                                                        }}
                                                    >
                                                        {row.node.uniqueId || ''}
                                                    </Typography>
                                                </Tooltip>
                                            )}

                                            {/* Name */}
                                            {visibleColumns.has('name') && (
                                                <Tooltip title={row.node.name || ''} arrow>
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            flexGrow: 1,
                                                            minWidth: 0,
                                                            cursor: 'default',
                                                        }}
                                                    >
                                                        {row.node.name || ''}
                                                    </Typography>
                                                </Tooltip>
                                            )}

                                            {/* Total Success */}
                                            {visibleColumns.has('success') && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{fontSize: '0.7rem', width: 32, flexShrink: 0, color: colorSuccess, fontWeight: 'bold'}}
                                                >
                                                    {totals.success}
                                                </Typography>
                                            )}

                                            {/* Total Failure */}
                                            {visibleColumns.has('failure') && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{fontSize: '0.7rem', width: 32, flexShrink: 0, color: colorFailure, fontWeight: 'bold'}}
                                                >
                                                    {totals.failure}
                                                </Typography>
                                            )}

                                            {/* Demand Coverage */}
                                            {visibleColumns.has('demand') && (
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    sx={{fontSize: '0.7rem', width: 70, flexShrink: 0, color: 'text.secondary'}}
                                                >
                                                    {denomReports > 0
                                                        ? `${Math.round(((totals.success + totals.failure) / denomReports) * 100)}%`
                                                        : '—'}
                                                </Typography>
                                            )}

                                            {/* Effectiveness Rate */}
                                            {visibleColumns.has('effectiveness') && (
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    sx={{fontSize: '0.7rem', width: 70, flexShrink: 0, color: 'text.secondary'}}
                                                >
                                                    {totals.success + totals.failure > 0
                                                        ? `${Math.round((totals.success / (totals.success + totals.failure)) * 100)}%`
                                                        : '—'}
                                                </Typography>
                                            )}

                                            {/* Failure Exposure — tinted yellow at ≥ 5 % (silent-killer threshold)
                                                 and red at ≥ 10 % so the F.Exp signal reads ahead of the status chip.
                                                 No thin-sample fade: F.Exp = Failures / Total Reports has a fixed
                                                 window-wide denominator, so it isn't a small-sample estimator. */}
                                            {visibleColumns.has('failureExposure') && ((): React.ReactElement => {
                                                const denom = totals.success + totals.failure;
                                                const fExpPct = denom > 0 && denomReports > 0
                                                    ? Math.round((denom / denomReports) * (1 - totals.success / denom) * 100)
                                                    : null;
                                                const pill = fExpPct != null ? fexpPillStyle(fExpPct) : null;
                                                return (
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        sx={{fontSize: '0.7rem', width: 70, flexShrink: 0, color: 'text.secondary'}}
                                                    >
                                                        {fExpPct != null ? (
                                                            <span style={pill ?? undefined}>{fExpPct}%</span>
                                                        ) : '—'}
                                                    </Typography>
                                                );
                                            })()}

                                            {/* MTBF — cumulative in all-time mode, recent-window with wear-out arrow in window mode.
                                                 The numeric value is clickable: opens the failure-gap distribution dialog
                                                 so analysts can see whether the mean is representative or hides clustering. */}
                                            {viewMode === 'timeline' && visibleColumns.has('mtbf') && ((): React.ReactElement => {
                                                const openDist = (): void => setGapDistBarrier({nodeId: row.node.id, name: row.node.name || '', uniqueId: row.node.uniqueId || ''});
                                                if (!inWindowMode) {
                                                    const m = barrierMtbf.get(row.node.id);
                                                    return (
                                                        <Tooltip title={m != null ? 'Mean Time Between Failures — average days between consecutive failure events. Click for full distribution.' : ''} arrow>
                                                            <Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 70, flexShrink: 0, color: 'text.secondary'}}>
                                                                {m != null
                                                                    ? <Box component="span" onClick={openDist} sx={{color: 'primary.main', cursor: 'pointer', '&:hover': {textDecoration: 'underline'}}}>{`${m}d`}</Box>
                                                                    : '—'}
                                                            </Typography>
                                                        </Tooltip>
                                                    );
                                                }
                                                const wm = barrierWindowMtbf.get(row.node.id);
                                                const recent = wm?.recent ?? null;
                                                const prior = wm?.prior ?? null;
                                                if (recent == null) {
                                                    return (
                                                        <Tooltip title={prior != null ? `No recent-window MTBF (fewer than 2 failures in the recent half). Prior: ${prior}d.` : 'Need at least 2 failures inside a window to compute MTBF.'} arrow>
                                                            <Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 70, flexShrink: 0, color: 'text.disabled', cursor: 'help'}}>—</Typography>
                                                        </Tooltip>
                                                    );
                                                }
                                                // Wear-out indicator: recent ≪ prior means failures arrive faster.
                                                // Mirror cluster-trend thresholds (< 0.7 / > 1.3) so the reading is consistent.
                                                let arrow = '';
                                                let color: string = 'text.secondary';
                                                let signal = '';
                                                if (prior != null && prior > 0) {
                                                    const ratio = recent / prior;
                                                    if (ratio < 0.7) { arrow = '↓'; color = colorFailure; signal = `Wear-out signal: recent gaps are ~${(prior / recent).toFixed(1)}× shorter than prior.`; }
                                                    else if (ratio > 1.3) { arrow = '↑'; color = colorSuccess; signal = `Improving: recent gaps are ~${(recent / prior).toFixed(1)}× longer than prior.`; }
                                                    else { arrow = '→'; color = 'text.disabled'; signal = 'Stable: recent and prior MTBF are within ±30%.'; }
                                                }
                                                const tip = prior != null
                                                    ? `Recent: ${recent}d, prior: ${prior}d. ${signal} Click for full distribution.`
                                                    : `Recent: ${recent}d. Not enough prior-window failures to compare. Click for full distribution.`;
                                                return (
                                                    <Tooltip title={tip} arrow>
                                                        <Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 70, flexShrink: 0, color, fontWeight: arrow === '↓' || arrow === '↑' ? 'bold' : 'normal'}}>
                                                            <Box component="span" onClick={openDist} sx={{cursor: 'pointer', '&:hover': {textDecoration: 'underline'}}}>
                                                                {recent}d{arrow ? ` ${arrow}` : ''}
                                                            </Box>
                                                        </Typography>
                                                    </Tooltip>
                                                );
                                            })()}

                                            {/* Effectiveness Trend — all-time slope from worker (per granularity bucket)
                                                 in all-time mode, recent-window monthly slope from `barrierTrendStats`
                                                 in window mode. Dimmed + tooltip when the recent sample is thin
                                                 (soft confidence fade). */}
                                            {viewMode === 'timeline' && visibleColumns.has('effectivenessTrend') && ((): React.ReactElement => {
                                                const s = inWindowMode
                                                    ? (periodStats?.recentSlope ?? null)
                                                    : (barrierTrends.get(row.node.id)?.effectivenessSlope ?? null);
                                                if (s == null) return <Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 52, flexShrink: 0, color: 'text.secondary'}}>—</Typography>;
                                                const unit = inWindowMode ? 'mo' : (timeGranularity === 'monthly' ? 'mo' : timeGranularity === 'quarterly' ? 'q' : 'yr');
                                                const abs = Math.abs(s);
                                                const pct = `${abs < 0.005 ? '<1' : Math.round(abs * 100)}%/${unit}`;
                                                const tipSuffix = lowConfidence ? ` — ${lowConfTip}` : '';
                                                const opacity = lowConfidence ? 0.55 : 1;
                                                if (abs < TREND_STABLE_THRESHOLD) return <Tooltip title={`Stable (${pct})${tipSuffix}`} arrow><Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 52, flexShrink: 0, color: 'text.disabled', opacity, cursor: 'help'}}>→</Typography></Tooltip>;
                                                const rising = s > 0;
                                                return <Tooltip title={`${rising ? '+' : '−'}${pct}${tipSuffix}`} arrow><Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 52, flexShrink: 0, color: rising ? colorSuccess : colorFailure, fontWeight: 'bold', opacity, cursor: 'help'}}>{rising ? '↑' : '↓'}</Typography></Tooltip>;
                                            })()}

                                            {/* Demand Trend — same dual-source pattern as E.Trd. */}
                                            {viewMode === 'timeline' && visibleColumns.has('demandTrend') && ((): React.ReactElement => {
                                                const s = inWindowMode
                                                    ? (periodStats?.recentDemandSlope ?? null)
                                                    : (barrierTrends.get(row.node.id)?.demandSlope ?? null);
                                                if (s == null) return <Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 52, flexShrink: 0, color: 'text.secondary'}}>—</Typography>;
                                                const unit = inWindowMode ? 'mo' : (timeGranularity === 'monthly' ? 'mo' : timeGranularity === 'quarterly' ? 'q' : 'yr');
                                                const abs = Math.abs(s);
                                                const pct = `${abs < 0.005 ? '<1' : Math.round(abs * 100)}%/${unit}`;
                                                const tipSuffix = lowConfidence ? ` — ${lowConfTip}` : '';
                                                const opacity = lowConfidence ? 0.55 : 1;
                                                if (abs < TREND_STABLE_THRESHOLD) return <Tooltip title={`Stable (${pct})${tipSuffix}`} arrow><Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 52, flexShrink: 0, color: 'text.disabled', opacity, cursor: 'help'}}>→</Typography></Tooltip>;
                                                const rising = s > 0;
                                                // Demand: rising = concerning (red), falling = good (green)
                                                return <Tooltip title={`${rising ? '+' : '−'}${pct}${tipSuffix}`} arrow><Typography variant="body2" noWrap sx={{fontSize: '0.7rem', width: 52, flexShrink: 0, color: rising ? colorFailure : colorSuccess, fontWeight: 'bold', opacity, cursor: 'help'}}>{rising ? '↑' : '↓'}</Typography></Tooltip>;
                                            })()}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>

                    {/* Right column: header canvas + body canvas */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            minWidth: 0,
                        }}
                    >
                        {/* Header canvas — redraws visible portion on scroll */}
                        <Box
                            sx={{
                                height: HEADER_HEIGHT,
                                minHeight: HEADER_HEIGHT,
                                overflow: 'hidden',
                                backgroundColor: 'background.paper',
                                borderBottom: '1px solid',
                                borderBottomColor: 'divider',
                            }}
                        >
                            <canvas
                                ref={headerCanvasRef}
                                style={{display: 'block'}}
                            />
                        </Box>

                        {/* Body: canvas layer + transparent scroll overlay */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                position: 'relative',
                                minHeight: 0,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Canvas — fixed at top-left, never scrolls */}
                            <canvas
                                ref={bodyCanvasRef}
                                style={{position: 'absolute', top: 0, left: 0, display: 'block'}}
                            />
                            {/* Scroll overlay — transparent, provides scrollbar and captures events */}
                            <div
                                ref={matrixScrollRef}
                                onScroll={handleMatrixScroll}
                                style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto'}}
                            >
                                <div style={{width: totalWidth, height: totalHeight}} />
                            </div>
                        </Box>
                    </Box>
                </Box>
            ))}

            {/* Reports without barrier data */}
            <Dialog open={showUnmappedDialog} onClose={(): void => setShowUnmappedDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{fontSize: '1rem'}}>
                    Reports without barrier data ({displayedUnmappedReports.length.toLocaleString()}){topViewMode === 'window' ? ' — recent window' : ''}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>
                        These reports were assessed but have no barrier-node outcomes, so they don&apos;t contribute to barrier effectiveness metrics.
                    </Typography>
                    <table style={{borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem'}}>
                        <thead>
                            <tr>
                                <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Report</th>
                                <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Source</th>
                                <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Date</th>
                                <th style={{textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedUnmappedReports.slice(unmappedPage * UNMAPPED_PAGE_SIZE, (unmappedPage + 1) * UNMAPPED_PAGE_SIZE).map((r) => {
                                const d = r.dateTime ? new Date(r.dateTime) : null;
                                return (
                                    <tr key={r.reportId}>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{r.uniqueId || `Report ${r.reportId}`}</td>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{r.sourceName || '—'}</td>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{d && !isNaN(d.getTime()) ? d.toLocaleDateString() : '—'}</td>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center'}}>
                                            <Tooltip title="View report details" arrow>
                                                <IconButton
                                                    size="small"
                                                    sx={{padding: '2px'}}
                                                    onClick={(): void => {
                                                        setShowUnmappedDialog(false);
                                                        navigateToReportAnalysis(r.reportId);
                                                    }}
                                                >
                                                    <VisibilityIcon sx={{fontSize: 16}} />
                                                </IconButton>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {displayedUnmappedReports.length > UNMAPPED_PAGE_SIZE && (
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{mt: 1}}>
                            <Typography variant="caption" color="text.secondary">
                                {unmappedPage * UNMAPPED_PAGE_SIZE + 1}–{Math.min((unmappedPage + 1) * UNMAPPED_PAGE_SIZE, displayedUnmappedReports.length)} of {displayedUnmappedReports.length}
                            </Typography>
                            <IconButton
                                size="small"
                                disabled={unmappedPage === 0}
                                onClick={(): void => setUnmappedPage((p) => p - 1)}
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>◀</Typography>
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={(unmappedPage + 1) * UNMAPPED_PAGE_SIZE >= displayedUnmappedReports.length}
                                onClick={(): void => setUnmappedPage((p) => p + 1)}
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>▶</Typography>
                            </IconButton>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>

            {/* Barrier drill-down: reports that challenged the selected barrier */}
            <Dialog open={drillBarrier != null} onClose={(): void => setDrillBarrier(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{fontSize: '1rem'}}>
                    Reports challenging {drillBarrier?.name} ({drillReports.length.toLocaleString()})
                </DialogTitle>
                <DialogContent dividers>
                    <table style={{borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem'}}>
                        <thead>
                            <tr>
                                <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Report</th>
                                <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Source</th>
                                <th style={{textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Date</th>
                                <th style={{textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Outcome</th>
                                <th style={{textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, backgroundColor: '#fff'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drillReports.slice(drillPage * drillPageSize, (drillPage + 1) * drillPageSize).map((r) => {
                                const d = r.dateTime ? new Date(r.dateTime) : null;
                                return (
                                    <tr key={r.reportId}>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{r.uniqueId || `Report ${r.reportId}`}</td>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{r.sourceName || '—'}</td>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0'}}>{d && !isNaN(d.getTime()) ? d.toLocaleDateString() : '—'}</td>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', fontWeight: 'bold', color: r.outcome === 'SUCCESS' ? colorSuccess : colorFailure}}>
                                            {r.outcome === 'SUCCESS' ? 'Success' : 'Failure'}
                                        </td>
                                        <td style={{padding: '4px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center'}}>
                                            <Tooltip title="View report details" arrow>
                                                <IconButton
                                                    size="small"
                                                    sx={{padding: '2px'}}
                                                    onClick={(): void => {
                                                        setDrillBarrier(null);
                                                        navigateToReportAnalysis(r.reportId);
                                                    }}
                                                >
                                                    <VisibilityIcon sx={{fontSize: 16}} />
                                                </IconButton>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {drillReports.length > 0 && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 1}}>
                            <Typography variant="caption" color="text.secondary">Reports per page:</Typography>
                            <TextField
                                select
                                size="small"
                                value={drillPageSize}
                                onChange={(e): void => { setDrillPageSize(Number(e.target.value)); setDrillPage(0); }}
                                sx={{minWidth: 64, '& .MuiInputBase-input': {fontSize: '0.75rem', py: 0.5}}}
                            >
                                {[10, 25, 50, 100].map((n) => (
                                    <MenuItem key={n} value={n}>{n}</MenuItem>
                                ))}
                            </TextField>
                            <Typography variant="caption" color="text.secondary" sx={{flex: 1, textAlign: 'right'}}>
                                {drillPage * drillPageSize + 1}–{Math.min((drillPage + 1) * drillPageSize, drillReports.length)} of {drillReports.length}
                            </Typography>
                            <IconButton
                                size="small"
                                disabled={drillPage === 0}
                                onClick={(): void => setDrillPage((p) => p - 1)}
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>◀</Typography>
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={(drillPage + 1) * drillPageSize >= drillReports.length}
                                onClick={(): void => setDrillPage((p) => p + 1)}
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>▶</Typography>
                            </IconButton>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>


            {/* Failure-gap distribution drill-down (PDF/CDF + opt-in Weibull overlay).
                Opened by clicking an MTBF cell; the gap stats and Weibull fit are
                memoised above and passed in. */}
            <TimeToFailureDialog
                entity={gapDistBarrier ? {key: String(gapDistBarrier.nodeId), label: gapDistBarrier.uniqueId, sublabel: gapDistBarrier.name} : null}
                onClose={(): void => setGapDistBarrier(null)}
                gapDistData={gapDistData}
                weibullFit={weibullFit}
                showWeibullFit={showWeibullFit}
                onShowWeibullFitChange={setShowWeibullFit}
                predictionDays={predictionDays}
                onPredictionDaysChange={setPredictionDays}
                crowAmsaaFit={crowAmsaa.fit}
                initialTab={crowAmsaa.initialTab}
            />
        </Box>
    );
};

export default BarrierEffectivenessTab;
